// Single source of truth: global settings + the editable iReal-style chart.
// Derived data (optimised voicings, analysis, the flat performance order) is
// computed in components from the chart.

import { create } from 'zustand';
import { normalizeCompressionAmount } from '@/audio/compression';
import { DEFAULT_ENDING, normalizeEnding, type EndingOptions } from '@/audio/endings';
import {
  normalizeLevel,
  normalizeLevels,
  type ChannelLevels,
  type MixChannel,
  type MixFader,
  UNITY_LEVELS,
} from '@/audio/levels';
import { persist } from 'zustand/middleware';
import { DEFAULT_METRONOME, normalizeMetronome, type MetronomeSettings } from '@/audio/metronome';
import { FEEL_TIMINGS, type FeelTiming } from '@/audio/feel';
import {
  DEFAULT_SWING_FEEL,
  isSwingFeel,
  normalizeSwingOffset,
  type SwingFeel,
} from '@/music/swingFeel';
import {
  normalizeBandFeel,
  SWING_BAND_FEEL,
  SWING_OFFSET_KEYS,
  withBandFeel,
  type BassAccent,
  type MelodyPhrasing,
  type Placement,
} from '@/music/bandFeel';
import {
  DEFAULT_PRACTICE_METRONOME,
  normalizePracticeMetronome,
  type PracticeMetronome,
} from '@/audio/practiceMetronome';
import {
  CROSS_TAB_KEYS,
  PERSISTED_KEYS,
  pickBand,
  persistedSlice,
  type CrossTabKey,
} from './persistence';
import {
  SCALE_DEFAULT,
  chartScaleFromTier,
  clampScale,
  scoreScaleFromTier,
  normalizeDiagramMarkers,
  type DiagramMarkers,
} from './displayScale';
import { availableVoicingFamily } from './voicingAvailability';
import {
  CARRY_KEYS,
  TEMPO_MAX,
  TEMPO_MIN,
  captureHouseSettings,
  capturePlaylistCall,
  bandFrom,
  mergeSections,
  normalizeCarry,
  normalizeHouseDefaults,
  normalizePlaylistCall,
  normalizeTunePlayback,
  normalizeTuneSide,
  recordHasSettings,
  type CarryKey,
  type HouseDefault,
  type HouseSettings,
  type PlaybackDisplaced,
  type TunePlaybackRecord,
  type TuneSections,
  type WrittenPlayback,
} from './tunePlayback';
import { normalizeSectionStarts, sectionLabel, type SectionStart } from '@/music/sectionPlan';
import {
  normalizeAccountSync,
  type AccountSyncState,
  type TuneSettingsRecord,
} from './accountSyncPlan';
import { checkSavedTunes } from '@/storage/savedTunes';
import {
  canEditPlaylist,
  normalizePlaylistDeletes,
  normalizePlaylistLinks,
  type LinkAccess,
  type PlaylistDelete,
  type PlaylistLinks,
} from '@/storage/playlistLinks';
import { playlistContent, sameContent, type PlaylistContent } from '@/storage/playlistMerge';
import {
  PLAYLIST_SCHEMA_VERSION,
  addEntry,
  createPlaylistDoc,
  duplicatePlaylistDoc,
  makeEntryId,
  makePlaylistId,
  moveEntry,
  playlistRefKey,
  recordTombstone,
  removeEntry,
  replaceEntryRef,
  renamePlaylistDoc,
  setEntryCall,
  setEntryNote,
  setPlaylistDescription,
  type PlaylistCall,
  type PlaylistDoc,
  type PlaylistTombstone,
  type PlaylistTuneRef,
} from '@/storage/playlists';
import { type ChordTypeId } from '@/music/chords';
import {
  BUILTIN_ALTERNATIVE_SHAPES,
  DEFAULT_PREFERRED_ALTERNATIVE_IDS,
  MAX_ALTERNATIVE_SHAPES,
  allAlternativeShapes,
  normalizeAlternativeShapes,
  normalizePreferredAlternativeIds,
  type AlternativeShape,
} from '@/music/alternativeShapes';
import type { CompStyle } from '@/music/comping';
import { DEFAULT_TRAINER, trainerPassesToTarget, type TrainerConfig } from '@/audio/practice';
import type { BassFeel, BassStyle } from '@/music/walkingBass';
import { parseKey, pitchClassOf } from '@/music/notes';
import type { StringSet } from '@/music/tuning';
import { isVoicingFamilySelection, type VoicingFamilySelection } from '@/music/voicingFamilies';
import {
  normalizeVoicingPins,
  withPin,
  withoutLane,
  withoutPin,
  type PinLane,
  type TuneArrangement,
  type TuneVoicingSettings,
  type VoicingPin,
} from '@/music/voicingPins';
import { chartToSequence, createEmptyChart, sequenceToChart, songToChart } from '@/music/chart';
import type { ArrangementPolicy } from '@/music/performance';
import { chartToScore, scoreToChart } from '@/music/score/convert';
import { scoreHasWrittenBass, scoreMelodyIsBass } from '@/music/score/toSong';
import { voiceTiming } from '@/music/score/timing';
import {
  advanceEntryCursor,
  entryDurationInBeats,
  entryGridInBeats,
  entryMeasures,
  reconcileEntryCursor,
  stepEntryCursor,
  toggleDottedDuration,
  type EntryCursorMove,
  type ScoreEntryCursor,
} from '@/music/score/entry';
import {
  midiOf,
  noteNameOf,
  transposePitch,
  invertInterval,
  spellMidi,
  type SpelledPitch,
} from '@/music/score/pitch';
import * as formEdit from '@/music/score/formEdit';
import * as reharm from '@/music/score/reharm';
import type { ReharmProvenance } from '@/music/score/reharm';
import {
  applyCandidate,
  applyDerivation,
  resolveDeriveOptions,
  buildReharmContext,
  derive,
  type AdventureBand,
  type ReharmCandidate,
  type ReharmRung,
} from '@/music/reharm';
import * as partEdit from '@/music/score/partEdit';
import type { PartPreset } from '@/music/score/partEdit';
import { splitPartStaves } from '@/music/score/partSplit';
import { isPairedCodaFinal, normalizeScore } from '@/music/score/normalize';
import type { Articulation, DynamicMark, Score } from '@/music/score/types';
import { validateScore } from '@/music/score/validate';
import { DEFAULT_SCORE_EDITION, isScoreEdition, type ScoreEdition } from '@/music/score/edition';
import { shiftScoreOctave, transposeScore } from '@/music/score/transpose';
import { tidyTranscription, type TidyReport } from '@/music/score/transcription';
import * as voiceEdit from '@/music/score/voiceEdit';
import * as noteClipboard from '@/music/score/clipboard';
import type { ClipboardPasteMode, NoteClipboard } from '@/music/score/clipboard';
import { normalizePassage, passageMeasureIds, type Passage } from '@/music/passage';
import { parseMusicXMLScore } from '@/music/musicxml/parseScore';
import { exportMusicXMLScore, type MusicXMLExportResult } from '@/music/musicxml/serializeScore';
import { getChordPlayer } from '@/audio/player';
import type { BassSound, GuitarSound, LeadSound } from '@/audio/instrumentSamples';
import {
  DEFAULT_VOICE_LEADING_ENGINE_ID,
  normalizeVoiceLeadingEngineId,
  voiceLeadingEngine,
  type VoiceLeadingEngineId,
} from '@/music/voiceLeadingEngines';
import {
  getStylePreset,
  matchStylePreset,
  type CompInstrument,
  type StyleBand,
  type StyleId,
} from '@/music/style';
import type { PianoCompFamily } from '@/music/pianoComping';
import { parseIRealSongChunk, parseIRealURL, type IRealIndex } from '@/music/ireal/parse';
import { isAbcScheme, parseTuneIndex } from '@/music/tuneIndex';
import {
  normalizeTuneHistory,
  RECENT_LIMIT,
  recordOpen,
  recordPlay,
  type TuneHistory,
} from './tuneHistory';
import {
  markTaste,
  normalizeTasteRemovals,
  normalizeTuneTaste,
  noteTasteRemoval,
  restoreTasteMark,
  type TasteMark,
  type TasteRemoval,
  type TasteTune,
  type TasteVerdict,
  type TuneTaste,
} from './tuneTaste';
import { splitAbcBook, withAbcBookName } from '@/music/abc/book';
import { parseAbcChunk } from '@/music/abc/parse';
import type {
  BarlineClose,
  BarlineOpen,
  IRealChart,
  IRealChord,
  IRealMeasure,
} from '@/music/ireal/types';
import { createEmptySong, uid, type MelodyNote, type SequenceChord, type Song } from '@/music/song';
import type { ChordSymbolStyle } from '@/music/notation/chordLabel';
import type { ChordMelodyDensity } from '@/music/notation/arrangement';
import {
  DEFAULT_BARS_PER_LINE,
  normalizeBarsPerLine,
  type BarsPerLine,
} from '@/music/notation/lines';

export type Theme = 'light' | 'dark';
export type SortMode = 'default' | 'string';
/** The app's one visual style (UI review phase 4, Oct 2026: the trial styles
 * — Modern, Real Book, Flat Five, Guide Tones, Yellow Book, Paper — retired,
 * "Black Book only"): the Real Book's lettering and controls in Flat Five's
 * black, under the Flat Five name and mark. The shells still carry it as
 * `data-chart-font` (and `data-realbook`, `data-branded-bar`), which is what
 * the stylesheet keys on. Light and dark themes stay. */
export const VISUAL_STYLE = 'blackbook';
export type PlayheadTint = 'green' | 'amber' | 'blue' | 'pink';

// The playlist model lives in `@/storage/playlists` — pure data, no React and
// no store, which is what lets an account store consume the same shapes later.
// Re-exported here because most call sites reach for playlist types through
// the store they already import.
export {
  playlistRefKey,
  savedTuneKey,
  type PlaylistDoc,
  type PlaylistEntry,
  type PlaylistTombstone,
  type PlaylistTuneRef,
  type SavedTune,
} from '@/storage/playlists';

/** @deprecated The playlist shape is `PlaylistDoc`; this alias keeps older call
 * sites compiling while they are updated. */
export type UserPlaylist = PlaylistDoc;
/** Where the Tunes browser was last left — session memory so closing and
 * reopening the panel returns to the same list ('home' = the sources screen;
 * null = never browsed, so the panel picks its default). */
export type TunesBrowseScope =
  | { type: 'home' }
  | { type: 'all' }
  | { type: 'native' }
  | { type: 'loaded' }
  | { type: 'lead' }
  | { type: 'library' }
  | { type: 'saved' }
  | { type: 'custom'; id: string };
/** The Workbench's reading surface: chord-grid chart or staff notation.
 * With `VoicingLens` this forms the workspace's two-axis view model — surface
 * (how you read the tune) × lens (which generated grips ride on it). The old
 * four tabs (chart/guitar/score/arrange + the Arrange layout toggle) were the
 * historical spellings of five of the six cells; persisted values migrate in
 * the persist `merge` hook. */
export type ViewSurface = 'chart' | 'score' | 'map';
const VIEW_SURFACES: readonly ViewSurface[] = ['chart', 'score', 'map'];
const isViewSurface = (v: unknown): v is ViewSurface => VIEW_SURFACES.includes(v as ViewSurface);
/** The Map mode's check row: the chords shown (learning), faded and revealed
 * on tap (testing), or no check row at all (brackets only). */
/** What the Map surface does with the chords: shown, dimmed until a line is
 *  tapped, or hidden until a line is tapped. */
export type TuneMapDetail = 'shown' | 'dimmed' | 'hidden';
const TUNE_MAP_DETAILS: readonly TuneMapDetail[] = ['shown', 'dimmed', 'hidden'];
/** The levels' first names, as a persisted blob may still carry them. */
const LEGACY_TUNE_MAP_DETAIL: Record<string, TuneMapDetail> = {
  learning: 'shown',
  testing: 'dimmed',
  brackets: 'hidden',
};
const isTuneMapDetail = (v: unknown): v is TuneMapDetail =>
  TUNE_MAP_DETAILS.includes(v as TuneMapDetail);
/** The Map mode's presentation: the card (one line a cell, the default) or
 * the chart's own bar grid. */
export type TuneMapLayout = 'card' | 'grid';
const TUNE_MAP_LAYOUTS: readonly TuneMapLayout[] = ['card', 'grid'];
const isTuneMapLayout = (v: unknown): v is TuneMapLayout =>
  TUNE_MAP_LAYOUTS.includes(v as TuneMapLayout);
/** Which generated voicing arrangement rides on the surface: none, the
 * harmony-led backing ("comping", the classic voice-led grips), or the
 * melody-led chord-melody arrangement (requires a melody). */
export type VoicingLens = 'none' | 'comping' | 'melody';

/** How far up the ladder, how far out, and how much of the passage to touch. */
export interface ReharmPassageOptions {
  maxRung?: ReharmRung;
  adventure?: AdventureBand;
  density?: number;
  variation?: number;
}

interface AppState {
  // --- global settings ---
  defaultVoicingFamily: VoicingFamilySelection;
  voicingFamily: VoicingFamilySelection;
  /** User shapes supplement the strict family examples across all tunes. */
  alternativeShapes: AlternativeShape[];
  preferredAlternativeIds: string[];
  /** How the chords MOVE: which voice-leading engine voices the line. */
  voiceLeadingEngine: VoiceLeadingEngineId;
  /** Which part the workbench's score view reads: an index, or -1 = the
   * stacked full score (Score Studio's view vocabulary, shared). */
  scoreViewPart: number;
  stringSet: StringSet;
  freeStringSet: boolean; // voice-lead across both string sets; stringSet is the home set
  avoidB9: boolean;
  sortMode: SortMode;
  theme: Theme;
  audioEnabled: boolean;
  /** Hold a screen wake lock while the app is visible — a chart on a music
   * stand must not sleep mid-tune. The platform releases the lock whenever
   * the page is hidden, so this only spends battery while music is on screen.
   * Off = the OS idle timer applies as usual. */
  keepAwake: boolean;
  startingInversion: number;
  tempo: number; // BPM
  metronome: boolean;
  countIn: boolean; // clicked bar(s) before playback starts
  countInBars: number; // count-in length in bars (1-4)
  /** Master reverb amount 0..1 (the mixer's Master channel; send law in audio/reverb.ts). */
  masterReverb: number;
  masterReverbEnabled: boolean;
  /** Master bus compression 0..1; zero bypasses, 0.5 matches the original compressor. */
  masterCompression: number;
  masterCompressionEnabled: boolean;
  /** The mixer's faders: per-channel levels (1 = unity) and the master volume after the limiter. */
  channelLevels: ChannelLevels;
  /** The metronome's voice, accent pattern, subdivision and routing (#412; all tunes). */
  metronomeSettings: MetronomeSettings;
  /** The standalone metronome page's own settings (#413; the click voice is metronomeSettings). */
  practiceMetronome: PracticeMetronome;
  /** Where the band sits against the beat (#423): tight on the grid, or relaxed. */
  feelTiming: FeelTiming;
  /** The mixer's SOLO (session only): one channel heard alone, the rest silent. */
  soloChannel: MixChannel | null;
  countInBetweenLoops: boolean; // repeat the count-in between form passes
  speedPct: number; // practice speed, % of the arrangement tempo (playback only)
  trainer: TrainerConfig; // progressive speed trainer (per form pass)
  compInstrument: CompInstrument; // guitar grips or piano voicings (playback only)
  compPianoFamily: PianoCompFamily; // which piano voicing family the comp plays
  compStyle: CompStyle; // comping rhythm for the chords (playback only)
  compAmount: number; // freestyle approach-stab density 0..1
  drums: boolean; // play a DrumGenius loop under the arrangement
  drumLoopKey: string; // which loop from public/drums/manifest.json
  drumTimeStretch: boolean; // keep recorded drum pitch while changing tempo
  /** Sound the tune's melody line over the band (when the tune has one). */
  melodyChannel: boolean;
  leadSound: LeadSound;
  bassSound: BassSound;
  guitarSound: GuitarSound;
  bassline: boolean;
  bassSolo: boolean;
  bassStyle: BassStyle; // which rung of the walking-bass ladder to generate
  bassFeel: BassFeel; // half-note "two" feel vs quarter-note "four" feel
  /** Heads in 2, solos in 4 — the convention automated. Only audible under
   * the head/solos/head policy (see StyleBand.bassFollowForm). */
  bassFollowForm: boolean;
  bassSwing: boolean; // the band swings (every part, not only the bass)
  /** How it swings: the level and ratio (swingFeel.ts). A band field. */
  swingFeel: SwingFeel;
  /** The triplet eighth follows the kit's measured skip note (StyleBand.swingWithKit). */
  swingWithKit: boolean;
  /** Where each part sits against the kit, and how the line is phrased and
   *  the bass accented (bandFeel.ts). Band fields. */
  melodyPlacement: Placement;
  melodyPhrasing: MelodyPhrasing;
  bassPlacement: Placement;
  bassAccent: BassAccent;
  bassSkips: boolean; // eighth-note skips into the next beat
  compPlacement: Placement;
  /** How much the band swings against its kit, and each part against the
   *  band, under the Kit choice (StyleBand.swingOffset). Band fields. */
  swingOffset: number;
  melodySwingOffset: number;
  compSwingOffset: number;
  bassSwingOffset: number;
  bassGhosts: boolean; // add ghost-note skips
  bassAnticipate: boolean; // push chord-change downbeats early
  bassTriplets: boolean; // roll triplet "drops" into targets
  bassAmount: number; // embellishment density 0..1
  repeatForm: boolean; // loop the whole chart during playback
  /** How the last pass finishes: ritardando / tag / held chord (src/audio/endings.ts). */
  ending: EndingOptions;
  /** How the sheet becomes a performance: the notation as written, or the
   * jazz itinerary (head, solo choruses, head out taking the coda). */
  arrangementPolicy: ArrangementPolicy;
  viewSurface: ViewSurface;
  voicingLens: VoicingLens;
  /** Score surfaces: draw the sung verse lanes (inert when the tune has no lyrics). */
  showLyrics: boolean;
  /** Score surfaces: a small bar number above the start of every bar. */
  showBarNumbers: boolean;
  /** Lead-sheet score surfaces: full bars per line, or 'auto' to fill the
   * width (see music/notation/lines.ts). */
  barsPerLine: BarsPerLine;
  /** Chord-melody lens: a grip per harmony change, or under every melody note. */
  chordMelodyDensity: ChordMelodyDensity;
  /** Score surfaces: the harmonic-analysis lane under each system (key regions, romans, cadence brackets). */
  showAnalysis: boolean;
  unrollRepeats: boolean; // guitar view: expand repeats to a linear A/A′ form, voiced through
  cycleForm: boolean; // repeat the whole form voice-led until the opening voicing returns (A→A′→…)
  /** Chart surfaces on a desktop: lay the sheet out as side-by-side pages that
   * fit the screen (the Guitar Pro page view) instead of one vertical strip. */
  sheetPages: boolean;
  /** Map mode: how much of the check row the brackets carry. */
  tuneMapDetail: TuneMapDetail;
  /** Map mode: the card or the grid. */
  tuneMapLayout: TuneMapLayout;
  // Display scale as a PERCENTAGE of a device-appropriate base (see
  // displayScale.ts). Two scales, one mechanism: chord charts and engraved
  // notation are different content read at different densities, and a
  // workspace cell can show both at once.
  chartScale: number; // chord symbols + grip diagrams on chart surfaces
  scoreScale: number; // every engraved surface — workbench, Arrange, Studio
  arrangeChordNameScale: number; // label size relative to the Arrange diagram
  arrangeFretNumberScale: number; // one font size for single- and two-digit frets
  arrangeDiagramMarkers: DiagramMarkers;
  chordSymbolStyle: ChordSymbolStyle; // how chord qualities are spelled everywhere
  /** Which BOOK the sheet reads as — C treble (concert, as written), B♭, E♭ or
   *  C bass. A reading transform over the workbench's chart and score surfaces
   *  (`editionScore`); the Score itself and the band stay in concert pitch. */
  scoreEdition: ScoreEdition;
  /**
   * Which instrument's chord diagrams the voicings sheet DRAWS — guitar grips
   * or piano keyboards. A READING preference (the Chords menu), deliberately
   * separate from the band's `compInstrument`, which is what SOUNDS: you can
   * read grips while the piano comps, or study keyboard voicings over a
   * guitar comp.
   */
  diagramInstrument: CompInstrument;
  playheadTint: PlayheadTint; // colour of the current-bar highlight

  // --- the musical truth (slice 4: the score model) ---
  /** The single source of truth for all musical content. */
  score: Score;
  /**
   * Materialized lead-sheet projection of `score` — kept in exact sync by the
   * store's write seam (every `set` carrying `chart` lifts it onto `score`
   * and re-projects; nothing else may write either field). Consumers keep
   * reading `chart` unchanged; it is scoreToChart(score) at all times.
   */
  chart: IRealChart;
  selectedChordId: string | null;
  selectedMeasureId: string | null;
  /** Written-bar range for looping and copy/paste. Session-only. */
  passage: Passage | null;
  /** When set with a passage, playback loops just those written bars. */
  loopRegion: boolean;
  /** In-memory note clip (survives tune loads so a lick can move between charts). */
  noteClipboard: NoteClipboard | null;
  /** Measure new chords are appended to (defaults to the last measure). */
  insertionMeasureId: string | null;

  // --- score editor (melody lives on the score) ---
  selectedNoteId: string | null;
  selectedNoteheadIndex: number | null;
  /** Session-only rhythmic caret; unlike selection it can sit on empty time. */
  scoreEntryCursor: ScoreEntryCursor | null;
  /** Written entry value in quarter-note units (quarter=1, eighth=0.5). */
  entryDuration: number;
  /** Which voice staff clicks write to: 0 = melody, 1 = the stems-down voice. */
  entryVoice: number;
  /** Session-only authored staff selected in Score Studio. Null resolves to
   *  the first lead-role part, then the first part, for legacy scores. */
  scoreContentTarget: voiceEdit.ContentTarget | null;
  /** Score Studio undo/redo: whole-Score snapshots shared by notation, form,
   *  harmony and metadata edits. The persisted field names predate that scope. */
  melodyPast: Score[];
  melodyFuture: Score[];
  /** Session-only caret snapshots parallel to the score undo stacks. */
  entryCursorPast: (ScoreEntryCursor | null)[];
  entryCursorFuture: (ScoreEntryCursor | null)[];
  /** Session-only editing context paired with the same score snapshots. */
  scoreEditorPast: (ScoreEditorSnapshot | null)[];
  scoreEditorFuture: (ScoreEditorSnapshot | null)[];

  // --- per-tune band memory (iReal-style) ---
  /** Identity of the loaded tune (title+composer), used to remember its band. */
  currentTuneKey: string | null;
  /** The last tunes actually OPENED (any source), most recent first — the
   *  home page's shelf. Refs, not copies: a recent resolves through the same
   *  machinery as a playlist entry, so it can never go stale differently. */
  recentTunes: PlaylistTuneRef[];
  /** Your play history per tune (state/tuneHistory.ts): opens, seconds played
   *  and when, keyed by tune identity. Most played and Top picks read it. */
  tuneHistory: TuneHistory;
  /** The tunes you loved or asked to see less of (state/tuneTaste.ts), newest
   *  first. Top picks, the More like shelves and the Loved shelf read it. */
  tuneTaste: TuneTaste;
  /** Marks you took off, kept a while so taking one off reaches your other
   *  devices too (state/tuneTaste.ts). */
  tuneTasteRemoved: TasteRemoval[];
  /** Your other devices' play history, added up, as the account sync last
   *  read it: recents, Most played and Top picks read it beside your own. */
  otherHistory: TuneHistory;
  /** The account whose recents, play history and loves this device holds:
   *  another account signing in clears them (they are in the first one's
   *  account), as it clears playlists. Null until an account first syncs. */
  historyAccount: string | null;
  /** Counts the account sync bringing loves from another device: the home
   *  page takes the marks again then, never on your own taps. Session only. */
  tastePulls: number;
  /** Remembered band per tune — captured on any mixer change, no explicit save. */
  tuneBands: Record<string, StyleBand>;
  /**
   * Per-tune Feel → groove assignment (#174): how THIS tune realizes each
   * written cue. Keyed by the same stable tune identity as `tuneBands`, and
   * COPIED from whatever preset seeded it — a later edit to that preset must
   * never silently change a tune somebody already arranged.
   */
  tuneFeels: Record<string, Record<string, StyleBand>>;
  /**
   * How YOU voice each tune (voicingPins.ts): the Chords menu's voicing
   * settings, remembered like the band with no explicit save, and the pins —
   * your own grips laid over the generated line. Keyed by the same stable
   * tune identity as `tuneBands`. The tune itself is never touched.
   */
  tuneArrangements: Record<string, TuneArrangement>;
  /**
   * YOUR changes to each tune's playback beyond the band (tunePlayback.ts):
   * tempo, key, chorus count, form and ending, the mix and the sounds —
   * remembered like the band with no explicit save, and only what you changed,
   * so a house default change to anything you did not touch still reaches you.
   */
  tunePlayback: Record<string, TunePlaybackRecord>;
  /** The house defaults the owner saved for everyone, cached on this device
   *  (docs/tune-library-plan.md §3). Keyed by the same identity as tuneBands. */
  houseDefaults: Record<string, HouseDefault>;
  /** What the current tune's layers replaced in the carried fields — put back
   *  when the next tune opens, so one tune's house mix never leaks into another. */
  playbackDisplaced: PlaybackDisplaced;
  /** The loaded tune's own tempo, key and chorus count: what Reset returns to. */
  writtenPlayback: WrittenPlayback | null;
  /** The house revision the current tune was last resolved with (null: none). */
  appliedHouseRevision: number | null;
  /**
   * The playlist row the tune on screen was opened FROM, whose call (key,
   * tempo, feel, choruses, form, ending) it plays with. Set by the tune-load
   * seam, cleared by any other load; `tuneKey` pins it to the tune it was
   * opened with, so a stale source can never reach another tune.
   */
  playlistCallSource: PlaylistCallSource | null;
  /**
   * Follow the written cues, or force one groove over the whole form. A
   * playback PREFERENCE: forcing changes nothing on the page.
   */
  followFeelCues: boolean;

  // --- saved charts (v4: full Score snapshots, so voices/parts survive) ---
  savedCharts: Score[];

  // --- loaded playlist (in-memory only; raw source kept under a dedicated
  // localStorage key so a large playlist never bloats the persisted state) ---
  playlist: IRealIndex | null;

  // --- user-curated playlists (persisted) ---
  userPlaylists: PlaylistDoc[];
  /** Playlists this device deleted. Kept so a later sync can tell a deletion
   * from a list it has simply never seen, and not resurrect it. */
  deletedPlaylists: PlaylistTombstone[];
  /** Your playlists on the server — every playlist while signed in, yours and
   *  the shared ones you opened — keyed by playlist id: the row, what its link
   *  lets holders do, and the server's copy as this device last saw it
   *  (src/data/sharedPlaylistSync.ts). */
  playlistLinks: PlaylistLinks;
  /** The account whose playlists this device holds. Another account signing
   *  in here takes the list off the device (it is in that account) rather
   *  than moving it into the new one. Null: never signed in here since
   *  playlists followed the account, so the first to sign in takes them. */
  playlistAccount: string | null;
  /** Your playlists deleted here that your account has not deleted yet. */
  playlistDeletes: PlaylistDelete[];
  /** What this device last agreed with your account (src/data/accountSync.ts). */
  accountSync: AccountSyncState | null;
  /** Saved tunes you deleted here since the last sync, so the account deletes
   *  them too — a tune missing for any OTHER reason comes back from it. */
  deletedSavedTunes: string[];
  /** When each shared document last changed in THIS store (ms), so a second
   *  tab can tell a newer copy from an older one (src/state/crossTab.ts).
   *  Stamped by the write seam; an adoption copies the other tab's stamps. */
  keyStamps: Partial<Record<CrossTabKey, number>>;
  /**
   * Where the player is in a set they are working through — a CURSOR, not a
   * record of what is on screen. Opening something else does not clear it: a
   * detour to check a chord should not lose your place in the gig, so leaving
   * a set is an explicit act. Persisted, because the set you are rehearsing is
   * exactly what you want back after a reload.
   */
  setCursor: { playlistId: string; entryId: string } | null;
  /**
   * The playlist the last tune was filed into (Oct 2026): the Add to menu
   * lists it first, because filing is a run of adds into one list. Persisted;
   * the menu checks it still exists.
   */
  lastPlaylistAddedTo: string | null;
  /**
   * Run the set hands-free: when a tune reaches its end, load the next one and
   * play it. OFF by default and deliberately — it fires on a real ending, and
   * a tune ending while you are editing its chart should not carry you away
   * from what you were doing.
   */
  autoAdvanceSet: boolean;
  /**
   * "Play whatever is loaded, as soon as there is a transport to do it."
   *
   * Session-only, and a flag rather than an event because the asking and the
   * doing are often on different PAGES: pressing Play on a playlist loads a
   * tune and navigates, and the workbench that owns `playAll` does not exist
   * yet at that moment. A flag waits; an event fired into the void does not.
   */
  pendingPlay: boolean;
  /**
   * What starting a set had to skip over, or null.
   *
   * Session-only, and in the store for the same reason as `pendingPlay`: the
   * skip happens on the Playlists page and the person who needs telling is
   * looking at the workbench a moment later. A message left on the page they
   * just left explains nothing.
   */
  setSkipNotice: string | null;
  /**
   * How the LOADED tune would be stored in a playlist, or null when nothing
   * identifiable is loaded — a blank chart, an ad-hoc import, or a tune that
   * has since been edited.
   *
   * Null is the honest answer after an edit, not a missing feature: the score
   * on screen is no longer what the reference names, so storing it would put a
   * row in a set that reopens as something else. `addCurrentTuneToPlaylist`
   * saves a snapshot in that case instead.
   */
  currentTuneRef: PlaylistTuneRef | null;
  /**
   * Session-only (not persisted): counts the actions that REPLACE the tune —
   * a load, an import, a new or cleared chart — and nothing else. It is how a
   * consumer tells "another tune arrived" from "this tune was edited", which
   * the score cannot say: every edit makes a new Score, a rename changes the
   * title, and a library chart may carry no durable id to tell two loads
   * apart. The workbench scrolls to the top on it, and a deep link that is
   * still fetching stands down once it moves.
   */
  tuneLoadSeq: number;
  /** Session-only (not persisted): the Tunes browser's last scope. */
  tunesBrowseScope: TunesBrowseScope | null;
  /**
   * Session-only: which band the band controls show and edit while the band is
   * split by section or written feel — a label, or components/bandScope's
   * "all" (also what null means). A new tune starts at All.
   */
  bandScope: string | null;
  /**
   * Session-only: the written bar Play starts from (a tap on a bar sets it).
   * Null plays from the top; a new tune starts there.
   */
  playCursor: string | null;
  /**
   * Focus mode: the header and the transport slide away so the sheet has the
   * screen. Session-only ON PURPOSE — booting into a stripped page would
   * greet a first-time reader with no chrome; the view strip's Focus button
   * turns it on and the floating Leave focus button (FocusControls) off.
   */
  focusMode: boolean;

  // --- setters ---
  setDefaultVoicingFamily: (family: VoicingFamilySelection) => void;
  setVoicingFamily: (family: VoicingFamilySelection) => void;
  saveAlternativeShape: (shape: AlternativeShape) => boolean;
  removeAlternativeShape: (id: string) => void;
  setAlternativePreferred: (id: string, preferred: boolean) => void;
  setVoiceLeadingEngine: (engine: VoiceLeadingEngineId) => void;
  setScoreViewPart: (part: number) => void;
  setStringSet: (s: StringSet) => void;
  setFreeStringSet: (v: boolean) => void;
  setAvoidB9: (v: boolean) => void;
  setSortMode: (m: SortMode) => void;
  toggleTheme: () => void;
  setAudioEnabled: (v: boolean) => void;
  setKeepAwake: (v: boolean) => void;
  setStartingInversion: (n: number) => void;
  setTempo: (n: number) => void;
  setMetronome: (v: boolean) => void;
  setCountIn: (v: boolean) => void;
  setCountInBars: (n: number) => void;
  setMasterReverb: (amount: number) => void;
  setMasterReverbEnabled: (enabled: boolean) => void;
  patchEnding: (patch: Partial<EndingOptions>) => void;
  setMasterCompression: (amount: number | boolean) => void;
  setMasterCompressionEnabled: (enabled: boolean) => void;
  setChannelLevel: (fader: MixFader, level: number) => void;
  patchMetronome: (patch: Partial<MetronomeSettings>) => void;
  patchPracticeMetronome: (patch: Partial<PracticeMetronome>) => void;
  setFeelTiming: (feel: FeelTiming) => void;
  /** Solo one channel, or null to hear the band again; soloing the soloed one clears it. */
  setSoloChannel: (channel: MixChannel | null) => void;
  setCountInBetweenLoops: (v: boolean) => void;
  setSpeedPct: (n: number) => void;
  patchTrainer: (patch: Partial<TrainerConfig>) => void;
  setMelodyChannel: (v: boolean) => void;
  setLeadSound: (sound: LeadSound) => void;
  setBassSound: (sound: BassSound) => void;
  setGuitarSound: (sound: GuitarSound) => void;
  setDrumTimeStretch: (enabled: boolean) => void;
  setRepeatForm: (v: boolean) => void;
  setArrangementPolicy: (p: ArrangementPolicy) => void;
  setViewSurface: (s: ViewSurface) => void;
  setVoicingLens: (l: VoicingLens) => void;
  setShowLyrics: (showLyrics: boolean) => void;
  setShowBarNumbers: (showBarNumbers: boolean) => void;
  setBarsPerLine: (barsPerLine: BarsPerLine) => void;
  setChordMelodyDensity: (chordMelodyDensity: ChordMelodyDensity) => void;
  setShowAnalysis: (showAnalysis: boolean) => void;
  setUnrollRepeats: (v: boolean) => void;
  setCycleForm: (v: boolean) => void;
  setSheetPages: (v: boolean) => void;
  setTuneMapDetail: (detail: TuneMapDetail) => void;
  setTuneMapLayout: (layout: TuneMapLayout) => void;
  /** Change one or more band settings AND remember them for the current tune. */
  patchBand: (partial: Partial<StyleBand>) => void;
  /** A fetch of the house defaults landed: replace the cache. The tune on
   *  screen follows a changed default unless you have changed it yourself. */
  setHouseDefaults: (list: HouseDefault[]) => void;
  /** The owner saved a default for everyone: cache it and, on its own tune,
   *  follow it — the owner's own changes are now the house's. */
  adoptHouseDefault: (house: HouseDefault) => void;
  /** A house default was removed. */
  dropHouseDefault: (tuneKey: string) => void;
  /** Drop your changes to this tune's playback: back to the house default, or
   *  to the tune as written when there is none. */
  resetTunePlayback: () => void;
  /** Keep your version after the house default changed (the offer goes). */
  keepMyTunePlayback: () => void;
  /** Apply a whole-band style preset (and label the chart with it). */
  applyStyle: (id: StyleId) => void;
  setChartScale: (percent: number) => void;
  setScoreScale: (percent: number) => void;
  setArrangeChordNameScale: (percent: number) => void;
  setArrangeFretNumberScale: (percent: number) => void;
  setArrangeDiagramMarkers: (markers: DiagramMarkers) => void;
  setChordSymbolStyle: (s: ChordSymbolStyle) => void;
  setScoreEdition: (edition: ScoreEdition) => void;
  setDiagramInstrument: (i: CompInstrument) => void;
  setPlayheadTint: (t: PlayheadTint) => void;

  // --- chart meta ---
  setChartTitle: (title: string) => void;
  setChartComposer: (composer: string) => void;
  setChartKey: (key: string) => void;
  setChartStyle: (style: string) => void;
  /** Author the score's printed tempo and update preview playback with it. */
  setScoreTempo: (tempo: number) => void;
  setScoreTempoText: (tempoText: string) => void;
  setChartRepeats: (n: number) => void;
  /** Returns false when existing content does not fit the requested meter. */
  setTimeSignature: (sig: [number, number]) => boolean;
  transpose: (semitones: number) => void;
  /** Move every written note a whole octave. A register change, not a key
   * change — `transpose(±12)` is a no-op by design (see `shiftScoreOctave`). */
  shiftOctave: (octaves: number) => void;
  transposeToKey: (tonic: string) => void;

  // --- chords ---
  addChord: (input: AddChordInput) => void;
  updateChord: (id: string, patch: ChordPatch) => void;
  removeChord: (id: string) => void;
  setPreferredInversion: (id: string, inversion: number | null) => void;
  /** Pin your own grip over the generated one for a chord of this tune
   * (voicingPins.ts): `key` is the chord's position key in `lane`. */
  pinVoicing: (lane: PinLane, key: string, pin: VoicingPin) => void;
  clearVoicingPin: (lane: PinLane, key: string) => void;
  /** Reset one lane's pins, or every lane's. */
  clearVoicingPins: (lane?: PinLane) => void;
  /** Replace one chord by one or more inside its own span — the seam reharm
   * moves write through (see formEdit.replaceChord). */
  replaceChord: (
    id: string,
    chords: readonly formEdit.ReplacementChord[],
    provenance?: ReharmProvenance,
  ) => void;
  selectChord: (id: string | null) => void;

  // --- reharm layers (src/music/score/reharm.ts) ---
  /** Create a layer (derived from `from`, or the Original) and make it active. */
  createReharmLayer: (options?: { name?: string; from?: string | null }) => string;
  /** Switch the effective harmony to a layer, or to the Original with null. */
  activateReharmLayer: (layerId: string | null) => void;
  renameReharmLayer: (layerId: string, name: string) => void;
  deleteReharmLayer: (layerId: string) => void;
  duplicateReharmLayer: (layerId: string, name?: string) => string | null;
  /** In the active layer, give these measures their original harmony back. */
  revertReharmMeasures: (measureIds: readonly string[]) => void;
  /** Write an engine candidate into the active layer (a fresh one when none
   * is active), as ONE undo step. */
  applyReharmCandidate: (candidate: ReharmCandidate) => void;
  /** Derive a whole passage (the selected bars, or the form) as a NEW layer
   * under a rung ceiling and an adventure band. Returns what was made. */
  deriveReharmPassage: (options: ReharmPassageOptions & { name?: string }) => {
    layerId: string;
    count: number;
  } | null;
  /** The audition in flight: the score as it was before it started, the
   * settings that produced it, the bars it covers (null = the whole tune) and
   * how many chords they change. Session-only — an audition never outlives a
   * reload, and any other musical write ends it (see `endsReharmPreview`). */
  reharmPreview: {
    base: Score;
    count: number;
    options: ReharmPassageOptions;
    passage: Passage | null;
  } | null;
  /** The Reharmonize levers as the user left them (persisted): the settings
   *  the switch derives from. Held here rather than in the panel because the
   *  panel unmounts with the transport drawer, and a lever that snaps back
   *  every time the drawer closes is not a lever. */
  reharmSettings: Required<ReharmPassageOptions>;
  setReharmSettings: (patch: Partial<ReharmPassageOptions>) => void;
  /** Derive a passage and APPLY it as a preview layer, so it can be heard and
   * seen. Re-deriving replaces the previous audition rather than stacking on
   * it. Returns how many chords change. */
  previewReharmPassage: (options: ReharmPassageOptions) => number;
  /** Keep the audition as an ordinary layer — one undo step from before it
   * started, so undo puts back the harmony you began with. */
  keepReharmPreview: (name?: string) => { layerId: string; count: number } | null;
  /** Throw the audition away and put back the harmony underneath. */
  cancelReharmPreview: () => void;

  // --- measures ---
  selectMeasure: (id: string | null) => void;
  /** Drag or shift-extend a written-bar range. */
  selectPassage: (fromId: string, toId: string) => void;
  extendPassage: (toId: string) => void;
  clearPassage: () => void;
  setLoopRegion: (on: boolean) => void;
  scoreCopy: () => void;
  scoreCut: () => boolean;
  scorePaste: (mode?: ClipboardPasteMode) => boolean;
  setInsertionMeasure: (id: string | null) => void;
  /** Returns false when a structural edit would make the measure too short. */
  patchMeasure: (id: string, patch: MeasurePatch) => boolean;
  /** Assign the live band as this tune's realization of `feel`. */
  assignFeelGroove: (feel: string, band?: StyleBand) => void;
  setFollowFeelCues: (on: boolean) => void;
  /** Play this tune's sections with a band each (a playback choice, not the score). */
  setSectionsOn: (on: boolean) => void;
  /** How one section plays: a copy of `band`; no band clears it. */
  assignSectionBand: (label: string, band?: StyleBand) => void;
  /** Your own section starts; null returns to the tune's detected sections. */
  setSectionStarts: (starts: SectionStart[] | null) => void;
  /** Whether one section plays the written melody. */
  setSectionMelody: (label: string, on: boolean) => void;
  insertMeasureAfter: (id: string | null) => void;
  deleteMeasure: (id: string) => void;

  // --- score editor (melody edits, with their own undo/redo) ---
  selectNote: (id: string | null, headIndex?: number) => void;
  scoreAddChordHead: (pitch: SpelledPitch) => void;
  scoreRemoveChordHead: () => void;
  setScoreEntryCursor: (cursor: ScoreEntryCursor | null) => void;
  setEntryDuration: (quarterNotes: number) => void;
  setEntryVoice: (voice: number) => void;
  setScoreContentTarget: (target: voiceEdit.ContentTarget | null) => void;
  scoreAddPart: (preset: PartPreset) => string | null;
  scoreRenamePart: (partId: string, name: string) => void;
  scoreRemovePart: (partId: string) => void;
  /** Separate an eligible grand staff into two parts in one undoable edit. */
  scoreSplitPartStaves: (partId: string) => boolean;
  scoreAddNote: (measureIndex: number, note: Omit<MelodyNote, 'id'>, voice?: number) => void;
  /** Append a pitch after the last melody note (on-screen piano entry).
   *  Duration defaults to the editor's entry duration. */
  scoreAppendNote: (midi: number, durationBeats?: number) => void;
  /** Move the rhythmic caret; crossing the final bar appends one empty bar
   * unless growth is disabled for a held-key repeat. */
  scoreStepEntryCursor: (direction: -1 | 1, byMeasure?: boolean, growAtEnd?: boolean) => void;
  /** Change a pending pitch, or the selected note when one occupies the caret. */
  scoreNudgeEntryCursor: (semitones: number) => void;
  /** Insert three authored rest slots in the time of two current note values. */
  scoreInsertTripletAtCursor: () => void;
  /** Enter at the caret and advance by the written duration, growing the form. */
  scoreEnterAtCursor: (midi: number, rest?: boolean, spelledAs?: string) => void;
  scoreDeleteSelected: () => void;
  scoreNudgeSelected: (semitones: number) => void;
  scoreRespellSelected: () => void;
  scoreToggleSelectedArticulation: (articulation: Articulation) => void;
  scoreSetSelectedDynamic: (dynamic: DynamicMark | undefined) => void;
  /** Re-duration the selected event (select mode's 3–7 keys / duration
   * buttons), in written quarter-note units regardless of meter. The entry
   * duration follows so the toolbar reflects what was applied. */
  scoreSetSelectedDuration: (durationQuarters: number) => void;
  /** Toggle the dot on the selected event (select mode's `.`). */
  scoreToggleSelectedDot: () => void;
  /** Apply ♮/♯/♭ to the selected note's letter (select mode's =/#/- keys). */
  scoreApplySelectedAccidental: (accidental: 'natural' | 'sharp' | 'flat') => void;
  /** Toggle the outgoing tie on the selected note, or the preceding entry
   * event when no note is selected. Write mode can prefer that prior event. */
  scoreToggleOutgoingTie: (preferPrevious?: boolean) => void;
  // Id-addressed edits (the command layer / AI edits notes it can name;
  // the *Selected variants above serve the on-screen editor's selection).
  scoreUpdateNote: (id: string, patch: Partial<MelodyNote>) => void;
  scoreDeleteNote: (id: string) => void;
  scoreNudgeNote: (id: string, semitones: number) => void;
  scoreRespellNote: (id: string) => void;
  /** Strip the melody from every measure (the chords stay). */
  scoreClearMelody: () => void;
  /** Blank the sheet: melody AND chords (the form stays). One undo step. */
  scoreClearAll: () => void;
  scoreUndo: () => void;
  scoreRedo: () => void;
  importMusicXML: (xml: string) => { ok: true } | { ok: false; error: string };
  /** Clean up an AI-transcribed score (key/respell, re-bar, quantize) as one
   * undoable edit. Returns the report; changed:false commits nothing. */
  tidyScoreTranscription: () => TidyReport;
  exportMusicXML: () => MusicXMLExportResult;

  // --- chart lifecycle ---
  clearChart: () => void;
  newChart: () => void;
  /** Start a fresh empty chart with the given metadata (blank canvas). */
  newTune: (meta: NewTuneInput) => void;
  loadChart: (chart: IRealChart) => void;
  /** Parse a tune chunk (an iReal song or, under the 'abc' scheme, one ABC
   * tune) and load it; returns the title, or null if unreadable. */
  loadTuneFromChunk: (scheme: string, chunk: string) => string | null;
  loadSong: (song: Song) => void;
  importIRealText: (text: string) => { ok: true; title: string } | { ok: false; error: string };
  /** Open ABC text: one tune loads; a tunebook becomes the loaded list (named
   * `name`, e.g. its file's, when it does not name itself) and its first tune
   * loads. */
  importAbc: (
    text: string,
    name?: string,
  ) =>
    | { ok: true; title: string; tunes: number; persisted: boolean }
    | { ok: false; error: string };
  /** Load a saved Score snapshot as the current tune. */
  loadScore: (score: Score) => void;
  saveCurrentAs: (title: string) => void;
  /** Save a full named snapshot without renaming or replacing the active score. */
  saveCurrentCopy: (title: string) => string;
  deleteSavedChart: (id: string) => void;
  /** Take what came down from your account: tunes saved or deleted on another
   *  device, and tune settings changed there (a null record: reset there). */
  applyAccountPull: (pull: {
    addTunes: Score[];
    removeTunes: string[];
    settings: Record<string, TuneSettingsRecord | null>;
  }) => void;
  setAccountSync: (state: AccountSyncState | null) => void;
  /** Tombstones the account has now caught up with. */
  clearDeletedSavedTunes: (ids: string[]) => void;

  // --- playlist (runtime loader) ---
  setPlaylist: (source: string) => { ok: true; persisted: boolean } | { ok: false; error: string };
  rehydratePlaylist: () => void;
  clearPlaylist: () => void;

  // --- custom playlists ---
  setTunesBrowseScope: (scope: TunesBrowseScope) => void;
  setBandScope: (scope: string | null) => void;
  setPlayCursor: (measureId: string | null) => void;
  setFocusMode: (on: boolean) => void;
  toggleFocusMode: () => void;
  createPlaylist: (name: string) => string;
  renamePlaylist: (id: string, name: string) => void;
  describePlaylist: (id: string, description: string) => void;
  deletePlaylist: (id: string) => void;
  /** Copy a playlist under a new identity. Returns the copy's id. */
  duplicatePlaylist: (id: string, name?: string) => string | null;
  /** Follow a shared playlist opened from its link (or just shared from
   * here): the playlist joins your list, linked to the server's copy. */
  adoptSharedPlaylist: (
    doc: PlaylistDoc,
    share: { shareId: string; access: LinkAccess; mine: boolean; revision: number },
  ) => void;
  /** This device's playlists belong to this account from now on. When they
   * belonged to another account, they leave the device (they are in that
   * account). True when it cleared them. */
  claimPlaylistsFor: (userId: string) => boolean;
  /** One of your playlists is no longer in your account (deleted on another
   * device): take it off this one too, with nothing left to send. */
  forgetAccountPlaylist: (id: string) => void;
  /** These pending deletions reached the account. */
  clearPlaylistDeletes: (shareIds: readonly string[]) => void;
  /** The shared-playlist sync's write: new content from the server (or a
   * merge), and/or the link's new state; `link: null` unlinks, keeping the
   * playlist here as an ordinary one. Writes even a view-only playlist. */
  applySharedPlaylist: (
    id: string,
    patch: { content?: PlaylistContent; link?: PlaylistLinks[string] | null },
  ) => void;
  /** Add a row. Returns its id, or null when the playlist is not yours to change. */
  addToPlaylist: (id: string, ref: PlaylistTuneRef) => string | null;
  /** Move (or insert) a tune at the top of the recents shelf. */
  /** A tune was opened: it joins the recents and its history counts the open. */
  recordRecentTune: (ref: PlaylistTuneRef) => void;
  /** The band played a tune for `seconds` (data/playHistory.ts banks it). */
  recordTunePlay: (
    key: string,
    tune: { title: string; composer?: string; ref?: PlaylistTuneRef | null },
    seconds: number,
  ) => void;
  /** Love a tune, ask to see less of it, or (null) take the mark off. */
  setTuneTaste: (tune: TasteTune, verdict: TasteVerdict | null) => void;
  /** This device's recents, history and loves are `userId`'s from now on:
   *  another account's are cleared first. True when it cleared them. */
  claimHistoryFor: (userId: string) => boolean;
  /** The account sync's writes: merged loves, your devices' history. */
  applyPersonalPull: (pull: {
    tuneTaste?: TuneTaste;
    tuneTasteRemoved?: TasteRemoval[];
    tuneHistory?: TuneHistory;
    otherHistory?: TuneHistory;
    recentTunes?: PlaylistTuneRef[];
  }) => void;
  /** Undo a mark: the tune's mark as it was before, or none. */
  restoreTuneTaste: (tune: TasteTune, previous: TasteMark | null) => void;
  /** Remove one ROW. Takes an `entryId`, not a ref key — a set may hold the
   * same tune twice and only one of them is being removed. */
  removeFromPlaylist: (id: string, entryId: string) => void;
  movePlaylistEntry: (id: string, entryId: string, toIndex: number) => void;
  setPlaylistEntryNote: (id: string, entryId: string, note: string) => void;
  replacePlaylistEntryRef: (id: string, entryId: string, ref: PlaylistTuneRef) => void;
  /** Mark where in a set the player now is; recorded by the tune-load seam. */
  enterSetAt: (playlistId: string, entryId: string) => void;
  /** The tune on screen was opened from this playlist row: play it with the
   *  row's call. Recorded by the tune-load seam, after the load. */
  openedFromPlaylist: (playlistId: string, entryId: string) => void;
  /** Save how the tune on screen plays as its playlist row's call. False when
   *  the tune was not opened from a playlist, or the playlist is view only. */
  savePlaylistCall: () => boolean;
  /** Clear the call of the row the tune on screen was opened from. */
  clearPlaylistCall: () => void;
  leaveSet: () => void;
  setAutoAdvanceSet: (on: boolean) => void;
  requestPlayback: () => void;
  clearPendingPlay: () => void;
  noteSetSkip: (message: string | null) => void;
  /** Record what the loaded tune is; called by the tune-load seam. */
  noteCurrentTuneRef: (ref: PlaylistTuneRef) => void;
  /**
   * Add the tune on screen to a playlist, saving it first when there is no
   * live reference for it. Returns what it stored, so the caller can say.
   */
  addCurrentTuneToPlaylist: (playlistId: string) => {
    ok: boolean;
    saved: boolean;
    title: string;
    /** The row it made; null when nothing was added. */
    entryId: string | null;
  };
}

/** localStorage key for the raw playlist source — kept OUT of the persisted
 * `vlc:v2` blob so editing chords never re-serialises a ~1MB playlist. */
const PLAYLIST_KEY = 'vlc:playlist';

export interface AddChordInput {
  root: string;
  chordType: ChordTypeId;
  beats?: number;
  bass?: string;
  quality?: string;
  noChord?: boolean;
  targetTopNote?: string;
}

export interface NewTuneInput {
  title?: string;
  composer?: string;
  style?: string;
  key?: string;
}

export interface ChordPatch {
  root?: string;
  chordType?: ChordTypeId;
  beats?: number;
  bass?: string;
  quality?: string;
  parentheses?: IRealChord['parentheses'];
  noChord?: boolean;
  targetTopNote?: string | undefined;
}

export interface MeasurePatch {
  open?: BarlineOpen;
  close?: BarlineClose;
  section?: string | undefined;
  sectionRole?: import('@/music/ireal/types').SectionRole | undefined;
  feel?: import('@/music/score/types').FeelCue | undefined;
  bandCue?: import('@/music/score/types').BandCue | undefined;
  ending?: number | undefined;
  timeSig?: [number, number] | undefined;
  pickupBeats?: number | undefined;
  barRepeat?: (1 | 2) | undefined;
  coda?: boolean;
  segno?: boolean;
  fermata?: boolean;
  staffText?: string | undefined;
  directive?: string | undefined;
}

const DEFAULT_TEMPO = 100;
/** The tempo range every tempo write clamps to — the transport's stepper,
 *  slider and tap button share it, so no control can step outside it. It is
 *  defined beside the tune playback model, whose guards clamp to it too. */
export { TEMPO_MIN, TEMPO_MAX };

/** Stable identity for a tune's band memory (title + composer, case-folded). */
/**
 * The LEGACY per-tune key: lowercased "title composer". Renaming a tune
 * orphans its remembered band and two same-titled tunes collide — which is
 * why stable ids replaced it (#176). Kept only so bands remembered under the
 * old scheme still resolve, and migrate, on load.
 */
function legacyTuneKeyOf(chart: IRealChart): string {
  return `${(chart.title ?? '').trim().toLowerCase()} ${(chart.composer ?? '').trim().toLowerCase()}`;
}

/**
 * The per-tune identity band settings are remembered under. A `Score.id` is a
 * STABLE identity that survives renaming — saved tunes get one from
 * `saveCurrentAs`, openbook tunes from their manifest entry. A tune with no
 * durable identity (a chart loaded straight from the bundled iReal library,
 * which is never written back to) falls back to the legacy title key: that is
 * the documented session-only fallback in #123, and it keeps today's
 * behaviour for exactly the tunes that have nowhere to store an id.
 */
export function tuneIdentityOf(score: Score, chart: IRealChart): string {
  return score.id ? `id:${score.id}` : legacyTuneKeyOf(chart);
}

/** Clamp the 0..1 density fields. Lives with the single band mutation so an
 *  out-of-range amount cannot enter the store by any route. */
function clampBandPatch(patch: Partial<StyleBand>): Partial<StyleBand> {
  const out = { ...patch };
  const unit = (v: number) => Math.max(0, Math.min(1, v));
  if (out.bassAmount !== undefined) out.bassAmount = unit(out.bassAmount);
  if (out.compAmount !== undefined) out.compAmount = unit(out.compAmount);
  // A swing offset is held to its range; one that is not a number is dropped.
  for (const key of SWING_OFFSET_KEYS) {
    if (out[key] === undefined) continue;
    const offset = normalizeSwingOffset(out[key]);
    if (offset === undefined) delete out[key];
    else out[key] = offset;
  }
  return out;
}

/** The voicing settings a tune remembers — the live values, snapshotted. */
function liveVoicingSettings(s: AppState): TuneVoicingSettings {
  return {
    voicingFamily: s.voicingFamily,
    stringSet: s.stringSet,
    freeStringSet: s.freeStringSet,
    startingInversion: s.startingInversion,
    voiceLeadingEngine: s.voiceLeadingEngine,
  };
}

/**
 * THE voicing-settings mutation: set the live value AND remember the whole
 * set for this tune, the `patchBand` rule — so the family, register, engine
 * and opening voice you chose for a tune come back with it, and a tune you
 * never touched keeps whatever is live.
 */
function rememberVoicing(s: AppState, patch: Partial<TuneVoicingSettings>): Partial<AppState> {
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  const voicing = { ...liveVoicingSettings(s), ...patch } as TuneVoicingSettings;
  const rememberedFamily = s.tuneArrangements[key]?.voicing?.voicingFamily;
  // A paused family's live fallback must not overwrite its saved selection
  // when the user only changes the register, engine or opening voice.
  if (
    patch.voicingFamily === undefined &&
    rememberedFamily &&
    availableVoicingFamily(rememberedFamily) !== rememberedFamily
  ) {
    voicing.voicingFamily = rememberedFamily;
  }
  return {
    ...(patch as Partial<AppState>),
    currentTuneKey: key,
    tuneArrangements: {
      ...s.tuneArrangements,
      [key]: { ...s.tuneArrangements[key], voicing },
    },
  };
}

/** Pins are per tune: write the current tune's record. */
function updateTunePins(
  s: AppState,
  update: (pins: TuneArrangement['pins']) => TuneArrangement['pins'],
): Partial<AppState> {
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  const record = s.tuneArrangements[key] ?? {};
  const pins = update(record.pins);
  const next: TuneArrangement = { ...record };
  if (pins && Object.keys(pins).length) next.pins = pins;
  else delete next.pins;
  const tuneArrangements = { ...s.tuneArrangements };
  if (next.voicing || next.pins) tuneArrangements[key] = next;
  else delete tuneArrangements[key];
  return { currentTuneKey: key, tuneArrangements };
}

/** A remembered voicing record is untrusted on the way back in: every field
 * must be recognized by the model. Paused families remain valid saved data. */
function normalizeTuneVoicing(raw: unknown): TuneVoicingSettings | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const v = raw as Record<string, unknown>;
  if (
    !isVoicingFamilySelection(v.voicingFamily) ||
    (v.stringSet !== 'middle' && v.stringSet !== 'upper') ||
    typeof v.freeStringSet !== 'boolean' ||
    !Number.isInteger(v.startingInversion) ||
    (v.startingInversion as number) < 0 ||
    (v.startingInversion as number) > 3 ||
    typeof v.voiceLeadingEngine !== 'string'
  ) {
    return undefined;
  }
  return {
    voicingFamily: v.voicingFamily,
    stringSet: v.stringSet,
    freeStringSet: v.freeStringSet,
    startingInversion: v.startingInversion as number,
    voiceLeadingEngine: normalizeVoiceLeadingEngineId(v.voiceLeadingEngine),
  };
}

export function normalizeTuneArrangements(raw: unknown): Record<string, TuneArrangement> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, TuneArrangement> = {};
  for (const [key, entry] of Object.entries(raw as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const record: TuneArrangement = {};
    const voicing = normalizeTuneVoicing(e.voicing);
    if (voicing) record.voicing = voicing;
    const pins = normalizeVoicingPins(e.pins);
    if (pins) record.pins = pins;
    if (record.voicing || record.pins) out[key] = record;
  }
  return out;
}

/**
 * How the tune on screen plays its sections: your settings over the house
 * default's (`mergeSections`). Starts absent means the detected sections.
 */
export function tuneSectionsOf(
  s: Pick<AppState, 'currentTuneKey' | 'score' | 'chart' | 'houseDefaults' | 'tunePlayback'>,
): TuneSections {
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  return mergeSections(s.houseDefaults[key]?.settings.sections, s.tunePlayback[key]?.sections);
}

/** The current tune's arrangement record (its remembered voicing + pins). */
export function tuneArrangementOf(s: AppState): TuneArrangement | undefined {
  return s.tuneArrangements[s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart)];
}

// --- a tune's playback: your changes over the house default -----------------

/** The house revision a FIRST change to this tune was made against. Changes
 *  that predate the record (a band remembered before any house default
 *  existed) were made against none. */
function firstBasedOn(s: AppState, key: string): number {
  const older = !!s.tuneBands[key] || Object.keys(s.tuneFeels[key] ?? {}).length > 0;
  return older ? 0 : (s.houseDefaults[key]?.revision ?? 0);
}

/** Note which house revision your changes to this tune were made against. */
function withBasedOn(s: AppState, key: string): Record<string, TunePlaybackRecord> {
  if (s.tunePlayback[key]?.basedOn !== undefined) return s.tunePlayback;
  return { ...s.tunePlayback, [key]: { ...s.tunePlayback[key], basedOn: firstBasedOn(s, key) } };
}

/**
 * Remember a playback change for the tune on screen (the `patchBand` rule for
 * everything beyond the band). A carried field you set yourself is also yours
 * from here on: the next tune keeps it, rather than getting back the value a
 * house default displaced.
 */
function rememberPlayback(
  s: AppState,
  patch: Omit<TunePlaybackRecord, 'basedOn'>,
): Partial<AppState> {
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  const record: TunePlaybackRecord = {
    ...s.tunePlayback[key],
    ...patch,
    basedOn: s.tunePlayback[key]?.basedOn ?? firstBasedOn(s, key),
  };
  let displaced = s.playbackDisplaced;
  const carried = Object.keys(patch).filter((k): k is CarryKey =>
    (CARRY_KEYS as readonly string[]).includes(k),
  );
  if (carried.some((k) => k in displaced)) {
    displaced = { ...displaced };
    for (const k of carried) delete displaced[k];
  }
  return {
    currentTuneKey: key,
    tunePlayback: { ...s.tunePlayback, [key]: record },
    playbackDisplaced: displaced,
  };
}

/**
 * Change your sections record for the tune on screen. Emptied fields go, and
 * an empty record leaves the playback record rather than counting as a change.
 */
function rememberSections(
  s: AppState,
  edit: (mine: TuneSections) => TuneSections,
): Partial<AppState> {
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  const next = edit({ ...s.tunePlayback[key]?.sections });
  if (next.bands && !Object.keys(next.bands).length) delete next.bands;
  if (next.melody && !Object.keys(next.melody).length) delete next.melody;
  if (!Object.keys(next).length) {
    const rest: TunePlaybackRecord = { ...s.tunePlayback[key] };
    delete rest.sections;
    return {
      currentTuneKey: key,
      tunePlayback: { ...s.tunePlayback, [key]: rest },
    };
  }
  return rememberPlayback(s, { sections: next });
}

/** Set carried fields live and remember them for the tune. */
function carry(s: AppState, patch: Partial<Pick<AppState, CarryKey>>): Partial<AppState> {
  return { ...patch, ...rememberPlayback(s, patch as Omit<TunePlaybackRecord, 'basedOn'>) };
}

/** A key change is remembered as the tonic it lands on. */
function withKeyMemory(s: AppState, edit: Partial<AppState>): Partial<AppState> {
  const tonic = parseKey(edit.chart?.key)?.tonic;
  return tonic ? { ...edit, ...rememberPlayback(s, { key: tonic }) } : edit;
}

/**
 * Whether the tune on screen can carry a default for everyone: it must be a
 * tune other people can open too. A saved copy lives on one device (its id is
 * that device's), and a blank tune has no identity worth the name.
 */
export function canHoldHouseDefault(s: Pick<AppState, 'score' | 'chart'>): boolean {
  const title = (s.chart.title ?? '').trim();
  if (!title || title === 'Untitled Chart' || s.chart.measures.length === 0) return false;
  return !s.score.id?.startsWith('chart-');
}

/** The tune on screen's playback, as the owner would save it for everyone. */
export function houseCaptureOf(s: AppState): {
  tuneKey: string;
  title: string;
  composer: string;
  settings: HouseSettings & { schemaVersion: number };
} | null {
  if (!canHoldHouseDefault(s)) return null;
  const tuneKey = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  return {
    tuneKey,
    title: (s.chart.title ?? '').trim(),
    composer: (s.chart.composer ?? '').trim(),
    settings: captureHouseSettings(s, {
      band: pickBand(s),
      key: parseKey(s.chart.key)?.tonic ?? null,
      repeats: s.chart.repeats ?? 1,
      // The feel mapping is the one part that is not live state: the house's
      // own, with any you assigned on top.
      feels: { ...s.houseDefaults[tuneKey]?.settings.feels, ...s.tuneFeels[tuneKey] },
      sections: tuneSectionsOf({ ...s, currentTuneKey: tuneKey }),
    }),
  };
}

/** Whether you have changed this tune's playback yourself. */
export function hasOwnTunePlayback(
  s: Pick<AppState, 'tuneBands' | 'tuneFeels' | 'tunePlayback'>,
  key: string,
): boolean {
  return (
    !!s.tuneBands[key] ||
    Object.keys(s.tuneFeels[key] ?? {}).length > 0 ||
    recordHasSettings(s.tunePlayback[key])
  );
}

/** The score moved to a tonic without an undo step: a layer applying a key is
 *  how the tune OPENS, not an edit. Null when there is nothing to do. */
function scoreInTonic(score: Score, chartKey: string | undefined, tonic: string): Score | null {
  const prev = parseKey(chartKey);
  if (!prev || prev.tonic === tonic) return null;
  let delta: number;
  try {
    delta = (((pitchClassOf(tonic) - pitchClassOf(prev.tonic)) % 12) + 12) % 12;
  } catch {
    return null;
  }
  return transposeScore(score, delta, prev.mode === 'minor' ? `${tonic} minor` : tonic);
}

/** Where the tune on screen was opened from (a playlist row). */
export interface PlaylistCallSource {
  playlistId: string;
  entryId: string;
  tuneKey: string;
}

function normalizeCallSource(raw: unknown): PlaylistCallSource | null {
  if (!raw || typeof raw !== 'object') return null;
  const { playlistId, entryId, tuneKey } = raw as Record<string, unknown>;
  return typeof playlistId === 'string' &&
    typeof entryId === 'string' &&
    typeof tuneKey === 'string'
    ? { playlistId, entryId, tuneKey }
    : null;
}

/** The playlist row the tune on screen was opened from, while it still exists. */
export function playlistCallRow(
  s: Pick<AppState, 'playlistCallSource' | 'userPlaylists' | 'currentTuneKey' | 'score' | 'chart'>,
): { list: PlaylistDoc; entry: PlaylistDoc['entries'][number] } | null {
  const source = s.playlistCallSource;
  if (!source) return null;
  const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
  if (source.tuneKey !== key) return null;
  const list = s.userPlaylists.find((p) => p.id === source.playlistId);
  const entry = list?.entries.find((e) => e.entryId === source.entryId);
  return list && entry ? { list, entry } : null;
}

/** The call the tune on screen plays with, guarded; undefined for none. */
function activePlaylistCall(s: AppState): PlaylistCall | undefined {
  return normalizePlaylistCall(playlistCallRow(s)?.entry.call);
}

/** How the tune on screen plays, as a playlist row's call would save it. */
export function playlistCallOf(s: AppState): PlaylistCall {
  return capturePlaylistCall({
    tempo: s.tempo,
    band: pickBand(s),
    key: parseKey(s.chart.key)?.tonic ?? null,
    repeats: s.chart.repeats ?? 1,
    arrangementPolicy: s.arrangementPolicy,
    ending: s.ending,
  });
}

/**
 * On load, set the band for a tune and record what it was written with, then
 * resolve the rest of its playback (`resolvePlayback`). Sets `currentTuneKey`.
 * Applies live only — it does NOT re-capture into tuneBands (that's for user
 * changes).
 */
function resolveBand(
  get: () => AppState,
  set: (partial: Partial<AppState>) => void,
  chart: IRealChart,
): void {
  const s = get();
  set({
    currentTuneKey: tuneIdentityOf(s.score, chart),
    writtenPlayback: {
      tempo: s.tempo,
      key: parseKey(chart.key)?.tonic ?? null,
      repeats: chart.repeats ?? 1,
    },
  });
  resolvePlayback(get, set);
}

/** A stored band with a swing feel and every feel field: one saved before the
 *  feel existed swung the triplet eighth, and one saved before the placements
 *  and phrasing existed plays its own kind's defaults (bandFeel.ts), so that is
 *  what it gets, never the last tune's values. */
function withSwingFeel(band: StyleBand): StyleBand {
  return withBandFeel({
    ...band,
    swingFeel: isSwingFeel(band.swingFeel) ? band.swingFeel : DEFAULT_SWING_FEEL,
  });
}

/**
 * Resolve the tune on screen's playback from its layers, top first: YOUR
 * changes (remembered on this device), the HOUSE default the owner saved for
 * everyone, then the tune itself (its style preset, written tempo, key and
 * chorus count). Runs on every load, and again when you Reset or a changed
 * house default reaches a tune you have not changed.
 */
function resolvePlayback(get: () => AppState, set: (partial: Partial<AppState>) => void): void {
  const start = get();
  const chart = start.chart;
  const key = start.currentTuneKey ?? tuneIdentityOf(start.score, chart);
  const house = start.houseDefaults[key];
  const mine = start.tunePlayback[key];
  // Opened from a playlist row with a call: the call wins for what it sets.
  const call = activePlaylistCall(start);
  const bands = start.tuneBands;
  const remembered = bands[key];
  // A band remembered under the legacy title key still counts — carry it over
  // to the stable key so the memory survives the next rename. The legacy entry
  // is left in place: it costs nothing and losing data to a migration would be
  // worse than a stale key.
  const legacyKey = legacyTuneKeyOf(chart);
  const legacy = remembered ? undefined : bands[legacyKey];

  // --- the band: yours, else the house default, else the style preset ---
  if (remembered) {
    set(withSwingFeel(remembered));
  } else if (legacy) {
    set({ ...withSwingFeel(legacy), tuneBands: { ...bands, [key]: legacy } });
  } else {
    const id = matchStylePreset(chart.style);
    if (id) set({ ...getStylePreset(id)!.band });
    const score = get().score;
    const hasDedicatedBass = score.parts.some(
      (part) =>
        part.role === 'bass' &&
        part.measures.some((measure) =>
          measure.voices.some((voice) =>
            voiceTiming(voice).some(({ event }) => event.kind === 'note' && event.notes.length > 0),
          ),
        ),
    );
    // A dedicated bass part replaces the generated line in the Bass channel,
    // so that channel must be audible. So must a melody a bass plays: the
    // generated line rests only in the choruses that melody sounds in, and
    // walks the solos. Legacy lead/piano bass staves sound through Melody
    // instead and still suppress the generated bass. Empty bass parts keep
    // the preset; remembered channel choices above win.
    if (hasDedicatedBass || scoreMelodyIsBass(score)) set({ bassline: true });
    else if (scoreHasWrittenBass(score)) set({ bassline: false });
    // The house band was set up on this very tune, written bass and all.
    if (house?.settings.band) set({ ...house.settings.band });
  }
  // The playlist's feel, over whichever band that was. Only the feel: the
  // mutes and who comps stay the player's.
  if (call?.band) set({ ...call.band });
  // The voicing settings you chose for this tune come back with it; a tune
  // with no record keeps whatever is live (the drawer's defaults, or the last
  // tune's), exactly as before the record existed.
  const rememberedVoicing = normalizeTuneVoicing(get().tuneArrangements[key]?.voicing);
  if (rememberedVoicing) {
    set({
      ...rememberedVoicing,
      voicingFamily: availableVoicingFamily(rememberedVoicing.voicingFamily),
      voiceLeadingEngine: normalizeVoiceLeadingEngineId(rememberedVoicing.voiceLeadingEngine),
    });
  }

  // --- the carried fields (form, ending, mix, sounds) ---
  // Put back what the last tune's layers replaced, then layer this tune's.
  // With no layer anywhere nothing moves: these carry between tunes as before.
  set({ ...start.playbackDisplaced });
  const layer = {
    ...normalizeCarry(house?.settings),
    ...normalizeCarry(mine),
    ...(call?.policy ? { arrangementPolicy: call.policy } : {}),
    ...(call?.ending ? { ending: call.ending } : {}),
  };
  const before = get();
  const displaced: PlaybackDisplaced = {};
  for (const field of Object.keys(layer) as CarryKey[]) {
    (displaced as Record<CarryKey, unknown>)[field] = before[field];
  }
  set({ ...layer, playbackDisplaced: displaced });

  // --- the tune's own: tempo, key, chorus count ---
  const written = before.writtenPlayback;
  const side = {
    ...normalizeTuneSide(house?.settings),
    ...normalizeTuneSide(mine),
    ...normalizeTuneSide(call),
  };
  const tempo = side.tempo ?? written?.tempo;
  if (tempo !== undefined && tempo !== get().tempo) set({ tempo });
  const tonic = side.key ?? written?.key ?? undefined;
  let score = get().score;
  const inTonic = tonic ? scoreInTonic(score, get().chart.key, tonic) : null;
  if (inTonic) score = inTonic;
  const repeats = side.repeats ?? written?.repeats;
  if (repeats !== undefined && repeats !== (score.repeats ?? 1)) {
    const trainer = get().trainer;
    const minimum = trainer.enabled ? trainerPassesToTarget(trainer) : 1;
    score = formEdit.setRepeats(score, Math.max(minimum, repeats), Math.max(16, minimum, repeats));
  }
  if (score !== get().score) {
    // Not an edit: the reference to what is loaded still names it, since
    // opening that reference applies the same layers.
    set({ ...commitScore(score), currentTuneRef: get().currentTuneRef });
  }
  set({ currentTuneKey: key, appliedHouseRevision: house?.revision ?? null });
}

/** Your changes to one tune's playback, dropped (the voicings and pins stay:
 *  they are how you READ the tune, not how it plays). On the tune on screen a
 *  band remembered under its LEGACY title key goes too, or the next resolve
 *  would carry it straight back over (resolvePlayback's migration). */
function withoutOwnPlayback(s: AppState, key: string): Partial<AppState> {
  const tuneBands = { ...s.tuneBands };
  delete tuneBands[key];
  if (key === s.currentTuneKey) delete tuneBands[legacyTuneKeyOf(s.chart)];
  const tuneFeels = { ...s.tuneFeels };
  delete tuneFeels[key];
  const tunePlayback = { ...s.tunePlayback };
  delete tunePlayback[key];
  return { tuneBands, tuneFeels, tunePlayback };
}

/** A changed house default reaches the tune on screen when you have not
 *  changed that tune yourself; otherwise the tune offers it (Tune details). */
function followHouse(get: () => AppState, set: (partial: Partial<AppState>) => void): void {
  const s = get();
  const key = s.currentTuneKey;
  if (!key) return;
  const revision = s.houseDefaults[key]?.revision ?? null;
  if (revision === s.appliedHouseRevision || hasOwnTunePlayback(s, key)) return;
  resolvePlayback(get, set);
}

/**
 * Edit feedback: sound the pitch a score edit just produced. A notation
 * editor plays what you type — a note you enter or nudge with the arrow keys
 * should be heard, not checked visually against the staff. Never fires while
 * the transport runs: a stray preview over the band reads as a wrong note in
 * the arrangement, not as feedback.
 */
function previewEditedPitch(midi: number | null | undefined): void {
  if (midi == null) return;
  const player = getChordPlayer();
  if (player.getTransportState() === 'playing') return;
  void player.previewNote(midi);
}

function lastMeasure(chart: IRealChart): IRealMeasure {
  return chart.measures[chart.measures.length - 1];
}

/** Re-derive incidental invariants: measure 1 has a time sig, last bar is final. */
function normalize(chart: IRealChart): IRealChart {
  if (chart.measures.length === 0) {
    chart.measures.push({ id: uid('m'), chords: [], timeSig: chart.timeSignature });
  }
  if (!chart.measures[0].timeSig) chart.measures[0].timeSig = chart.timeSignature;
  const last = lastMeasure(chart);
  if (!last.close) last.close = 'final';
  // Demote stray interior finals, but retain the boundary that separates a
  // regular chorus from a detached paired-coda target.
  chart.measures.forEach((m, i) => {
    if (
      i < chart.measures.length - 1 &&
      m.close === 'final' &&
      !isPairedCodaFinal(chart.measures, i)
    ) {
      m.close = 'single';
    }
  });
  return chart;
}

/**
 * Lift a chart edit onto the score truth. The score is normalized (the model
 * owns the invariants) and the chart is re-projected, so the pair is always
 * exact: chart === scoreToChart(score). chartToScore is deterministic and
 * total, and scoreToChart∘chartToScore is idempotent (pinned in
 * src/music/score/convert.test.ts), so repeated lifts are stable.
 */
function liftChart(chart: IRealChart): { score: Score; chart: IRealChart } {
  const score = normalizeScore(chartToScore(chart));
  return { score, chart: scoreToChart(score) };
}

/** Commit a natively-edited score: normalize, re-project, merge extras.
 *  The write carries `score`, so the lifting seam passes it through. */
function commitScore(score: Score, extra?: Partial<AppState>): Partial<AppState> {
  const normalized = normalizeScore(score);
  return { score: normalized, chart: scoreToChart(normalized), ...extra };
}

/** An active speed schedule owns a minimum number of whole-form passes. Keep
 * that invariant when a tune, score snapshot, or persisted session is loaded. */
function withTrainerRepeatFloor(score: Score, trainer: TrainerConfig): Score {
  if (!trainer.enabled) return score;
  const minimumRepeats = trainerPassesToTarget(trainer);
  return (score.repeats ?? 1) < minimumRepeats
    ? formEdit.setRepeats(score, minimumRepeats, Math.max(16, minimumRepeats))
    : score;
}

function chartWithTrainerRepeatFloor(chart: IRealChart, trainer: TrainerConfig): IRealChart {
  if (!trainer.enabled) return chart;
  const minimumRepeats = trainerPassesToTarget(trainer);
  return (chart.repeats ?? 1) < minimumRepeats ? { ...chart, repeats: minimumRepeats } : chart;
}

function cloneChart(chart: IRealChart): IRealChart {
  return {
    ...chart,
    timeSignature: [...chart.timeSignature] as [number, number],
    measures: chart.measures.map((m) => ({
      ...m,
      timeSig: m.timeSig ? ([...m.timeSig] as [number, number]) : undefined,
      chords: m.chords.map((c) => ({ ...c })),
      melody: m.melody ? m.melody.map((n) => ({ ...n })) : undefined,
    })),
  };
}

// A complete head can easily exceed one hundred atomic entry/form edits.
// Whole-score snapshots are bounded, but retain enough history to rebuild a
// 32-bar tune without losing the beginning of the current editing session.
const MELODY_HISTORY_LIMIT = 256;

type ScoreEditorContext = Pick<
  AppState,
  | 'selectedNoteId'
  | 'selectedNoteheadIndex'
  | 'selectedMeasureId'
  | 'selectedChordId'
  | 'insertionMeasureId'
  | 'scoreContentTarget'
  | 'entryVoice'
  | 'scoreEntryCursor'
  | 'entryDuration'
  | 'passage'
  | 'loopRegion'
>;

interface ScoreEditorSnapshot {
  /** Identity check keeps older score/cursor-only history usable. */
  score: Score;
  context: ScoreEditorContext;
}

function captureScoreEditor(s: ScoreEditorContext & { score: Score }): ScoreEditorSnapshot {
  return {
    score: s.score,
    context: {
      selectedNoteId: s.selectedNoteId,
      selectedNoteheadIndex: s.selectedNoteheadIndex,
      selectedMeasureId: s.selectedMeasureId,
      selectedChordId: s.selectedChordId,
      insertionMeasureId: s.insertionMeasureId,
      scoreContentTarget: s.scoreContentTarget ? { ...s.scoreContentTarget } : null,
      entryVoice: s.entryVoice,
      scoreEntryCursor: s.scoreEntryCursor ? { ...s.scoreEntryCursor } : null,
      entryDuration: s.entryDuration,
      passage: s.passage ? { ...s.passage } : null,
      loopRegion: s.loopRegion,
    },
  };
}

/** External callers can still supply the older score/cursor-only stacks.
 * Keep missing context entries empty instead of applying another edit's selection. */
function alignedScoreEditors(
  scores: Score[],
  editors: (ScoreEditorSnapshot | null)[],
): (ScoreEditorSnapshot | null)[] {
  return scores.map((score, index) => (editors[index]?.score === score ? editors[index] : null));
}

/** Reset for the score editor whenever a different tune replaces the chart. */
const freshMelodyEditor = {
  selectedNoteheadIndex: null,
  selectedNoteId: null,
  scoreEntryCursor: null,
  scoreContentTarget: null as voiceEdit.ContentTarget | null,
  melodyPast: [] as Score[],
  melodyFuture: [] as Score[],
  entryCursorPast: [] as (ScoreEntryCursor | null)[],
  entryCursorFuture: [] as (ScoreEntryCursor | null)[],
  scoreEditorPast: [] as (ScoreEditorSnapshot | null)[],
  scoreEditorFuture: [] as (ScoreEditorSnapshot | null)[],
  passage: null as Passage | null,
  loopRegion: false,
};

/** Everything a tune-REPLACING action resets, plus the load counter — one
 *  spread, so an action that starts a different tune cannot reset the editor
 *  and forget to count the load. */
function freshTune(s: AppState) {
  return {
    ...freshMelodyEditor,
    tuneLoadSeq: s.tuneLoadSeq + 1,
    // A new tune is opened from nowhere until the load seam says otherwise.
    playlistCallSource: null as PlaylistCallSource | null,
    // Its sections are its own: the band controls start at All.
    bandScope: null as string | null,
    // And it plays from the top.
    playCursor: null as string | null,
  };
}

/** Padding rests are implementation detail; pitched events and authored or
 * imported rests can own selection at a rhythmic caret. */
function selectableEventIdAt(
  score: Score,
  cursor: ScoreEntryCursor,
  voiceIndex: number,
  target?: voiceEdit.ContentTarget,
): string | null {
  const event = voiceEdit.findEventAt(score, cursor.measureId, voiceIndex, cursor.beat, target);
  if (!event || (event.rest && /^r[:-]/.test(event.id))) return null;
  return event.id;
}

function cursorForEvent(
  score: Score,
  id: string | null,
  fallbackMidi = 60,
  target?: voiceEdit.ContentTarget,
  headIndex?: number | null,
): { cursor: ScoreEntryCursor; voiceIndex: number; target: voiceEdit.ContentTarget } | null {
  if (!id) return null;
  const event = voiceEdit.describeEvent(score, id, target);
  const measureId = event ? score.form[event.measureIndex]?.id : undefined;
  if (!event || !measureId) return null;
  return {
    cursor: {
      measureId,
      beat: event.beat,
      midi: selectedHeadMidi(score, id, headIndex, target) ?? event.midi ?? fallbackMidi,
    },
    voiceIndex: event.voiceIndex,
    target: { partId: event.partId, staffIndex: event.staffIndex },
  };
}

function selectedHeadMidi(
  score: Score,
  id: string,
  headIndex?: number | null,
  target?: voiceEdit.ContentTarget,
): number | null {
  const heads = voiceEdit.getNoteHeads(score, id, target);
  const head = heads?.[headIndex ?? voiceEdit.defaultNoteHeadIndex(score, id, target)];
  return head ? midiOf(head.pitch) : null;
}

/** Resolve a session target without ever redirecting an explicit valid
 * selection. Legacy scores choose their lead-role part, then part zero. */
function scoreContentTargetOf(
  score: Score,
  requested: voiceEdit.ContentTarget | null,
): voiceEdit.ContentTarget | undefined {
  if (requested) {
    const part = score.parts.find((candidate) => candidate.id === requested.partId);
    if (
      part &&
      Number.isInteger(requested.staffIndex) &&
      requested.staffIndex >= 0 &&
      requested.staffIndex < part.staves.length
    ) {
      return requested;
    }
  }
  const part = score.parts.find((candidate) => candidate.role === 'lead') ?? score.parts[0];
  return part ? { partId: part.id, staffIndex: 0 } : undefined;
}

function scoreEntryGrid(
  score: Score,
  cursor: ScoreEntryCursor | null,
  durationQuarters: number,
): number {
  const measureId = score.form.some((measure) => measure.id === cursor?.measureId)
    ? cursor!.measureId
    : (score.form.at(-1)?.id ?? '');
  return entryGridInBeats(score, measureId, durationQuarters);
}

/** Preserve the caret's musical offset when a form edit changes the active
 * denominator. Beat numbers are denominator units (beat 3 in 6/8 is the same
 * quarter offset as beat 2 in 3/4), so numeric clamping alone is not enough. */
function reconcileEntryCursorAfterFormEdit(
  previous: Score,
  next: Score,
  cursor: ScoreEntryCursor | null,
  durationQuarters: number,
): ScoreEntryCursor | null {
  let remapped = cursor;
  if (
    cursor &&
    previous.form.some((measure) => measure.id === cursor.measureId) &&
    next.form.some((measure) => measure.id === cursor.measureId)
  ) {
    const oldUnitsPerQuarter = entryDurationInBeats(previous, cursor.measureId, 1);
    const newUnitsPerQuarter = entryDurationInBeats(next, cursor.measureId, 1);
    const convertedBeat = 1 + ((cursor.beat - 1) * newUnitsPerQuarter) / oldUnitsPerQuarter;
    remapped = { ...cursor, beat: convertedBeat };
  }
  return reconcileEntryCursor(next, remapped, scoreEntryGrid(next, remapped, durationQuarters));
}

/** Keep deletion local: a caret in the removed measure moves to the measure
 * that took its place (or the previous final measure), preserving its musical
 * offset across any denominator change. Surviving carets still remap when the
 * deleted measure carried an inherited meter change. */
function reconcileEntryCursorAfterMeasureDelete(
  previous: Score,
  next: Score,
  deletedId: string,
  cursor: ScoreEntryCursor | null,
  durationQuarters: number,
): ScoreEntryCursor | null {
  if (!cursor || cursor.measureId !== deletedId) {
    return reconcileEntryCursorAfterFormEdit(previous, next, cursor, durationQuarters);
  }
  if (next.form.length === 0) return null;
  const deletedIndex = previous.form.findIndex((measure) => measure.id === deletedId);
  const target = next.form[Math.min(Math.max(0, deletedIndex), next.form.length - 1)];
  const oldUnitsPerQuarter = entryDurationInBeats(previous, deletedId, 1);
  const newUnitsPerQuarter = entryDurationInBeats(next, target.id, 1);
  const remapped: ScoreEntryCursor = {
    ...cursor,
    measureId: target.id,
    beat: 1 + ((cursor.beat - 1) * newUnitsPerQuarter) / oldUnitsPerQuarter,
  };
  return reconcileEntryCursor(next, remapped, scoreEntryGrid(next, remapped, durationQuarters));
}

const editorVoice = (voiceIndex: number): 0 | 1 => (voiceIndex === 1 ? 1 : 0);

/** Restore authored selections exactly, including an intentionally empty
 * selection. Missing IDs are cleared; a deleted staff falls back to the lead. */
function restoreScoreEditor(score: Score, context: ScoreEditorContext): ScoreEditorContext {
  const ids = score.form.map((measure) => measure.id);
  const measureId = (id: string | null) => (id && ids.includes(id) ? id : null);
  const target = context.scoreContentTarget
    ? (scoreContentTargetOf(score, context.scoreContentTarget) ?? null)
    : null;
  const selectedNoteId =
    context.selectedNoteId &&
    voiceEdit.describeEvent(score, context.selectedNoteId, target ?? undefined)
      ? context.selectedNoteId
      : null;
  const entryDuration =
    Number.isFinite(context.entryDuration) && context.entryDuration > 0 ? context.entryDuration : 1;
  const cursor = context.scoreEntryCursor;
  const passage = context.passage
    ? normalizePassage(ids, context.passage.fromMeasureId, context.passage.toMeasureId)
    : null;
  return {
    selectedNoteId,
    selectedNoteheadIndex:
      selectedNoteId &&
      context.selectedNoteheadIndex != null &&
      voiceEdit.getNoteHeads(score, selectedNoteId, target ?? undefined)?.[
        context.selectedNoteheadIndex
      ]
        ? context.selectedNoteheadIndex
        : null,
    selectedMeasureId: measureId(context.selectedMeasureId),
    selectedChordId: score.form.some((measure) =>
      measure.harmony?.some((chord) => chord.id === context.selectedChordId),
    )
      ? context.selectedChordId
      : null,
    insertionMeasureId: measureId(context.insertionMeasureId),
    scoreContentTarget: target,
    entryVoice: editorVoice(context.entryVoice),
    scoreEntryCursor:
      cursor && measureId(cursor.measureId)
        ? reconcileEntryCursor(score, cursor, scoreEntryGrid(score, cursor, entryDuration))
        : null,
    entryDuration,
    passage,
    loopRegion: context.loopRegion && passage !== null,
  };
}

/** Cursor-only history predates full context snapshots. Preserve its old
 * event-at-caret behaviour without overriding explicit new empty selections. */
function legacyScoreEditor(
  score: Score,
  cursor: ScoreEntryCursor | null,
  s: ScoreEditorContext,
): ScoreEditorContext {
  const target = scoreContentTargetOf(score, s.scoreContentTarget);
  const reconciled = reconcileEntryCursor(
    score,
    cursor,
    scoreEntryGrid(score, cursor, s.entryDuration),
  );
  return restoreScoreEditor(score, {
    ...s,
    selectedChordId: null,
    selectedMeasureId: null,
    insertionMeasureId: null,
    scoreContentTarget: target ?? null,
    scoreEntryCursor: reconciled,
    selectedNoteheadIndex: null,
    selectedNoteId: reconciled
      ? selectableEventIdAt(score, reconciled, s.entryVoice, target)
      : null,
  });
}

function scoreTieTargetId(
  s: Pick<
    AppState,
    'score' | 'selectedNoteId' | 'scoreEntryCursor' | 'entryVoice' | 'scoreContentTarget'
  >,
  preferPrevious: boolean,
): string | null {
  const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
  const selected = s.selectedNoteId
    ? voiceEdit.describeEvent(s.score, s.selectedNoteId, target)
    : null;
  const selectedId = selected && !selected.rest ? s.selectedNoteId : null;
  let previous = s.scoreEntryCursor
    ? voiceEdit.findEventBefore(
        s.score,
        s.scoreEntryCursor.measureId,
        s.entryVoice,
        s.scoreEntryCursor.beat,
        target,
      )
    : null;
  if (!previous && s.scoreEntryCursor) {
    const measureIndex = s.score.form.findIndex(
      (measure) => measure.id === s.scoreEntryCursor!.measureId,
    );
    const previousMeasure = measureIndex > 0 ? s.score.form[measureIndex - 1] : null;
    if (previousMeasure) {
      previous = voiceEdit.findLastEvent(s.score, previousMeasure.id, s.entryVoice, target);
    }
  }
  const previousId = previous && !previous.rest ? previous.id : null;
  return preferPrevious ? (previousId ?? selectedId) : (selectedId ?? previousId);
}

/** Apply a Score Studio edit, pushing the previous whole Score onto the undo
 * stack. Whole snapshots keep notation, form, harmony and metadata atomic. */
function commitScoreEdit(
  s: ScoreEditorContext &
    Pick<AppState, 'score' | 'melodyPast' | 'entryCursorPast' | 'scoreEditorPast'>,
  next: Score | null,
  extra?: Partial<AppState>,
): Partial<AppState> {
  if (!next || next === s.score) return {};
  return {
    ...commitScore(next),
    ...extra,
    melodyPast: [...s.melodyPast, s.score].slice(-MELODY_HISTORY_LIMIT),
    melodyFuture: [],
    entryCursorPast: [...s.entryCursorPast, s.scoreEntryCursor].slice(-MELODY_HISTORY_LIMIT),
    entryCursorFuture: [],
    scoreEditorPast: [
      ...alignedScoreEditors(s.melodyPast, s.scoreEditorPast),
      captureScoreEditor(s),
    ].slice(-MELODY_HISTORY_LIMIT),
    scoreEditorFuture: [],
  };
}

/**
 * The state a HARMONY edit applies to: while an audition runs, the harmony
 * underneath it. The preview is the active layer, so an op run on `s.score`
 * files the edit into the preview, and the write seam then drops that layer
 * with the edit inside it, leaving an undo step that only restores the
 * audition for the seam to drop again. Run on the harmony underneath, the
 * edit lands in the layer the audition was derived from (or the Original),
 * the seam ends the audition, and undo goes back to where the audition began.
 * A chord that exists only in the audition is not there to edit, so the op
 * refuses and the audition plays on: the Studio's "Keep the preview to edit
 * it" rule, for every edit path.
 */
function beneathAudition<S extends Pick<AppState, 'score' | 'reharmPreview'>>(s: S): S {
  return s.reharmPreview ? { ...s, score: s.reharmPreview.base } : s;
}

/**
 * Derive a passage over `score`: the selected bars when there is a passage,
 * else the whole form. One helper so the committed derivation, the audition
 * and the command cannot drift on scoping. Null when there is nothing in
 * scope or the settings produce no change.
 */
function derivePassageOn(
  score: Score,
  passage: Passage | null,
  options: ReharmPassageOptions & { name?: string; preview?: boolean; tempo?: number },
): { score: Score; layerId: string; count: number } | null {
  const ctx = buildReharmContext(score);
  const ids = score.form.map((m) => m.id);
  const inScope = passage ? new Set(passageMeasureIds(ids, passage)) : null;
  const indexes = ctx.measureIds
    .map((id, i) => (inScope === null || inScope.has(id) ? i : -1))
    .filter((i) => i >= 0);
  if (indexes.length === 0) return null;
  const candidates = derive(ctx, {
    ...options,
    from: indexes[0],
    to: indexes[indexes.length - 1],
  });
  const applied = applyDerivation(score, candidates, options.name, {
    preview: options.preview,
    derivation: resolveDeriveOptions(options),
  });
  return applied ? { ...applied, count: candidates.length } : null;
}

/** Repair a persisted chart blob (or convert a legacy SequenceChord[]). */
function normalizeStoredChart(raw: unknown, title: string, key: string): IRealChart {
  try {
    if (Array.isArray(raw)) {
      return normalize(
        sequenceToChart(raw as SequenceChord[], title || 'Untitled Chart', key || 'C'),
      );
    }
    if (raw && typeof raw === 'object' && Array.isArray((raw as IRealChart).measures)) {
      const chart = raw as IRealChart;
      if (!Array.isArray(chart.measures) || chart.measures.length === 0)
        return createEmptyChart(title, key);
      return normalize(cloneChart(chart));
    }
  } catch {
    /* fall through */
  }
  return createEmptyChart(title || 'Untitled Chart', key || 'C');
}

/** Initial truth: an empty chart lifted once, so score/chart start in sync. */
/** A compile error here means the persistence registry names a field that is
 *  not (or is no longer) part of AppState — a rename or a typo. */
const _persistedKeysAreAppState: readonly (keyof AppState)[] = PERSISTED_KEYS;
void _persistedKeysAreAppState;

const INITIAL_TRUTH = liftChart(createEmptyChart());

/**
 * The write seam (slice 4): any state write that carries `chart` (and no
 * explicit `score`) is lifted onto the score truth atomically — callers keep
 * writing the familiar chart shape, but `score` is what they're really
 * writing. Applied to BOTH the creator's `set` and the store api's
 * `setState` (tests and devtools write through the latter), so there is no
 * unlifted path to either field.
 */
/**
 * Any write carrying `chart` or `score` changes the music, and the moment the
 * music changes `currentTuneRef` stops describing what is loaded — the score
 * on screen is no longer the thing that reference names.
 *
 * Invalidating it HERE rather than at each edit action is the whole point: this
 * is already the one seam every musical write passes through, so there is no
 * edit path that can forget. Loads clear it too and that is fine — the tune
 * load seam re-records the ref immediately afterwards, so a genuine load ends
 * up with the ref and an edit ends up without one.
 */
function clearsTuneRef(p: object): boolean {
  return ('chart' in p || 'score' in p) && !('currentTuneRef' in p);
}

/**
 * An AUDITION ends the moment anything else writes the music. Same seam and
 * same reasoning as `clearsTuneRef` above: a load, an undo, a chord edit or a
 * layer switch all mean the preview no longer describes what is on screen, and
 * catching that here is what makes it impossible for an edit path to forget.
 * The three preview actions carry `reharmPreview` themselves, which is how
 * they say "this write IS the audition" and pass through untouched.
 *
 * The state is only half of it: the preview LAYER is applied to the score, so
 * it comes off too, giving the form back the harmony underneath (and the layer
 * the audition was derived from). Nothing being auditioned is the fast path —
 * `dropReharmPreview` returns the same object and no work is done.
 */
function endsReharmPreview(p: object): boolean {
  return ('chart' in p || 'score' in p) && !('reharmPreview' in p);
}

/** A persisted `keyStamps` blob, guarded: finite numbers on known keys only. */
function normalizeKeyStamps(raw: unknown): Partial<Record<CrossTabKey, number>> {
  const out: Partial<Record<CrossTabKey, number>> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const key of CROSS_TAB_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Each shared key's guard — the same checks the rehydrate `merge` applies. */
function adoptKey(key: CrossTabKey, raw: unknown, local: AppState): unknown {
  switch (key) {
    case 'savedCharts':
      return Array.isArray(raw) ? checkSavedTunes(raw).kept : undefined;
    case 'deletedSavedTunes':
      return Array.isArray(raw)
        ? raw.filter((id): id is string => typeof id === 'string')
        : undefined;
    case 'userPlaylists':
    case 'deletedPlaylists':
    case 'recentTunes':
      return Array.isArray(raw) ? raw : undefined;
    case 'tuneHistory':
      return normalizeTuneHistory(raw);
    case 'tuneTaste':
      return normalizeTuneTaste(raw);
    case 'tuneTasteRemoved':
      return normalizeTasteRemovals(raw);
    case 'otherHistory':
      return normalizeTuneHistory(raw);
    case 'historyAccount':
      return typeof raw === 'string' && raw ? raw : raw === null ? null : undefined;
    case 'tuneBands':
    case 'tuneFeels':
      return isPlainObject(raw) ? raw : undefined;
    case 'tuneArrangements':
      return normalizeTuneArrangements(raw);
    case 'tunePlayback':
      return normalizeTunePlayback(raw);
    case 'houseDefaults':
      return normalizeHouseDefaults(raw, getStylePreset('swing')!.band);
    case 'accountSync':
      return normalizeAccountSync(raw);
    case 'playlistLinks':
      return normalizePlaylistLinks(raw);
    case 'playlistDeletes':
      return normalizePlaylistDeletes(raw);
    case 'playlistAccount':
      return typeof raw === 'string' && raw ? raw : raw === null ? null : undefined;
    case 'alternativeShapes': {
      const builtin = new Set(BUILTIN_ALTERNATIVE_SHAPES.map((shape) => shape.id));
      return normalizeAlternativeShapes(raw).filter((shape) => !builtin.has(shape.id));
    }
    case 'preferredAlternativeIds':
      return normalizePreferredAlternativeIds(raw, allAlternativeShapes(local.alternativeShapes));
  }
}

/**
 * What this store should take from ANOTHER tab's persisted state: each shared
 * key whose change stamp there is newer than here, guarded like a rehydrate,
 * with the stamps it came with. Null when nothing is newer — which is also
 * what ends the exchange: the write this adoption causes carries the same
 * stamps back, and the other tab finds nothing newer in it. Exported for
 * src/state/crossTab.ts and its tests.
 */
export function crossTabPatch(
  foreign: Record<string, unknown>,
  local: AppState,
): Partial<AppState> | null {
  const stamps = normalizeKeyStamps(foreign.keyStamps);
  const patch: Record<string, unknown> = {};
  const adopted: Partial<Record<CrossTabKey, number>> = {};
  // Shapes before the preferences that name them.
  for (const key of CROSS_TAB_KEYS) {
    const theirs = stamps[key];
    if (theirs === undefined || !(key in foreign)) continue;
    if (theirs <= (local.keyStamps[key] ?? 0)) continue;
    const value = adoptKey(key, foreign[key], {
      ...local,
      ...(patch as Partial<AppState>),
    } as AppState);
    if (value === undefined) continue;
    patch[key] = value;
    adopted[key] = theirs;
  }
  if (Object.keys(adopted).length === 0) return null;
  return { ...(patch as Partial<AppState>), keyStamps: { ...local.keyStamps, ...adopted } };
}

/**
 * Stamp the shared documents this write changes (by reference: every store
 * write replaces what it changes). A write that carries `keyStamps` itself is
 * an adoption from another tab and keeps the stamps it brings.
 */
function stampSharedKeys(p: object, current: AppState | undefined): object {
  if (!current || 'keyStamps' in p) return p;
  let stamps: Partial<Record<CrossTabKey, number>> | null = null;
  const record = p as Record<string, unknown>;
  for (const key of CROSS_TAB_KEYS) {
    if (key in p && record[key] !== current[key]) {
      stamps ??= { ...current.keyStamps };
      stamps[key] = Math.max(Date.now(), (current.keyStamps[key] ?? 0) + 1);
    }
  }
  return stamps ? { ...p, keyStamps: stamps } : p;
}

function liftingWrite<S>(rawSet: S, getState: () => AppState): S {
  const augment = (raw: unknown, current?: AppState): unknown => {
    if (!raw || typeof raw !== 'object') return raw;
    const p = stampSharedKeys(raw, current ?? getState());
    const lifted =
      'chart' in p && !('score' in p)
        ? { ...p, ...liftChart((p as { chart: IRealChart }).chart) }
        : p;
    const withRef = clearsTuneRef(lifted) ? { ...lifted, currentTuneRef: null } : lifted;
    if (!endsReharmPreview(withRef)) return withRef;
    const score = (withRef as { score?: Score }).score;
    const dropped = score ? reharm.dropReharmPreview(score) : undefined;
    return dropped && dropped !== score
      ? { ...withRef, reharmPreview: null, ...commitScore(dropped) }
      : { ...withRef, reharmPreview: null };
  };
  const wrapped = (partial: unknown, replace?: unknown) => {
    const next =
      typeof partial === 'function'
        ? (s: AppState) => augment((partial as (s: AppState) => unknown)(s), s)
        : augment(partial);
    return (rawSet as unknown as (p: unknown, r?: unknown) => unknown)(next, replace);
  };
  return wrapped as unknown as S;
}

/** Lift a chart-shaped saved entry to a Score snapshot; null when corrupt. */
function liftSavedChart(c: IRealChart): Score | null {
  try {
    return { ...liftChart(normalize(cloneChart(c))).score, id: c.id ?? uid('chart') };
  } catch {
    return null;
  }
}

/**
 * Persist migration (exported for tests). v1 stored the chart as a
 * SequenceChord[] and presets as Song[]; v2 stored IRealChart; v3 flips the
 * persisted truth to `score` (the chart is re-projected at rehydrate by the
 * persist `merge` hook, never stored); v4 lifts `savedCharts` to Score
 * snapshots so saved tunes keep voices/parts; v5 separates Arrange's display
 * size from the standalone Score view; v7 adds the persisted voicing-family
 * preference. One-way, versioned — lifts run once here, not on every boot.
 */
/** v6: playlist entries became typed references — lift raw SavedTune rows to
 * `{kind:'ireal', …}`; entries that already carry a kind pass through. */
function liftPlaylistEntries(p: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(p.userPlaylists)) return p;
  const userPlaylists = (p.userPlaylists as { entries?: unknown[] }[]).map((list) => ({
    ...list,
    entries: Array.isArray(list.entries)
      ? list.entries
          .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
          .map((e) => ('kind' in e ? e : { kind: 'ireal', ...e }))
      : [],
  }));
  return { ...p, userPlaylists };
}

/** v7: multiple voicing families became selectable. Every older session was
 * implicitly Drop 2. Only the default is persisted; an interim build briefly
 * stored `voicingFamily`, so accept it as the default while removing the
 * session-only field from the migrated blob. */
function liftDefaultVoicingFamily(p: Record<string, unknown>): Record<string, unknown> {
  const defaultVoicingFamily: VoicingFamilySelection = isVoicingFamilySelection(
    p.defaultVoicingFamily,
  )
    ? p.defaultVoicingFamily
    : isVoicingFamilySelection(p.voicingFamily)
      ? p.voicingFamily
      : 'drop-2';
  const next: Record<string, unknown> = { ...p, defaultVoicingFamily };
  delete next.voicingFamily;
  return next;
}

/**
 * The three S/M/L tier settings become two percentages.
 *
 * `arrangementSize` is dropped rather than mapped: Arrange remembering a
 * different notation size from the Score view was an accident of having two
 * fields, not a feature anyone asked for. A user's `scoreSize` wins, because
 * that is the one they set on the page they read most.
 */
function liftDisplayScale(p: Record<string, unknown>): Record<string, unknown> {
  if (typeof p.chartScale === 'number' && typeof p.scoreScale === 'number') return p;
  const next: Record<string, unknown> = {
    ...p,
    chartScale: chartScaleFromTier(p.chartSize),
    scoreScale: scoreScaleFromTier(p.scoreSize),
  };
  delete next.chartSize;
  delete next.scoreSize;
  delete next.arrangementSize;
  return next;
}

/** v9: cross-layout voice leading ("Allow layout changes") became the
 * default. A v≤8 blob's stored `false` is overwhelmingly the OLD DEFAULT,
 * not a choice — the persist layer writes every key unconditionally — so
 * older sessions adopt the new default ONCE; switching it off afterwards
 * sticks, because a v9 blob never takes this lift again. */
function liftFreeStringSetDefault(p: Record<string, unknown>): Record<string, unknown> {
  return { ...p, freeStringSet: true };
}

/** v10: implementation-flavoured engine ids became the stable Pathfinder
 * public contract. Preserve every old selection; unknown future ids fall back
 * safely to v1 rather than leaving a dead picker value. */
function liftVoiceLeadingEngine(p: Record<string, unknown>): Record<string, unknown> {
  return { ...p, voiceLeadingEngine: normalizeVoiceLeadingEngineId(p.voiceLeadingEngine) };
}

/**
 * v11: playlists became documents.
 *
 * A bare `{id, name, entries:[ref,…]}` gains the metadata contract every other
 * user-owned entity already carried — schemaVersion, timestamps — plus a
 * stable id PER ROW, so a set can hold the same tune twice and a later merge
 * can match rows across devices instead of guessing from the referent.
 *
 * Existing playlist ids are PRESERVED: `tunesBrowseScope` and any open UI hold
 * them, and re-minting would strand a user mid-browse. `createdAt` is the
 * migration time because the real one was never recorded — inventing an
 * earlier date would be a more confident lie than the data supports.
 */
function liftPlaylistDocs(p: Record<string, unknown>): Record<string, unknown> {
  const deletedPlaylists = Array.isArray(p.deletedPlaylists) ? p.deletedPlaylists : [];
  if (!Array.isArray(p.userPlaylists)) return { ...p, deletedPlaylists };
  const now = new Date().toISOString();
  const userPlaylists = (p.userPlaylists as unknown[])
    .filter((list): list is Record<string, unknown> => !!list && typeof list === 'object')
    .map((list) => {
      if (list.kind === 'playlist') return list;
      const name =
        typeof list.name === 'string' && list.name.trim() ? list.name.trim() : 'Untitled';
      const entries = (Array.isArray(list.entries) ? list.entries : [])
        .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
        .map((e) => (e.ref && typeof e.ref === 'object' ? e : { entryId: makeEntryId(), ref: e }));
      return {
        kind: 'playlist',
        id: typeof list.id === 'string' && list.id ? list.id : makePlaylistId(name),
        schemaVersion: PLAYLIST_SCHEMA_VERSION,
        name,
        entries,
        createdAt: now,
        updatedAt: now,
      };
    });
  return { ...p, userPlaylists, deletedPlaylists };
}

export function migratePersisted(persisted: unknown, version: number): unknown {
  if (!persisted || typeof persisted !== 'object') return persisted as never;
  // v11 runs LAST and on every path: whichever branch below a blob takes, it
  // still comes out holding pre-document playlists.
  const lifted = liftPlaylistDocs(migrateThroughV10(persisted, version) as Record<string, unknown>);
  const band = version < 12 ? liftHouseBand(lifted) : lifted;
  const style = version < 13 ? liftHouseStyle(band) : band;
  return version < 14 ? liftHouseBass(style) : style;
}

/**
 * v14: the house bass is the upright and a swinging band walks with skips
 * (user ruling Oct 2026, docs/bass-feel.md). A browser holding the OLD
 * defaults moves once. The electric moves wherever it stood as the sound in
 * force: the live setting, the value a layer puts back on the next tune, and
 * the cached house defaults, which record every carried field. A swinging
 * band's `bassSkips: false` moves in the live band and in every stored band:
 * in the one day the field existed before this, false was its default, so it
 * records no choice. A per-tune record's own bass sound records only what was
 * changed, so it was a choice and stays.
 */
function liftHouseBass(p: Record<string, unknown>): Record<string, unknown> {
  const isObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);
  const skipping = (band: unknown): unknown =>
    isObject(band) && band.bassSwing === true && band.bassSkips === false
      ? { ...band, bassSkips: true }
      : band;
  const eachValue = (record: unknown, lift: (value: unknown) => unknown): unknown =>
    isObject(record)
      ? Object.fromEntries(Object.entries(record).map(([key, value]) => [key, lift(value)]))
      : record;
  const sectionsSkipping = (sections: unknown): unknown =>
    isObject(sections) && isObject(sections.bands)
      ? { ...sections, bands: eachValue(sections.bands, skipping) }
      : sections;
  const next = skipping(p) as Record<string, unknown>;
  if (next.bassSound === 'electric') next.bassSound = 'upright';
  if (isObject(next.playbackDisplaced) && next.playbackDisplaced.bassSound === 'electric') {
    next.playbackDisplaced = { ...next.playbackDisplaced, bassSound: 'upright' };
  }
  next.tuneBands = eachValue(next.tuneBands, skipping);
  next.tuneFeels = eachValue(next.tuneFeels, (feels) => eachValue(feels, skipping));
  next.tunePlayback = eachValue(next.tunePlayback, (record) =>
    isObject(record) && record.sections !== undefined
      ? { ...record, sections: sectionsSkipping(record.sections) }
      : record,
  );
  next.houseDefaults = eachValue(next.houseDefaults, (house) => {
    if (!isObject(house) || !isObject(house.settings)) return house;
    const settings: Record<string, unknown> = { ...house.settings };
    if (settings.bassSound === 'electric') delete settings.bassSound;
    settings.band = skipping(settings.band);
    if (settings.feels !== undefined) settings.feels = eachValue(settings.feels, skipping);
    if (settings.sections !== undefined) settings.sections = sectionsSkipping(settings.sections);
    return { ...house, settings };
  });
  for (const key of ['tuneBands', 'tuneFeels', 'tunePlayback', 'houseDefaults']) {
    if (next[key] === undefined) delete next[key];
  }
  return next;
}

/**
 * v13: the house style is Black Book (user ruling Sep 2026, "make black book
 * the default style for all users"). A browser still on the old default,
 * Modern, moves once; any other style was a choice and stays.
 */
function liftHouseStyle(p: Record<string, unknown>): Record<string, unknown> {
  return p.chartFont === 'modern' ? { ...p, chartFont: 'blackbook' } : p;
}

/**
 * v12: the house band moved (user ruling Sep 2026) — a piano melody over a
 * left-hand piano comp, where it was a tenor over a guitar. A browser still
 * holding the OLD default values moves with it once; anything else it holds
 * was a choice and stays. Per-tune memory (tuneBands) is never touched: a tune
 * remembered on guitar was arranged that way.
 */
function liftHouseBand(p: Record<string, unknown>): Record<string, unknown> {
  const next = { ...p };
  if (next.leadSound === 'tenor-sax') next.leadSound = 'piano';
  if (next.compInstrument === 'guitar') {
    next.compInstrument = 'piano-left';
    if (next.compPianoFamily === 'two-hand') next.compPianoFamily = 'lh-rootless';
  }
  return next;
}

function migrateThroughV10(persisted: object, version: number): unknown {
  const p = persisted as Record<string, unknown>;
  if (version >= 9) return liftVoiceLeadingEngine(liftDisplayScale(liftDefaultVoicingFamily(p)));
  if (version >= 6)
    return liftVoiceLeadingEngine(
      liftFreeStringSetDefault(liftDisplayScale(liftDefaultVoicingFamily(p))),
    );
  if (version === 5) {
    return liftVoiceLeadingEngine(
      liftFreeStringSetDefault(liftDisplayScale(liftDefaultVoicingFamily(liftPlaylistEntries(p)))),
    );
  }
  if (version === 4) {
    return liftVoiceLeadingEngine(
      liftFreeStringSetDefault(liftDisplayScale(liftDefaultVoicingFamily(liftPlaylistEntries(p)))),
    ) as never;
  }
  if (version === 3) {
    const savedCharts = Array.isArray(p.savedCharts)
      ? (p.savedCharts as IRealChart[])
          .filter((c) => c && Array.isArray(c.measures))
          .map(liftSavedChart)
          .filter((s): s is Score => s !== null)
      : [];
    return liftVoiceLeadingEngine(
      liftFreeStringSetDefault(
        liftDisplayScale(liftDefaultVoicingFamily(liftPlaylistEntries({ ...p, savedCharts }))),
      ),
    ) as never;
  }
  const stringSet: StringSet = p.stringSet === 'upper' ? 'upper' : 'middle';
  const title = typeof p.chartTitle === 'string' ? p.chartTitle : 'Untitled Chart';
  const key = typeof p.chartKey === 'string' ? p.chartKey : 'C';
  // v1 stored chart as SequenceChord[] and savedPresets as Song[].
  const chart = normalizeStoredChart(p.chart, title, key);
  let savedChartsRaw: IRealChart[] = [];
  if (version < 2 && Array.isArray(p.savedPresets)) {
    savedChartsRaw = (p.savedPresets as Song[])
      .map((song): IRealChart | null => {
        try {
          return { ...songToChart(song), id: uid('chart') };
        } catch {
          return null;
        }
      })
      .filter((c): c is IRealChart => c !== null);
  } else if (Array.isArray(p.savedCharts)) {
    savedChartsRaw = (p.savedCharts as IRealChart[]).filter((c) => c && Array.isArray(c.measures));
  }
  const { score } = liftChart(chart);
  const savedCharts = savedChartsRaw.map(liftSavedChart).filter((s): s is Score => s !== null);
  return liftVoiceLeadingEngine(
    liftFreeStringSetDefault(
      liftDisplayScale(
        liftDefaultVoicingFamily(
          liftPlaylistEntries({
            ...p,
            stringSet,
            score,
            savedCharts,
          }),
        ),
      ),
    ),
  ) as never;
}

/** A copy of a record without one key. */
function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _dropped, ...rest } = record;
  return rest;
}

/**
 * Apply a pure playlist operation to one list.
 *
 * Every playlist mutation routes through here so the operations stay in
 * `storage/playlists` where they are testable without a store, and so a
 * no-op — a rename to the same name, a drag that lands where it started —
 * returns the identical array and React skips the re-render.
 */
function editPlaylist(
  state: AppState,
  id: string,
  update: (doc: PlaylistDoc) => PlaylistDoc,
): Partial<AppState> {
  const at = state.userPlaylists.findIndex((p) => p.id === id);
  // A playlist shared with you view only is the sharer's to change.
  if (at < 0 || !canEditPlaylist(state.playlistLinks, id)) return {};
  const next = update(state.userPlaylists[at]);
  if (next === state.userPlaylists[at]) return {};
  const userPlaylists = [...state.userPlaylists];
  userPlaylists[at] = next;
  return { userPlaylists };
}

export const useStore = create<AppState>()(
  persist(
    (rawSet, get) => {
      const set = liftingWrite(rawSet, get);
      return {
        defaultVoicingFamily: 'drop-2',
        voicingFamily: 'drop-2',
        alternativeShapes: [],
        preferredAlternativeIds: [...DEFAULT_PREFERRED_ALTERNATIVE_IDS],
        stringSet: 'middle',
        freeStringSet: true,
        avoidB9: true,
        sortMode: 'default',
        theme: 'light',
        audioEnabled: true,
        keepAwake: true,
        startingInversion: 0,
        voiceLeadingEngine: DEFAULT_VOICE_LEADING_ENGINE_ID,
        scoreViewPart: 0,
        tempo: DEFAULT_TEMPO,
        metronome: false,
        countIn: false,
        countInBars: 1,
        masterReverb: 0,
        masterReverbEnabled: false,
        masterCompression: 0,
        masterCompressionEnabled: false,
        channelLevels: { ...UNITY_LEVELS },
        soloChannel: null,
        metronomeSettings: { ...DEFAULT_METRONOME },
        practiceMetronome: normalizePracticeMetronome(DEFAULT_PRACTICE_METRONOME),
        feelTiming: 'relaxed',
        countInBetweenLoops: false,
        speedPct: 100,
        trainer: DEFAULT_TRAINER,
        compInstrument: 'piano-left',
        compPianoFamily: 'lh-rootless',
        compStyle: 'interactive',
        compAmount: 0.5,
        drums: false,
        drumLoopKey: '5b2bb8454ff3019d', // Medium swing — Jimmy Cobb
        drumTimeStretch: true,
        melodyChannel: true,
        leadSound: 'piano',
        bassSound: 'upright',
        guitarSound: 'jazz-guitar',
        bassline: false,
        bassSolo: false,
        bassStyle: 'walking',
        bassFeel: 'four',
        bassFollowForm: true,
        bassSwing: true,
        swingFeel: DEFAULT_SWING_FEEL,
        ...SWING_BAND_FEEL,
        bassGhosts: false,
        bassAnticipate: false,
        bassTriplets: false,
        bassAmount: 0.35,
        repeatForm: false,
        ending: DEFAULT_ENDING,
        arrangementPolicy: 'as-written',
        viewSurface: 'chart',
        voicingLens: 'none',
        showLyrics: true,
        showBarNumbers: true,
        barsPerLine: DEFAULT_BARS_PER_LINE,
        chordMelodyDensity: 'changes',
        showAnalysis: false,
        unrollRepeats: false,
        cycleForm: false,
        sheetPages: false,
        tuneMapDetail: 'shown',
        tuneMapLayout: 'card',
        // 125% == the old 'large' chart tier, which was the default.
        chartScale: chartScaleFromTier('large'),
        scoreScale: SCALE_DEFAULT,
        arrangeChordNameScale: SCALE_DEFAULT,
        arrangeFretNumberScale: SCALE_DEFAULT,
        arrangeDiagramMarkers: 'dots',
        chordSymbolStyle: 'triangle',
        scoreEdition: DEFAULT_SCORE_EDITION,
        diagramInstrument: 'guitar',
        playheadTint: 'green',

        score: INITIAL_TRUTH.score,
        chart: INITIAL_TRUTH.chart,
        selectedChordId: null,
        selectedMeasureId: null,
        reharmPreview: null,
        reharmSettings: { maxRung: 2, adventure: 'inside', density: 0.5, variation: 0 },
        passage: null,
        loopRegion: false,
        noteClipboard: null,
        insertionMeasureId: null,

        selectedNoteheadIndex: null,
        selectedNoteId: null,
        scoreEntryCursor: null,
        entryDuration: 1,
        entryVoice: 0,
        scoreContentTarget: null,
        melodyPast: [],
        melodyFuture: [],
        entryCursorPast: [],
        entryCursorFuture: [],
        scoreEditorPast: [],
        scoreEditorFuture: [],

        currentTuneKey: null,
        recentTunes: [],
        tuneHistory: {},
        tuneTaste: [],
        tuneTasteRemoved: [],
        otherHistory: {},
        historyAccount: null,
        tastePulls: 0,
        tuneBands: {},
        tuneFeels: {},
        tuneArrangements: {},
        tunePlayback: {},
        houseDefaults: {},
        playbackDisplaced: {},
        writtenPlayback: null,
        appliedHouseRevision: null,
        playlistCallSource: null,
        followFeelCues: true,

        savedCharts: [],
        playlist: null,
        userPlaylists: [],
        deletedPlaylists: [],
        playlistLinks: {},
        playlistAccount: null,
        playlistDeletes: [],
        accountSync: null,
        deletedSavedTunes: [],
        keyStamps: {},
        setCursor: null,
        lastPlaylistAddedTo: null,
        autoAdvanceSet: false,
        pendingPlay: false,
        setSkipNotice: null,
        currentTuneRef: null,
        tuneLoadSeq: 0,
        tunesBrowseScope: null,
        bandScope: null,
        playCursor: null,
        focusMode: false,

        setDefaultVoicingFamily: (defaultVoicingFamily) => {
          getChordPlayer().stop();
          const family = availableVoicingFamily(defaultVoicingFamily);
          set((s) => ({
            defaultVoicingFamily: family,
            ...rememberVoicing(s, { voicingFamily: family }),
          }));
        },
        setVoicingFamily: (voicingFamily) => {
          getChordPlayer().stop();
          const family = availableVoicingFamily(voicingFamily);
          if (family !== voicingFamily) {
            // A legacy link requesting a paused family is a live fallback,
            // not an explicit choice to replace the tune's saved family.
            set({ voicingFamily: family });
            return;
          }
          set((s) => rememberVoicing(s, { voicingFamily: family }));
        },
        saveAlternativeShape: (shape) => {
          const normalized = normalizeAlternativeShapes([shape])[0];
          if (
            !normalized ||
            BUILTIN_ALTERNATIVE_SHAPES.some((builtin) => builtin.id === normalized.id)
          ) {
            return false;
          }
          const existing = get().alternativeShapes;
          const index = existing.findIndex((entry) => entry.id === normalized.id);
          if (index < 0 && existing.length >= MAX_ALTERNATIVE_SHAPES) return false;
          getChordPlayer().stop();
          set({
            alternativeShapes:
              index < 0
                ? [...existing, normalized]
                : existing.map((entry, i) => (i === index ? normalized : entry)),
          });
          return true;
        },
        removeAlternativeShape: (id) => {
          if (!get().alternativeShapes.some((shape) => shape.id === id)) return;
          getChordPlayer().stop();
          set((s) => ({
            alternativeShapes: s.alternativeShapes.filter((shape) => shape.id !== id),
            preferredAlternativeIds: s.preferredAlternativeIds.filter(
              (preferred) => preferred !== id,
            ),
          }));
        },
        setAlternativePreferred: (id, preferred) => {
          const state = get();
          if (!allAlternativeShapes(state.alternativeShapes).some((shape) => shape.id === id))
            return;
          if (state.preferredAlternativeIds.includes(id) === preferred) return;
          getChordPlayer().stop();
          set({
            preferredAlternativeIds: preferred
              ? [...state.preferredAlternativeIds, id]
              : state.preferredAlternativeIds.filter((entry) => entry !== id),
          });
        },
        setVoiceLeadingEngine: (engine) => {
          getChordPlayer().stop();
          set((s) => rememberVoicing(s, { voiceLeadingEngine: voiceLeadingEngine(engine).id }));
        },
        setScoreViewPart: (part) => set({ scoreViewPart: Math.trunc(part) }),
        setStringSet: (stringSet) => {
          getChordPlayer().stop();
          set((s) => rememberVoicing(s, { stringSet }));
        },
        setFreeStringSet: (freeStringSet) => set((s) => rememberVoicing(s, { freeStringSet })),
        setAvoidB9: (avoidB9) => set({ avoidB9 }),
        setSortMode: (sortMode) => set({ sortMode }),
        toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),
        setAudioEnabled: (audioEnabled) => set({ audioEnabled }),
        setKeepAwake: (keepAwake) => set({ keepAwake }),
        setStartingInversion: (startingInversion) =>
          set((s) => rememberVoicing(s, { startingInversion })),
        setTempo: (tempo) =>
          set((s) => {
            const next = Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(tempo)));
            return { tempo: next, ...rememberPlayback(s, { tempo: next }) };
          }),
        setMetronome: (metronome) => set({ metronome }),
        setCountIn: (countIn) => set({ countIn }),
        setCountInBars: (n) => set({ countInBars: Math.max(1, Math.min(4, Math.round(n))) }),
        setMasterReverb: (amount) => {
          const value = Number.isFinite(amount) ? Math.max(0, Math.min(1, amount)) : 0;
          set((s) => carry(s, { masterReverb: value, masterReverbEnabled: value > 0 }));
        },
        setMasterReverbEnabled: (enabled) =>
          set((s) =>
            carry(s, {
              masterReverbEnabled: enabled,
              masterReverb: enabled && s.masterReverb === 0 ? 0.25 : s.masterReverb,
            }),
          ),
        setMasterCompression: (amount) => {
          if (typeof amount === 'boolean') {
            get().setMasterCompressionEnabled(amount);
            return;
          }
          const value = normalizeCompressionAmount(amount);
          set((s) => carry(s, { masterCompression: value, masterCompressionEnabled: value > 0 }));
        },
        setMasterCompressionEnabled: (enabled) =>
          set((s) =>
            carry(s, {
              masterCompressionEnabled: enabled,
              masterCompression: enabled && s.masterCompression === 0 ? 0.5 : s.masterCompression,
            }),
          ),
        setChannelLevel: (fader, level) =>
          set((s) =>
            carry(s, {
              channelLevels: { ...s.channelLevels, [fader]: normalizeLevel(fader, level) },
            }),
          ),
        patchMetronome: (patch) =>
          set((s) => ({
            metronomeSettings: normalizeMetronome({ ...s.metronomeSettings, ...patch }),
          })),
        setFeelTiming: (feelTiming) =>
          set(() => (FEEL_TIMINGS.includes(feelTiming) ? { feelTiming } : {})),
        patchPracticeMetronome: (patch) =>
          set((s) => ({
            practiceMetronome: normalizePracticeMetronome({ ...s.practiceMetronome, ...patch }),
          })),
        setSoloChannel: (channel) =>
          set((s) => ({ soloChannel: channel === s.soloChannel ? null : channel })),
        setCountInBetweenLoops: (countInBetweenLoops) => set({ countInBetweenLoops }),
        setSpeedPct: (n) => set({ speedPct: Math.max(25, Math.min(150, Math.round(n))) }),
        patchTrainer: (patch) =>
          set((s) => {
            const merged = { ...s.trainer, ...patch };
            const pct = (v: number) => Math.max(25, Math.min(150, Math.round(v)));
            const trainer = {
              enabled: !!merged.enabled,
              fromPct: pct(merged.fromPct),
              toPct: pct(merged.toPct),
              stepPct: Math.max(1, Math.min(50, Math.round(merged.stepPct))),
              passesPerStep: Math.max(1, Math.min(8, Math.round(merged.passesPerStep))),
            };
            const score = withTrainerRepeatFloor(s.score, trainer);
            return score === s.score ? { trainer } : { ...commitScore(score), trainer };
          }),
        setMelodyChannel: (melodyChannel) => set((s) => carry(s, { melodyChannel })),
        setLeadSound: (leadSound) => set((s) => carry(s, { leadSound })),
        setBassSound: (bassSound) => set((s) => carry(s, { bassSound })),
        setGuitarSound: (guitarSound) => set((s) => carry(s, { guitarSound })),
        setDrumTimeStretch: (drumTimeStretch) => set({ drumTimeStretch }),
        setRepeatForm: (repeatForm) => set({ repeatForm }),
        patchEnding: (patch) =>
          set((s) => carry(s, { ending: normalizeEnding({ ...s.ending, ...patch }) })),
        setArrangementPolicy: (arrangementPolicy) => set((s) => carry(s, { arrangementPolicy })),
        setViewSurface: (viewSurface) => set({ viewSurface }),
        setVoicingLens: (voicingLens) => set({ voicingLens }),
        setShowLyrics: (showLyrics) => set({ showLyrics }),
        setShowBarNumbers: (showBarNumbers) => set({ showBarNumbers }),
        setBarsPerLine: (barsPerLine) => set({ barsPerLine: normalizeBarsPerLine(barsPerLine) }),
        setChordMelodyDensity: (chordMelodyDensity) => set({ chordMelodyDensity }),
        setShowAnalysis: (showAnalysis) => set({ showAnalysis }),
        setUnrollRepeats: (unrollRepeats) => set({ unrollRepeats }),
        setCycleForm: (cycleForm) => set({ cycleForm }),
        setSheetPages: (sheetPages) => set({ sheetPages }),
        setTuneMapDetail: (tuneMapDetail) => set({ tuneMapDetail }),
        setTuneMapLayout: (tuneMapLayout) => set({ tuneMapLayout }),
        setChartScale: (percent) => set({ chartScale: clampScale(percent) }),
        setScoreScale: (percent) => set({ scoreScale: clampScale(percent) }),
        setArrangeChordNameScale: (percent) => set({ arrangeChordNameScale: clampScale(percent) }),
        setArrangeFretNumberScale: (percent) =>
          set({ arrangeFretNumberScale: clampScale(percent) }),
        setArrangeDiagramMarkers: (markers) =>
          set({ arrangeDiagramMarkers: normalizeDiagramMarkers(markers) }),
        setChordSymbolStyle: (chordSymbolStyle) => set({ chordSymbolStyle }),
        setScoreEdition: (scoreEdition) =>
          set({
            scoreEdition: isScoreEdition(scoreEdition) ? scoreEdition : DEFAULT_SCORE_EDITION,
          }),
        setDiagramInstrument: (diagramInstrument) => set({ diagramInstrument }),
        setPlayheadTint: (playheadTint) => set({ playheadTint }),

        // THE one band mutation. Every band change — mixer, transport, command
        // layer, AI — goes through here, so "remembered per tune" can never be
        // half-true again (issue #123: the commands used to call raw global
        // setters, so an AI edit leaked across tunes and vanished on reload).
        // The raw per-field setters were deleted rather than re-pointed: a
        // second write path that merely happens to be unused today is a
        // loaded gun.
        patchBand: (partial) =>
          set((s) => {
            const clean = clampBandPatch(partial);
            // Set the live band and remember the full band for this tune — the
            // "iReal remembers your edits without a save" behaviour.
            const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
            const nextBand = { ...pickBand(s), ...clean };
            return {
              ...clean,
              currentTuneKey: key,
              tuneBands: { ...s.tuneBands, [key]: nextBand },
              tunePlayback: withBasedOn(s, key),
            };
          }),
        setHouseDefaults: (list) => {
          const houseDefaults: Record<string, HouseDefault> = {};
          for (const house of list) houseDefaults[house.tuneKey] = house;
          set({ houseDefaults });
          followHouse(get, set);
        },
        adoptHouseDefault: (house) => {
          set((s) => {
            const houseDefaults = { ...s.houseDefaults, [house.tuneKey]: house };
            // The owner just set this tune up: their own changes to it ARE the
            // house default now, so they stop being a separate layer.
            if (house.tuneKey !== s.currentTuneKey) return { houseDefaults };
            return { houseDefaults, ...withoutOwnPlayback(s, house.tuneKey) };
          });
          followHouse(get, set);
        },
        dropHouseDefault: (tuneKey) => {
          set((s) => {
            const houseDefaults = { ...s.houseDefaults };
            delete houseDefaults[tuneKey];
            return { houseDefaults };
          });
          followHouse(get, set);
        },
        resetTunePlayback: () => {
          const key = get().currentTuneKey;
          if (!key) return;
          set((s) => withoutOwnPlayback(s, key));
          resolvePlayback(get, set);
        },
        keepMyTunePlayback: () =>
          set((s) => {
            const key = s.currentTuneKey;
            const revision = key ? s.houseDefaults[key]?.revision : undefined;
            if (!key || revision === undefined) return {};
            return {
              tunePlayback: {
                ...s.tunePlayback,
                [key]: { ...s.tunePlayback[key], basedOn: revision },
              },
            };
          }),
        /**
         * Record how this tune plays a written Feel — a COPY of the band, not
         * a reference to a preset, so editing that preset later cannot reach
         * a tune already arranged with it (the copy-on-apply rule from #176).
         * Passing no band clears the assignment.
         */
        assignFeelGroove: (feel, band) =>
          set((s) => {
            const label = feel.trim();
            if (!label) return {};
            const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
            const forTune = { ...(s.tuneFeels[key] ?? {}) };
            if (band) forTune[label] = structuredClone(band);
            else delete forTune[label];
            return {
              currentTuneKey: key,
              tuneFeels: { ...s.tuneFeels, [key]: forTune },
              tunePlayback: withBasedOn(s, key),
            };
          }),
        setFollowFeelCues: (on) => set({ followFeelCues: on }),
        setSectionsOn: (on) => set((s) => rememberSections(s, (mine) => ({ ...mine, on }))),
        assignSectionBand: (label, band) =>
          set((s) => {
            const name = label.trim();
            if (!name) return {};
            return rememberSections(s, (mine) => {
              const bands = { ...mine.bands };
              // A COPY, like a feel's band: a preset edited later cannot reach it.
              if (band) bands[name] = structuredClone(band);
              else delete bands[name];
              return { ...mine, bands };
            });
          }),
        setSectionStarts: (starts) =>
          set((s) =>
            rememberSections(s, (mine) => {
              const next = { ...mine };
              const guarded = starts ? normalizeSectionStarts(starts) : undefined;
              if (guarded) next.starts = guarded;
              else delete next.starts;
              return next;
            }),
          ),
        setSectionMelody: (label, on) =>
          set((s) => {
            const name = sectionLabel(label);
            if (!name) return {};
            const key = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
            // Sparse: a value the house default (or, with none, playing the
            // melody) already gives is not a change of yours.
            const given = s.houseDefaults[key]?.settings.sections?.melody?.[name] ?? true;
            return rememberSections(s, (mine) => {
              const melody = { ...mine.melody };
              if (on === given) delete melody[name];
              else melody[name] = on;
              return { ...mine, melody };
            });
          }),
        applyStyle: (id) => {
          const preset = getStylePreset(id);
          if (!preset) return;
          // Playback only: the band changes (and is remembered per tune via
          // patchBand), but the chart's WRITTEN style is the tune's identity —
          // a swing tune played as a bossa is still a swing tune, so the
          // masthead label never follows the transport (user ruling July 2026).
          //
          // A preset is a FEEL, not a pair of hands: like groove documents
          // (grooveStateToBand), it never moves the comp instrument or the
          // piano voicing family — applying Bossa Nova while the comp plays
          // piano changes what the band plays, not who plays it. (This also
          // used to STOP a running piano arrangement: the preset's default
          // 'guitar' erased the pianoMidis mid-flight.)
          // Keep the player's channel choices too. Selecting a new groove
          // must not bring a muted part back in; loading a new tune still
          // gets the preset's intentional defaults through resolveBand.
          const { compInstrument, compPianoFamily, bassline, bassSolo, drums } = get();
          get().patchBand({
            ...preset.band,
            compInstrument,
            compPianoFamily,
            bassline,
            bassSolo,
            drums,
          });
        },

        setChartTitle: (title) => set((s) => commitScoreEdit(s, formEdit.setTitle(s.score, title))),
        setChartComposer: (composer) =>
          set((s) => commitScoreEdit(s, formEdit.setComposer(s.score, composer))),
        setChartKey: (key) => set((s) => commitScoreEdit(s, formEdit.setKeyLabel(s.score, key))),
        setChartStyle: (style) => set((s) => commitScoreEdit(s, formEdit.setStyle(s.score, style))),
        setScoreTempo: (tempo) =>
          set((s) => {
            const nextTempo = Math.max(TEMPO_MIN, Math.min(TEMPO_MAX, Math.round(tempo)));
            // Writing the tempo INTO the tune makes it the tune's own: a tempo
            // you set earlier for playback would otherwise win over it on the
            // next load.
            const key = s.currentTuneKey;
            const record = key ? s.tunePlayback[key] : undefined;
            let withoutTempo = {};
            if (key && record?.tempo !== undefined) {
              const { tempo: _written, ...rest } = record;
              withoutTempo = { tunePlayback: { ...s.tunePlayback, [key]: rest } };
            }
            return {
              ...commitScoreEdit(s, formEdit.setTempo(s.score, nextTempo)),
              tempo: nextTempo,
              ...withoutTempo,
              writtenPlayback: s.writtenPlayback && { ...s.writtenPlayback, tempo: nextTempo },
            };
          }),
        setScoreTempoText: (tempoText) =>
          set((s) => commitScoreEdit(s, formEdit.setTempoText(s.score, tempoText))),
        setChartRepeats: (n) =>
          set((s) => {
            const minimumRepeats = s.trainer.enabled ? trainerPassesToTarget(s.trainer) : 1;
            const maximumRepeats = Math.max(16, minimumRepeats, s.chart.repeats ?? 1);
            const edit = commitScoreEdit(
              s,
              formEdit.setRepeats(s.score, Math.max(minimumRepeats, n), maximumRepeats),
            );
            const repeats = edit.chart?.repeats;
            return repeats === undefined ? edit : { ...edit, ...rememberPlayback(s, { repeats }) };
          }),

        setTimeSignature: (sig) => {
          let accepted = false;
          set((s) => {
            const current = s.score.form[0]?.timeSig;
            if (current?.[0] === sig[0] && current[1] === sig[1]) {
              accepted = true;
              return {};
            }
            const next = formEdit.setTimeSignature(s.score, sig);
            accepted = next !== s.score;
            if (!accepted) return {};
            const cursor = reconcileEntryCursorAfterFormEdit(
              s.score,
              next,
              s.scoreEntryCursor,
              s.entryDuration,
            );
            return commitScoreEdit(s, next, {
              scoreEntryCursor: cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: cursor
                ? selectableEventIdAt(
                    next,
                    cursor,
                    s.entryVoice,
                    scoreContentTargetOf(next, s.scoreContentTarget),
                  )
                : null,
            });
          });
          return accepted;
        },

        shiftOctave: (octaves) =>
          set((s) => commitScoreEdit(s, shiftScoreOctave(s.score, octaves))),

        transpose: (semitones) =>
          set((s) => {
            const next = transposeScore(s.score, semitones);
            const wrapped = ((semitones % 12) + 12) % 12;
            const pitchShift = wrapped > 6 ? wrapped - 12 : wrapped;
            const shiftedCursor = s.scoreEntryCursor
              ? {
                  ...s.scoreEntryCursor,
                  midi: Math.min(127, Math.max(0, s.scoreEntryCursor.midi + pitchShift)),
                }
              : null;
            const located = cursorForEvent(
              next,
              s.selectedNoteId,
              shiftedCursor?.midi,
              scoreContentTargetOf(next, s.scoreContentTarget),
              s.selectedNoteheadIndex,
            );
            const cursor =
              located?.cursor ??
              reconcileEntryCursor(
                next,
                shiftedCursor,
                scoreEntryGrid(next, shiftedCursor, s.entryDuration),
              );
            return withKeyMemory(
              s,
              commitScoreEdit(s, next, {
                scoreEntryCursor: cursor,
                ...(located
                  ? {
                      entryVoice: editorVoice(located.voiceIndex),
                      scoreContentTarget: located.target,
                    }
                  : {}),
              }),
            );
          }),

        transposeToKey: (tonic) =>
          set((s) => {
            const prev = parseKey(s.chart.key);
            const curTonic = prev?.tonic ?? 'C';
            let delta = 0;
            try {
              delta = (((pitchClassOf(tonic) - pitchClassOf(curTonic)) % 12) + 12) % 12;
            } catch {
              delta = 0;
            }
            const newKey = prev?.mode === 'minor' ? `${tonic} minor` : tonic;
            const next = transposeScore(s.score, delta, newKey);
            const pitchShift = delta > 6 ? delta - 12 : delta;
            const shiftedCursor = s.scoreEntryCursor
              ? {
                  ...s.scoreEntryCursor,
                  midi: Math.min(127, Math.max(0, s.scoreEntryCursor.midi + pitchShift)),
                }
              : null;
            const located = cursorForEvent(
              next,
              s.selectedNoteId,
              shiftedCursor?.midi,
              scoreContentTargetOf(next, s.scoreContentTarget),
              s.selectedNoteheadIndex,
            );
            const cursor =
              located?.cursor ??
              reconcileEntryCursor(
                next,
                shiftedCursor,
                scoreEntryGrid(next, shiftedCursor, s.entryDuration),
              );
            return withKeyMemory(
              s,
              commitScoreEdit(s, next, {
                scoreEntryCursor: cursor,
                ...(located
                  ? {
                      entryVoice: editorVoice(located.voiceIndex),
                      scoreContentTarget: located.target,
                    }
                  : {}),
              }),
            );
          }),

        // Harmony edits run beneath a live audition (see `beneathAudition`).
        addChord: (input) =>
          set((s) => {
            const b = beneathAudition(s);
            const result = formEdit.addChord(b.score, input, s.insertionMeasureId);
            return commitScoreEdit(b, result.score, {
              insertionMeasureId: result.insertionMeasureId,
            });
          }),

        updateChord: (id, patch) =>
          set((s) => {
            const b = beneathAudition(s);
            const next = formEdit.updateChord(b.score, id, patch);
            return next ? commitScoreEdit(b, next) : {};
          }),

        removeChord: (id) =>
          set((s) => {
            const b = beneathAudition(s);
            const next = formEdit.removeChord(b.score, id);
            return next
              ? commitScoreEdit(b, next, {
                  selectedChordId: s.selectedChordId === id ? null : s.selectedChordId,
                })
              : {};
          }),

        setPreferredInversion: (id, inversion) =>
          set((s) => {
            const b = beneathAudition(s);
            const next = formEdit.setPreferredInversion(b.score, id, inversion);
            return next ? commitScoreEdit(b, next) : {};
          }),
        // Pins never stop the transport: a grip change keeps every onset, so
        // the band swaps it in live (the same-timing event swap).
        pinVoicing: (lane, key, pin) =>
          set((s) => updateTunePins(s, (pins) => withPin(pins, lane, key, pin))),
        clearVoicingPin: (lane, key) =>
          set((s) => updateTunePins(s, (pins) => withoutPin(pins, lane, key))),
        clearVoicingPins: (lane) =>
          set((s) => updateTunePins(s, (pins) => withoutLane(pins, lane))),

        replaceChord: (id, chords, provenance) =>
          set((s) => {
            const b = beneathAudition(s);
            const next = formEdit.replaceChord(b.score, id, chords, provenance);
            return next
              ? commitScoreEdit(b, next, {
                  selectedChordId: s.selectedChordId === id ? null : s.selectedChordId,
                })
              : {};
          }),

        selectChord: (selectedChordId) => set({ selectedChordId }),

        // Layer ops are ordinary undoable score edits: switching layers
        // rewrites the effective harmony, so useBandArrangement's timing-shape
        // check decides between a live swap and a stop exactly as for any
        // harmony edit.
        createReharmLayer: (options) => {
          let layerId = '';
          set((s) => {
            const result = reharm.createReharmLayer(s.score, options);
            layerId = result.layerId;
            return commitScoreEdit(s, result.score);
          });
          return layerId;
        },
        activateReharmLayer: (layerId) =>
          set((s) => {
            const next = reharm.activateReharmLayer(s.score, layerId);
            return next ? commitScoreEdit(s, next) : {};
          }),
        renameReharmLayer: (layerId, name) =>
          set((s) => {
            const next = reharm.renameReharmLayer(s.score, layerId, name);
            return next ? commitScoreEdit(s, next) : {};
          }),
        deleteReharmLayer: (layerId) =>
          set((s) => {
            const next = reharm.deleteReharmLayer(s.score, layerId);
            return next ? commitScoreEdit(s, next) : {};
          }),
        duplicateReharmLayer: (layerId, name) => {
          let created: string | null = null;
          set((s) => {
            const result = reharm.duplicateReharmLayer(s.score, layerId, name);
            if (!result) return {};
            created = result.layerId;
            return commitScoreEdit(s, result.score);
          });
          return created;
        },
        revertReharmMeasures: (measureIds) =>
          set((s) => {
            const next = reharm.revertReharmMeasures(s.score, measureIds);
            return next ? commitScoreEdit(s, next) : {};
          }),
        deriveReharmPassage: (options) => {
          let result: { layerId: string; count: number } | null = null;
          set((s) => {
            // A derivation asked for outright replaces any audition, and is
            // derived from the harmony that was there before it started.
            const base = s.reharmPreview?.base ?? s.score;
            // The transport's tempo is what the layer will be played at, so
            // it is the one the harmonic-rhythm cap reads.
            const applied = derivePassageOn(base, s.passage, { ...options, tempo: s.tempo });
            if (!applied)
              return s.reharmPreview ? { ...commitScore(base), reharmPreview: null } : {};
            result = { layerId: applied.layerId, count: applied.count };
            return commitScoreEdit({ ...s, score: base }, applied.score, { reharmPreview: null });
          });
          return result;
        },

        // Auditioning. The preview is a real layer on a real score, so the
        // sheet, the diagrams and the band all follow with no special path —
        // what makes it an audition is that it is written WITHOUT a history
        // entry (a slider drag must not fill the undo stack) and always
        // derived from `base`, so re-deriving replaces it instead of piling
        // derivation on derivation.
        setReharmSettings: (patch) =>
          set((s) => ({ reharmSettings: { ...s.reharmSettings, ...patch } })),
        previewReharmPassage: (options) => {
          let count = 0;
          set((s) => {
            const base = s.reharmPreview?.base ?? s.score;
            const applied = derivePassageOn(base, s.passage, {
              ...options,
              tempo: s.tempo,
              name: 'Preview',
              preview: true,
            });
            count = applied?.count ?? 0;
            const next = applied?.score ?? base;
            const preview = { base, count, options, passage: s.passage };
            return next === s.score
              ? { reharmPreview: preview }
              : { ...commitScore(next), reharmPreview: preview };
          });
          return count;
        },
        keepReharmPreview: (name) => {
          let result: { layerId: string; count: number } | null = null;
          set((s) => {
            const preview = s.reharmPreview;
            const layer = reharm.previewReharmLayer(s.score);
            if (!preview || !layer) return preview ? { reharmPreview: null } : {};
            const kept = reharm.keepReharmPreview(s.score, name);
            if (!kept) return { reharmPreview: null };
            result = { layerId: layer.id, count: preview.count };
            // ONE undo step, from before the audition began: undo puts back
            // the harmony you started with, not the audition you just kept.
            return commitScoreEdit({ ...s, score: preview.base }, kept, { reharmPreview: null });
          });
          return result;
        },
        cancelReharmPreview: () =>
          set((s) =>
            s.reharmPreview ? { ...commitScore(s.reharmPreview.base), reharmPreview: null } : {},
          ),

        applyReharmCandidate: (candidate) =>
          set((s) => {
            const b = beneathAudition(s);
            const next = applyCandidate(b.score, candidate);
            return next
              ? commitScoreEdit(b, next, {
                  selectedChordId:
                    s.selectedChordId === candidate.chordId ? null : s.selectedChordId,
                })
              : {};
          }),

        selectMeasure: (selectedMeasureId) =>
          set({ selectedMeasureId, insertionMeasureId: selectedMeasureId }),
        selectPassage: (fromId, toId) =>
          set((s) => {
            const next = normalizePassage(
              s.score.form.map((measure) => measure.id),
              fromId,
              toId,
            );
            return next ? { passage: next, loopRegion: true } : {};
          }),
        extendPassage: (toId) =>
          set((s) => {
            const ids = s.score.form.map((measure) => measure.id);
            const from = s.passage?.fromMeasureId ?? s.selectedMeasureId ?? toId;
            const next = normalizePassage(ids, from, toId);
            return next ? { passage: next, loopRegion: true } : {};
          }),
        clearPassage: () => set({ passage: null, loopRegion: false }),
        setLoopRegion: (on) => set((s) => ({ loopRegion: on && s.passage != null })),
        scoreCopy: () =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const clip = s.passage
              ? noteClipboard.copyPassage(s.score, s.passage, target)
              : s.selectedNoteId
                ? noteClipboard.copyEvents(s.score, [s.selectedNoteId], target)
                : { notes: [] };
            return clip.notes.length > 0 || clip.bars?.length ? { noteClipboard: clip } : {};
          }),
        scoreCut: () => {
          let changed = false;
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            if (s.passage) {
              const clip = noteClipboard.copyPassage(s.score, s.passage, target);
              const next = noteClipboard.cutPassage(s.score, s.passage, target);
              if (!next) return {};
              changed = true;
              return commitScoreEdit(s, next, {
                noteClipboard: clip,
                selectedNoteheadIndex: null,
                selectedNoteId: null,
                scoreEntryCursor: null,
              });
            }
            if (!s.selectedNoteId) return {};
            const clip = noteClipboard.copyEvents(s.score, [s.selectedNoteId], target);
            const next = voiceEdit.deleteNote(s.score, s.selectedNoteId, target);
            if (!next || clip.notes.length === 0) return {};
            changed = true;
            return commitScoreEdit(s, next, {
              noteClipboard: clip,
              selectedNoteheadIndex: null,
              selectedNoteId: null,
            });
          });
          return changed;
        },
        scorePaste: (mode = 'all') => {
          let changed = false;
          set((s) => {
            if (
              !s.noteClipboard ||
              (!s.noteClipboard.notes.length && !s.noteClipboard.bars?.length)
            ) {
              return {};
            }
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const ids = s.score.form.map((measure) => measure.id);
            let measureIndex = -1;
            let beat = 1;
            const pastesBars = mode !== 'notes' && !!s.noteClipboard.bars?.length;
            if (pastesBars && s.passage) {
              measureIndex = ids.indexOf(s.passage.fromMeasureId);
            } else if (pastesBars && s.selectedMeasureId) {
              measureIndex = ids.indexOf(s.selectedMeasureId);
            } else if (s.scoreEntryCursor) {
              measureIndex = ids.indexOf(s.scoreEntryCursor.measureId);
              beat = s.scoreEntryCursor.beat;
            } else if (s.selectedNoteId) {
              const located = voiceEdit.describeEvent(s.score, s.selectedNoteId, target);
              if (located) {
                measureIndex = located.measureIndex;
                beat = located.beat;
              }
            } else if (s.selectedMeasureId) {
              measureIndex = ids.indexOf(s.selectedMeasureId);
            } else if (s.passage) {
              measureIndex = ids.indexOf(s.passage.fromMeasureId);
            }
            if (measureIndex < 0) return {};
            const next = noteClipboard.pasteClipboard(
              s.score,
              { measureIndex, beat },
              s.noteClipboard,
              mode,
              target,
            );
            if (!next) return {};
            changed = true;
            // Replace-entry removes the destination event's identity. Follow
            // the new event so the phone's note controls stay open after paste.
            const firstNote = s.noteClipboard.notes[0];
            const voice = firstNote?.voice ?? s.entryVoice;
            const nextIds = next.form.map((measure) => measure.id);
            const pastesWholeBars = mode === 'all' && !!s.noteClipboard.bars?.length;
            const cursor = {
              measureId: nextIds[measureIndex],
              beat: pastesWholeBars ? 1 : beat,
              midi: firstNote?.midi ?? s.scoreEntryCursor?.midi ?? 60,
            };
            const barCount = s.noteClipboard.bars?.length ?? 0;
            const passage =
              barCount > 0
                ? {
                    fromMeasureId: nextIds[measureIndex],
                    toMeasureId: nextIds[Math.min(nextIds.length - 1, measureIndex + barCount - 1)],
                  }
                : s.passage;
            const pastesNotes = !!firstNote && (mode === 'all' || mode === 'notes');
            return commitScoreEdit(s, next, {
              selectedNoteheadIndex: null,
              selectedNoteId: pastesNotes ? selectableEventIdAt(next, cursor, voice, target) : null,
              selectedMeasureId: cursor.measureId,
              entryVoice: voice,
              scoreEntryCursor: pastesNotes ? cursor : pastesBars ? null : s.scoreEntryCursor,
              passage,
            });
          });
          return changed;
        },
        setInsertionMeasure: (insertionMeasureId) => set({ insertionMeasureId }),

        patchMeasure: (id, patch) => {
          let accepted = false;
          set((s) => {
            const next = formEdit.patchMeasure(s.score, id, patch);
            if (!next || next === s.score) return {};
            accepted = true;
            const cursor = reconcileEntryCursorAfterFormEdit(
              s.score,
              next,
              s.scoreEntryCursor,
              s.entryDuration,
            );
            return commitScoreEdit(s, next, {
              scoreEntryCursor: cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: cursor
                ? selectableEventIdAt(
                    next,
                    cursor,
                    s.entryVoice,
                    scoreContentTargetOf(next, s.scoreContentTarget),
                  )
                : null,
            });
          });
          return accepted;
        },

        insertMeasureAfter: (id) =>
          set((s) => {
            const result = formEdit.insertMeasureAfter(s.score, id);
            return commitScoreEdit(s, result.score, {
              selectedMeasureId: result.measureId,
              insertionMeasureId: result.measureId,
            });
          }),

        deleteMeasure: (id) =>
          set((s) => {
            const next = formEdit.deleteMeasure(s.score, id);
            return next
              ? commitScoreEdit(s, next, {
                  selectedMeasureId: s.selectedMeasureId === id ? null : s.selectedMeasureId,
                  insertionMeasureId: s.insertionMeasureId === id ? null : s.insertionMeasureId,
                  selectedNoteheadIndex: null,
                  selectedNoteId: null,
                  scoreEntryCursor: reconcileEntryCursorAfterMeasureDelete(
                    s.score,
                    next,
                    id,
                    s.scoreEntryCursor,
                    s.entryDuration,
                  ),
                })
              : {};
          }),

        selectNote: (selectedNoteId, headIndex) =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const selectedNoteheadIndex =
              selectedNoteId &&
              headIndex !== undefined &&
              Number.isInteger(headIndex) &&
              voiceEdit.getNoteHeads(s.score, selectedNoteId, target)?.[headIndex]
                ? headIndex
                : null;
            const located = cursorForEvent(
              s.score,
              selectedNoteId,
              s.scoreEntryCursor?.midi,
              target,
              selectedNoteheadIndex,
            );
            return {
              selectedNoteId,
              selectedNoteheadIndex,
              ...(located
                ? {
                    scoreEntryCursor: located.cursor,
                    entryVoice: editorVoice(located.voiceIndex),
                    scoreContentTarget: located.target,
                  }
                : {}),
            };
          }),
        setScoreEntryCursor: (scoreEntryCursor) =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const cursor = reconcileEntryCursor(
              s.score,
              scoreEntryCursor,
              scoreEntryGrid(s.score, scoreEntryCursor, s.entryDuration),
            );
            return {
              scoreEntryCursor: cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: cursor
                ? selectableEventIdAt(s.score, cursor, s.entryVoice, target)
                : null,
            };
          }),
        setEntryDuration: (entryDuration) => set({ entryDuration }),
        setEntryVoice: (entryVoice) =>
          set((s) => {
            const voice = entryVoice === 1 ? 1 : 0;
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            return {
              entryVoice: voice,
              selectedNoteheadIndex: null,
              selectedNoteId: s.scoreEntryCursor
                ? selectableEventIdAt(s.score, s.scoreEntryCursor, voice, target)
                : null,
            };
          }),

        setScoreContentTarget: (requested) =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, requested);
            return {
              scoreContentTarget: target ?? null,
              selectedNoteheadIndex: null,
              selectedNoteId: null,
              scoreEntryCursor: null,
              entryVoice: 0,
            };
          }),

        scoreAddPart: (preset) => {
          let addedId: string | null = null;
          set((s) => {
            const next = partEdit.addPart(s.score, preset);
            const added = next.parts.at(-1);
            if (!added || next === s.score) return {};
            addedId = added.id;
            return commitScoreEdit(s, next, {
              scoreContentTarget: { partId: added.id, staffIndex: 0 },
              selectedNoteheadIndex: null,
              selectedNoteId: null,
              scoreEntryCursor: null,
              entryVoice: 0,
            });
          });
          return addedId;
        },

        scoreRenamePart: (partId, name) =>
          set((s) => commitScoreEdit(s, partEdit.renamePart(s.score, partId, name))),

        scoreRemovePart: (partId) =>
          set((s) => {
            const removedIndex = s.score.parts.findIndex((part) => part.id === partId);
            const next = partEdit.removePart(s.score, partId);
            if (next === s.score) return {};
            const fallback = next.parts[Math.min(Math.max(0, removedIndex), next.parts.length - 1)];
            const current = scoreContentTargetOf(next, s.scoreContentTarget);
            const target =
              s.scoreContentTarget?.partId === partId
                ? fallback && { partId: fallback.id, staffIndex: 0 }
                : current;
            return commitScoreEdit(s, next, {
              scoreContentTarget: target ?? null,
              selectedNoteheadIndex: null,
              selectedNoteId: null,
              scoreEntryCursor: null,
              entryVoice: 0,
            });
          }),

        scoreSplitPartStaves: (partId) => {
          let changed = false;
          set((s) => {
            const partIndex = s.score.parts.findIndex((part) => part.id === partId);
            if (partIndex < 0) return {};
            const next = splitPartStaves(s.score, partId);
            if (next === s.score) return {};
            changed = true;
            return commitScoreEdit(s, next, {
              scoreContentTarget: { partId: next.parts[partIndex].id, staffIndex: 0 },
              selectedNoteheadIndex: null,
              selectedNoteId: null,
              scoreEntryCursor: null,
              entryVoice: 0,
            });
          });
          return changed;
        },

        scoreAddNote: (measureIndex, note, voice) => {
          const before = get().score;
          set((s) => {
            const targetVoice = voice ?? s.entryVoice;
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            let next: Score | null;
            try {
              next = voiceEdit.addNote(
                s.score,
                measureIndex,
                {
                  midi: note.midi,
                  beat: note.beat,
                  duration: note.duration,
                  ...(note.spelledAs ? { spelledAs: note.spelledAs } : {}),
                  ...(note.rest ? { rest: true } : {}),
                },
                targetVoice,
                target,
              );
            } catch {
              // Unsupported free placement inside an arbitrary tuplet is a
              // safe no-op; selecting the member enables exact replacement.
              return {};
            }
            const measureId = s.score.form[measureIndex]?.id;
            if (!next || !measureId) return {};
            const event = voiceEdit.findEventAt(next, measureId, targetVoice, note.beat, target);
            const cursor: ScoreEntryCursor = {
              measureId,
              beat: event?.beat ?? note.beat,
              midi: event?.midi ?? s.scoreEntryCursor?.midi ?? note.midi,
            };
            return commitScoreEdit(s, next, {
              scoreEntryCursor: cursor,
              entryVoice: editorVoice(targetVoice),
              selectedNoteheadIndex: null,
              selectedNoteId: event && (!event.rest || !/^r[:-]/.test(event.id)) ? event.id : null,
              scoreContentTarget: target ?? null,
            });
          });
          // Clicking a note onto the staff is entry too — sound it.
          if (!note.rest && get().score !== before) previewEditedPitch(note.midi);
        },

        scoreAppendNote: (midi, durationBeats) =>
          set((s) => {
            const lastId = s.score.form.at(-1)?.id ?? '';
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            return commitScoreEdit(
              s,
              voiceEdit.appendNote(
                s.score,
                midi,
                durationBeats ?? entryDurationInBeats(s.score, lastId, s.entryDuration),
                target,
              ),
            );
          }),

        scoreStepEntryCursor: (direction, byMeasure = false, growAtEnd = true) =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const seeded =
              s.scoreEntryCursor ??
              cursorForEvent(s.score, s.selectedNoteId, 60, target)?.cursor ??
              (s.score.form[0] ? { measureId: s.score.form[0].id, beat: 1, midi: 60 } : null);
            if (!seeded) return {};

            let move: EntryCursorMove;
            const selected = s.selectedNoteId
              ? voiceEdit.describeEvent(s.score, s.selectedNoteId, target)
              : null;
            const selectedMeasureId = selected
              ? s.score.form[selected.measureIndex]?.id
              : undefined;
            const selectedAtCursor =
              selected &&
              selected.voiceIndex === s.entryVoice &&
              selectedMeasureId === seeded.measureId &&
              Math.abs(selected.beat - seeded.beat) < 1e-9;
            if (direction < 0 && !byMeasure && selectedAtCursor) {
              const previous = voiceEdit.findEventBefore(
                s.score,
                seeded.measureId,
                s.entryVoice,
                seeded.beat,
                target,
              );
              move = previous
                ? {
                    cursor: {
                      ...seeded,
                      beat: previous.beat,
                      midi: previous.midi ?? seeded.midi,
                    },
                  }
                : stepEntryCursor(
                    seeded,
                    entryMeasures(s.score),
                    (measure) => entryGridInBeats(s.score, measure.id, s.entryDuration),
                    direction,
                    byMeasure,
                  );
            } else if (direction > 0 && !byMeasure && selectedAtCursor && selected.duration > 0) {
              move = advanceEntryCursor(seeded, entryMeasures(s.score), selected.duration);
            } else {
              move = stepEntryCursor(
                seeded,
                entryMeasures(s.score),
                (measure) => entryGridInBeats(s.score, measure.id, s.entryDuration),
                direction,
                byMeasure,
              );
            }

            // A grid step knows only the active note-value subdivision. When
            // Left crosses a barline, prefer the prior bar's real final onset
            // so imported tuplets round-trip exactly (for example beat 4.8 in
            // a final-beat 5:4 group) instead of landing on beat 4.
            if (
              direction < 0 &&
              !byMeasure &&
              !('appendAfterId' in move) &&
              move.cursor.measureId !== seeded.measureId
            ) {
              const previous = voiceEdit.findLastEvent(
                s.score,
                move.cursor.measureId,
                s.entryVoice,
                target,
              );
              if (previous) {
                move = {
                  cursor: {
                    ...move.cursor,
                    beat: previous.beat,
                    midi: previous.midi ?? seeded.midi,
                  },
                };
              }
            }

            if ('appendAfterId' in move) {
              if (!growAtEnd) return {};
              const grown = formEdit.insertMeasureAfter(s.score, move.appendAfterId);
              const cursor = { ...seeded, measureId: grown.measureId, beat: 1 };
              return commitScoreEdit(s, grown.score, {
                scoreEntryCursor: cursor,
                selectedNoteheadIndex: null,
                selectedNoteId: null,
              });
            }
            return {
              scoreEntryCursor: move.cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: selectableEventIdAt(s.score, move.cursor, s.entryVoice, target),
            };
          }),

        scoreNudgeEntryCursor: (semitones) => {
          const before = get();
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const selected = s.selectedNoteId
              ? voiceEdit.describeEvent(s.score, s.selectedNoteId, target)
              : null;
            if (s.selectedNoteId && selected?.voiceIndex === s.entryVoice) {
              const next = voiceEdit.nudgeNote(
                s.score,
                s.selectedNoteId,
                semitones,
                target,
                s.selectedNoteheadIndex ?? undefined,
              );
              if (next) {
                const event = voiceEdit.describeEvent(next, s.selectedNoteId, target);
                return commitScoreEdit(s, next, {
                  scoreEntryCursor:
                    event && s.score.form[event.measureIndex]
                      ? {
                          measureId: s.score.form[event.measureIndex].id,
                          beat: event.beat,
                          midi:
                            selectedHeadMidi(
                              next,
                              s.selectedNoteId,
                              s.selectedNoteheadIndex,
                              target,
                            ) ??
                            event.midi ??
                            s.scoreEntryCursor?.midi ??
                            60,
                        }
                      : s.scoreEntryCursor,
                });
              }
              // Rests are selectable authored events but have no pitch to
              // nudge. Pitch arrows still adjust the pending caret pitch so
              // the next A-G/Enter replacement is predictable.
              if (!selected.rest) return {};
            }
            if (!s.scoreEntryCursor) return {};
            return {
              scoreEntryCursor: {
                ...s.scoreEntryCursor,
                midi: Math.min(127, Math.max(0, s.scoreEntryCursor.midi + semitones)),
              },
            };
          });
          const after = get();
          if (after.score !== before.score && after.selectedNoteId) {
            // The arrows repitched a written note — sound where it landed.
            // That is the ONLY audible case: on a rest (or a bare caret) the
            // arrows merely tune the invisible pending-entry pitch, and a
            // pitch sounding while a REST is selected reads as a contradiction,
            // not feedback. Sound means "a note changed".
            const moved = voiceEdit.describeEvent(
              after.score,
              after.selectedNoteId,
              scoreContentTargetOf(after.score, after.scoreContentTarget),
            );
            if (moved && !moved.rest)
              previewEditedPitch(
                selectedHeadMidi(after.score, after.selectedNoteId, after.selectedNoteheadIndex) ??
                  moved.midi,
              );
          }
        },

        scoreInsertTripletAtCursor: () =>
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const seeded =
              s.scoreEntryCursor ??
              (s.score.form[0] ? { measureId: s.score.form[0].id, beat: 1, midi: 60 } : null);
            if (!seeded) return {};
            const measureIndex = s.score.form.findIndex(
              (measure) => measure.id === seeded.measureId,
            );
            if (measureIndex < 0) return {};
            let next: Score | null;
            try {
              next = voiceEdit.insertTriplet(
                s.score,
                measureIndex,
                {
                  beat: seeded.beat,
                  duration: entryDurationInBeats(s.score, seeded.measureId, s.entryDuration),
                },
                s.entryVoice,
                target,
              );
            } catch {
              return {};
            }
            if (!next) return {};
            const first = voiceEdit.findEventAt(
              next,
              seeded.measureId,
              s.entryVoice,
              seeded.beat,
              target,
            );
            if (!first) return {};
            const cursor: ScoreEntryCursor = {
              ...seeded,
              beat: first.beat,
              midi: first.midi ?? seeded.midi,
            };
            return commitScoreEdit(s, next, {
              scoreEntryCursor: cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: first.id,
            });
          }),

        scoreEnterAtCursor: (midi, rest = false, spelledAs) => {
          const before = get().score;
          set((s) => {
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const seeded =
              s.scoreEntryCursor ??
              (s.score.form[0] ? { measureId: s.score.form[0].id, beat: 1, midi } : null);
            if (!seeded) return {};
            const measureIndex = s.score.form.findIndex(
              (measure) => measure.id === seeded.measureId,
            );
            if (measureIndex < 0) return {};
            let entrySpan = entryDurationInBeats(s.score, seeded.measureId, s.entryDuration);
            const selected = s.selectedNoteId
              ? voiceEdit.describeEvent(s.score, s.selectedNoteId, target)
              : null;
            const selectedAtCursor =
              selected?.voiceIndex === s.entryVoice &&
              s.score.form[selected.measureIndex]?.id === seeded.measureId &&
              Math.abs(selected.beat - seeded.beat) < 1e-9;
            let next = selectedAtCursor
              ? voiceEdit.replaceTupletEventContent(
                  s.score,
                  s.selectedNoteId!,
                  rest ? { rest: true } : { midi, ...(spelledAs ? { spelledAs } : {}) },
                  target,
                )
              : null;
            let entered = next ? voiceEdit.describeEvent(next, s.selectedNoteId!, target) : null;
            if (entered) {
              // Tuplet replacement retains the member's exact written span.
              entrySpan = entered.duration;
            } else {
              try {
                next = voiceEdit.addNote(
                  s.score,
                  measureIndex,
                  {
                    midi,
                    beat: seeded.beat,
                    duration: entrySpan,
                    ...(spelledAs ? { spelledAs } : {}),
                    ...(rest ? { rest: true } : {}),
                  },
                  s.entryVoice,
                  target,
                );
              } catch {
                return {};
              }
              entered = next
                ? voiceEdit.findEventAt(next, seeded.measureId, s.entryVoice, seeded.beat, target)
                : null;
            }
            if (!next) return {};
            if (!entered) return {};
            const atEntry: ScoreEntryCursor = {
              ...seeded,
              beat: entered.beat,
              midi: rest ? seeded.midi : (entered.midi ?? midi),
            };
            // A long value in an odd or pickup measure can be rebuilt as
            // several tied events. Advance by the complete requested span,
            // not merely the first rendered fragment returned above.
            const move = advanceEntryCursor(atEntry, entryMeasures(next), entrySpan);
            let cursor: ScoreEntryCursor;
            if ('appendAfterId' in move) {
              const grown = formEdit.insertMeasureAfter(next, move.appendAfterId);
              next = grown.score;
              cursor = { ...atEntry, measureId: grown.measureId, beat: 1 };
            } else {
              cursor = move.cursor;
            }
            return commitScoreEdit(s, next, {
              scoreEntryCursor: cursor,
              selectedNoteheadIndex: null,
              selectedNoteId: selectableEventIdAt(next, cursor, s.entryVoice, target),
            });
          });
          // Sound what was just written. The updater bails with {} on an
          // impossible entry (unknown measure, no room), leaving the score
          // reference identical — stay silent then, or the player would
          // confirm a note that never landed.
          if (!rest && get().score !== before) previewEditedPitch(midi);
        },

        scoreAddChordHead: (pitch) =>
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const heads = voiceEdit.getNoteHeads(s.score, s.selectedNoteId, target);
            const next = voiceEdit.addChordHead(s.score, s.selectedNoteId, pitch, target);
            return commitScoreEdit(s, next, {
              selectedNoteheadIndex: heads?.length ?? null,
              scoreEntryCursor: s.scoreEntryCursor
                ? { ...s.scoreEntryCursor, midi: midiOf(pitch) }
                : null,
            });
          }),
        scoreRemoveChordHead: () =>
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const heads = voiceEdit.getNoteHeads(s.score, s.selectedNoteId, target);
            if (!heads?.length) return {};
            const index =
              s.selectedNoteheadIndex ??
              voiceEdit.defaultNoteHeadIndex(s.score, s.selectedNoteId, target);
            const next = voiceEdit.removeChordHead(s.score, s.selectedNoteId, index, target);
            if (!next) return {};
            const nextIndex = heads.length > 1 ? Math.min(index, heads.length - 2) : null;
            const located = cursorForEvent(
              next,
              s.selectedNoteId,
              s.scoreEntryCursor?.midi,
              target,
              nextIndex,
            );
            return commitScoreEdit(s, next, {
              selectedNoteheadIndex: nextIndex,
              ...(located ? { scoreEntryCursor: located.cursor } : {}),
            });
          }),

        scoreDeleteSelected: () => {
          const current = get();
          if (
            current.selectedNoteId &&
            current.selectedNoteheadIndex !== null &&
            (voiceEdit.getNoteHeads(current.score, current.selectedNoteId)?.length ?? 0) > 1
          ) {
            current.scoreRemoveChordHead();
            return;
          }
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const located = cursorForEvent(
              s.score,
              s.selectedNoteId,
              s.scoreEntryCursor?.midi,
              target,
            );
            return commitScoreEdit(s, voiceEdit.deleteNote(s.score, s.selectedNoteId, target), {
              selectedNoteheadIndex: null,
              selectedNoteId: null,
              ...(located ? { scoreEntryCursor: located.cursor } : {}),
            });
          });
        },

        scoreNudgeSelected: (semitones) => {
          const before = get().score;
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const next = voiceEdit.nudgeNote(
              s.score,
              s.selectedNoteId,
              semitones,
              target,
              s.selectedNoteheadIndex ?? undefined,
            );
            if (!next) return {};
            const located = cursorForEvent(
              next,
              s.selectedNoteId,
              s.scoreEntryCursor?.midi,
              target,
              s.selectedNoteheadIndex,
            );
            return commitScoreEdit(s, next, located ? { scoreEntryCursor: located.cursor } : {});
          });
          // Sound the pitch the arrows just moved to — read back from the
          // score so what plays is what engraved, octave shifts included.
          const after = get();
          if (after.score === before || !after.selectedNoteId) return;
          const moved = voiceEdit.describeEvent(
            after.score,
            after.selectedNoteId,
            scoreContentTargetOf(after.score, after.scoreContentTarget),
          );
          if (moved && !moved.rest)
            previewEditedPitch(
              selectedHeadMidi(after.score, after.selectedNoteId, after.selectedNoteheadIndex) ??
                moved.midi,
            );
        },

        scoreRespellSelected: () =>
          set((s) =>
            s.selectedNoteId
              ? commitScoreEdit(
                  s,
                  voiceEdit.respellNote(
                    s.score,
                    s.selectedNoteId,
                    scoreContentTargetOf(s.score, s.scoreContentTarget),
                    s.selectedNoteheadIndex ?? undefined,
                  ),
                )
              : {},
          ),

        scoreToggleSelectedArticulation: (articulation) =>
          set((s) =>
            s.selectedNoteId
              ? commitScoreEdit(
                  s,
                  voiceEdit.toggleArticulation(
                    s.score,
                    s.selectedNoteId,
                    articulation,
                    scoreContentTargetOf(s.score, s.scoreContentTarget),
                  ),
                )
              : {},
          ),

        scoreSetSelectedDynamic: (dynamic) =>
          set((s) =>
            s.selectedNoteId
              ? commitScoreEdit(
                  s,
                  voiceEdit.setDynamic(
                    s.score,
                    s.selectedNoteId,
                    dynamic,
                    scoreContentTargetOf(s.score, s.scoreContentTarget),
                  ),
                )
              : {},
          ),

        scoreSetSelectedDuration: (durationQuarters) =>
          set((s) => {
            if (!s.selectedNoteId || !Number.isFinite(durationQuarters) || durationQuarters <= 0) {
              return {};
            }
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const described = voiceEdit.describeEvent(s.score, s.selectedNoteId, target);
            if (!described || described.duration <= 0) return {}; // grace notes have no timed span
            const measureId = s.score.form[described.measureIndex].id;
            const next = voiceEdit.updateNote(
              s.score,
              s.selectedNoteId,
              { duration: entryDurationInBeats(s.score, measureId, durationQuarters) },
              target,
            );
            if (!next) return {}; // tuplet members and grace notes keep their span
            // The entry duration FOLLOWS the applied value: the toolbar then
            // shows what just happened, and the next entered note matches it.
            return commitScoreEdit(s, next, { entryDuration: durationQuarters });
          }),

        scoreToggleSelectedDot: () =>
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const described = voiceEdit.describeEvent(s.score, s.selectedNoteId, target);
            if (!described || described.duration <= 0) return {};
            const measureId = s.score.form[described.measureIndex].id;
            const quarters = described.duration / entryDurationInBeats(s.score, measureId, 1);
            const durationQuarters = toggleDottedDuration(quarters);
            if (durationQuarters === quarters) return {};
            const next = voiceEdit.updateNote(
              s.score,
              s.selectedNoteId,
              { duration: entryDurationInBeats(s.score, measureId, durationQuarters) },
              target,
            );
            if (!next) return {};
            return commitScoreEdit(s, next, { entryDuration: durationQuarters });
          }),

        scoreApplySelectedAccidental: (accidental) => {
          const before = get().score;
          set((s) => {
            if (!s.selectedNoteId) return {};
            const target = scoreContentTargetOf(s.score, s.scoreContentTarget);
            const heads = voiceEdit.getNoteHeads(s.score, s.selectedNoteId, target);
            const head =
              heads?.[
                s.selectedNoteheadIndex ??
                  voiceEdit.defaultNoteHeadIndex(s.score, s.selectedNoteId, target)
              ];
            if (!head) return {};
            const described = voiceEdit.describeEvent(s.score, s.selectedNoteId, target);
            const transposition = described
              ? s.score.parts[described.partIndex].transposition
              : undefined;
            let written = head.pitch;
            if (transposition) {
              try {
                written = transposePitch(head.pitch, transposition);
              } catch {
                written = spellMidi(midiOf(head.pitch) + transposition.semitones);
              }
            }
            const alter = accidental === 'sharp' ? 1 : accidental === 'flat' ? -1 : 0;
            if (written.alter === alter) return {};
            let pitch = { ...written, alter };
            if (transposition) {
              try {
                pitch = transposePitch(pitch, invertInterval(transposition));
              } catch {
                pitch = spellMidi(midiOf(pitch) - transposition.semitones);
              }
            }
            const next = voiceEdit.updateNote(
              s.score,
              s.selectedNoteId,
              {
                midi: midiOf(pitch),
                spelledAs: noteNameOf(pitch),
                headIndex: s.selectedNoteheadIndex ?? undefined,
              },
              target,
            );
            if (!next) return {};
            const located = cursorForEvent(
              next,
              s.selectedNoteId,
              s.scoreEntryCursor?.midi,
              target,
              s.selectedNoteheadIndex,
            );
            return commitScoreEdit(s, next, located ? { scoreEntryCursor: located.cursor } : {});
          });
          // An accidental is a pitch change — sound where the note landed,
          // exactly like the arrow keys.
          const after = get();
          if (after.score === before || !after.selectedNoteId) return;
          const moved = voiceEdit.describeEvent(
            after.score,
            after.selectedNoteId,
            scoreContentTargetOf(after.score, after.scoreContentTarget),
          );
          if (moved && !moved.rest)
            previewEditedPitch(
              selectedHeadMidi(after.score, after.selectedNoteId, after.selectedNoteheadIndex) ??
                moved.midi,
            );
        },

        scoreToggleOutgoingTie: (preferPrevious = false) =>
          set((s) => {
            const id = scoreTieTargetId(s, preferPrevious);
            return id
              ? commitScoreEdit(
                  s,
                  voiceEdit.toggleOutgoingTie(
                    s.score,
                    id,
                    scoreContentTargetOf(s.score, s.scoreContentTarget),
                    id === s.selectedNoteId ? (s.selectedNoteheadIndex ?? undefined) : undefined,
                  ),
                )
              : {};
          }),

        scoreUpdateNote: (id, patch) =>
          set((s) =>
            commitScoreEdit(
              s,
              voiceEdit.updateNote(s.score, id, {
                ...(patch.midi !== undefined ? { midi: patch.midi } : {}),
                ...(patch.beat !== undefined ? { beat: patch.beat } : {}),
                ...(patch.duration !== undefined ? { duration: patch.duration } : {}),
                ...(patch.spelledAs !== undefined ? { spelledAs: patch.spelledAs } : {}),
                ...(patch.tie !== undefined ? { tie: patch.tie } : {}),
              }),
            ),
          ),

        scoreDeleteNote: (id) =>
          set((s) => ({
            ...commitScoreEdit(s, voiceEdit.deleteNote(s.score, id)),
            ...(s.selectedNoteId === id
              ? { selectedNoteId: null, selectedNoteheadIndex: null }
              : {}),
          })),

        scoreNudgeNote: (id, semitones) =>
          set((s) => commitScoreEdit(s, voiceEdit.nudgeNote(s.score, id, semitones))),

        scoreRespellNote: (id) =>
          set((s) => commitScoreEdit(s, voiceEdit.respellNote(s.score, id))),

        scoreClearMelody: () =>
          set((s) => ({
            ...commitScoreEdit(
              s,
              voiceEdit.clearMelody(s.score, scoreContentTargetOf(s.score, s.scoreContentTarget)),
            ),
            selectedNoteheadIndex: null,
            selectedNoteId: null,
          })),

        scoreClearAll: () =>
          set((s) => {
            const b = beneathAudition(s);
            return {
              ...commitScoreEdit(b, formEdit.clearHarmony(voiceEdit.clearMelody(b.score))),
              selectedNoteheadIndex: null,
              selectedNoteId: null,
            };
          }),

        scoreUndo: () =>
          set((s) => {
            if (s.melodyPast.length === 0) return {};
            const previous = s.melodyPast[s.melodyPast.length - 1];
            const previousCursor = s.entryCursorPast[s.entryCursorPast.length - 1] ?? null;
            const pastEditors = alignedScoreEditors(s.melodyPast, s.scoreEditorPast);
            const previousEditor = pastEditors.at(-1);
            const restored = withTrainerRepeatFloor(previous, s.trainer);
            const tempoChanged = previous.tempo !== s.score.tempo;
            return {
              ...commitScore(restored),
              ...(tempoChanged ? { tempo: previous.tempo ?? DEFAULT_TEMPO } : {}),
              ...(previousEditor
                ? restoreScoreEditor(restored, previousEditor.context)
                : legacyScoreEditor(restored, previousCursor, s)),
              melodyPast: s.melodyPast.slice(0, -1),
              melodyFuture: [s.score, ...s.melodyFuture].slice(0, MELODY_HISTORY_LIMIT),
              entryCursorPast: s.entryCursorPast.slice(0, -1),
              entryCursorFuture: [s.scoreEntryCursor, ...s.entryCursorFuture].slice(
                0,
                MELODY_HISTORY_LIMIT,
              ),
              scoreEditorPast: pastEditors.slice(0, -1),
              scoreEditorFuture: [
                captureScoreEditor(s),
                ...alignedScoreEditors(s.melodyFuture, s.scoreEditorFuture),
              ].slice(0, MELODY_HISTORY_LIMIT),
            };
          }),

        scoreRedo: () =>
          set((s) => {
            if (s.melodyFuture.length === 0) return {};
            const [next, ...rest] = s.melodyFuture;
            const [nextCursor = null, ...cursorRest] = s.entryCursorFuture;
            const [nextEditor, ...editorRest] = alignedScoreEditors(
              s.melodyFuture,
              s.scoreEditorFuture,
            );
            const restored = withTrainerRepeatFloor(next, s.trainer);
            const tempoChanged = next.tempo !== s.score.tempo;
            return {
              ...commitScore(restored),
              ...(tempoChanged ? { tempo: next.tempo ?? DEFAULT_TEMPO } : {}),
              ...(nextEditor
                ? restoreScoreEditor(restored, nextEditor.context)
                : legacyScoreEditor(restored, nextCursor, s)),
              melodyPast: [...s.melodyPast, s.score].slice(-MELODY_HISTORY_LIMIT),
              melodyFuture: rest,
              entryCursorPast: [...s.entryCursorPast, s.scoreEntryCursor].slice(
                -MELODY_HISTORY_LIMIT,
              ),
              entryCursorFuture: cursorRest,
              scoreEditorPast: [
                ...alignedScoreEditors(s.melodyPast, s.scoreEditorPast),
                captureScoreEditor(s),
              ].slice(-MELODY_HISTORY_LIMIT),
              scoreEditorFuture: editorRest,
            };
          }),

        importMusicXML: (xml) => {
          try {
            // Slice 5: MusicXML lands on the score model directly — spellings
            // exact, tuplets explicit, every voice and part kept. (Rendering
            // still shows the lead part's first voice; the rest is stored.)
            const score = parseMusicXMLScore(xml);
            const validationErrors = validateScore(score).filter(
              (issue) => issue.severity === 'error',
            );
            if (validationErrors.length > 0) {
              const shown = validationErrors.slice(0, 5).map((issue) => issue.message);
              const remaining = validationErrors.length - shown.length;
              return {
                ok: false,
                error: `Score validation failed: ${shown.join('; ')}${remaining > 0 ? `; plus ${remaining} more` : ''}`,
              };
            }
            getChordPlayer().stop();
            set((s) => ({
              ...commitScore(withTrainerRepeatFloor(score, s.trainer)),
              // Honour the file's tempo (like loadChart does for chart.tempo).
              tempo: score.tempo ?? s.tempo,
              selectedChordId: null,
              selectedMeasureId: null,
              insertionMeasureId: null,
              ...freshTune(s),
            }));
            resolveBand(get, set, get().chart);
            return { ok: true };
          } catch (e) {
            return { ok: false, error: e instanceof Error ? e.message : 'Import failed' };
          }
        },

        tidyScoreTranscription: () => {
          const { score, report } = tidyTranscription(get().score);
          if (report.changed) {
            // A re-bar rewrites the form the player snapshotted at Play, and
            // every measure/event id — stop the transport and drop volatile
            // selections; undo history keeps the pre-tidy score.
            getChordPlayer().stop();
            set((s) =>
              commitScoreEdit(s, score, {
                selectedChordId: null,
                selectedMeasureId: null,
                insertionMeasureId: null,
                selectedNoteheadIndex: null,
                selectedNoteId: null,
                scoreEntryCursor: null,
                passage: null,
              }),
            );
          }
          return report;
        },

        // Export derives from the score truth directly — the explicit rests it
        // fills in make measures account for their whole written time, which
        // MusicXML requires (gaps used to export underfull).
        exportMusicXML: () => exportMusicXMLScore(get().score),

        // A blank chart is a tune of its own: resolve its identity like any
        // load, or band, voicing and pin edits made on it would be remembered
        // under the tune that was loaded before.
        clearChart: () => {
          set((s) => ({
            chart: chartWithTrainerRepeatFloor(
              createEmptyChart('Untitled Chart', s.chart.key ?? 'C'),
              s.trainer,
            ),
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        newChart: () => {
          set((s) => ({
            chart: chartWithTrainerRepeatFloor(createEmptyChart(), s.trainer),
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        newTune: (meta) => {
          // A fresh tune replaces whatever's loaded — stop the transport so the
          // playhead resets, same as loadChart.
          getChordPlayer().stop();
          const chart = createEmptyChart(
            meta.title?.trim() || 'Untitled Chart',
            meta.key?.trim() || 'C',
          );
          if (meta.composer?.trim()) chart.composer = meta.composer.trim();
          if (meta.style?.trim()) chart.style = meta.style.trim();
          set((s) => ({
            chart: chartWithTrainerRepeatFloor(chart, s.trainer),
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            // Starting a blank one LEAVES the set. Editing a tune mid-set is
            // fine and must not, but "New" says you are done with this gig —
            // and a blank canvas has no transport for the set controls to live
            // on, so staying in would mean being in a set with nothing on
            // screen to say so or to get out with.
            setCursor: null,
            autoAdvanceSet: false,
            setSkipNotice: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        loadTuneFromChunk: (scheme, chunk) => {
          if (isAbcScheme(scheme)) {
            try {
              const { score } = parseAbcChunk(chunk);
              get().loadScore(score);
              return score.title;
            } catch {
              return null;
            }
          }
          const chart = parseIRealSongChunk(scheme, chunk);
          if (!chart) return null;
          get().loadChart(chart);
          return chart.title;
        },

        importAbc: (text, name) => {
          const tunes = splitAbcBook(text).tunes;
          if (tunes.length === 0) {
            return { ok: false as const, error: 'No ABC tune found (a tune needs a K: line).' };
          }
          let persisted = true;
          if (tunes.length > 1) {
            const res = get().setPlaylist(withAbcBookName(text, name));
            if (!res.ok) return res;
            persisted = res.persisted;
          }
          const title = get().loadTuneFromChunk('abc', tunes[0].chunk);
          if (title === null) return { ok: false as const, error: 'Could not read that tune.' };
          return { ok: true as const, title, tunes: tunes.length, persisted };
        },

        loadChart: (chart) => {
          // A new tune must not inherit the old one's playback — stop the
          // transport so the playhead resets to the start.
          getChordPlayer().stop();
          set((s) => ({
            chart: chartWithTrainerRepeatFloor(normalize(cloneChart(chart)), s.trainer),
            tempo: chart.tempo ?? DEFAULT_TEMPO,
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        loadScore: (score) => {
          getChordPlayer().stop();
          set((s) => ({
            ...commitScore(withTrainerRepeatFloor(structuredClone(score), s.trainer)),
            tempo: score.tempo ?? s.tempo,
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        loadSong: (song) => {
          getChordPlayer().stop();
          const chart = normalize(songToChart(song));
          set((s) => ({
            chart: chartWithTrainerRepeatFloor(chart, s.trainer),
            tempo: s.tempo,
            selectedChordId: null,
            selectedMeasureId: null,
            insertionMeasureId: null,
            ...freshTune(s),
          }));
          resolveBand(get, set, get().chart);
        },

        importIRealText: (text) => {
          try {
            const playlist = parseIRealURL(text);
            const chart = playlist.songs[0];
            get().loadChart(chart);
            return { ok: true as const, title: chart.title };
          } catch (e) {
            return {
              ok: false as const,
              error: e instanceof Error ? e.message : 'Could not read that link.',
            };
          }
        },

        saveCurrentAs: (title) =>
          set((s) => {
            // Snapshot the SCORE — a chart snapshot would silently drop any
            // second voice or extra part (they don't project into the chart).
            const snapshot: Score = { ...structuredClone(s.score), id: uid('chart'), title };
            return {
              savedCharts: [...s.savedCharts, snapshot],
              ...commitScore(formEdit.setTitle(s.score, title)),
            };
          }),

        saveCurrentCopy: (title) => {
          const id = uid('chart');
          set((s) => ({
            savedCharts: [
              ...s.savedCharts,
              { ...structuredClone(s.score), id, title: title.trim() || s.score.title },
            ],
          }));
          return id;
        },

        deleteSavedChart: (id) =>
          set((s) => ({
            savedCharts: s.savedCharts.filter((c) => c.id !== id),
            deletedSavedTunes: s.deletedSavedTunes.includes(id)
              ? s.deletedSavedTunes
              : [...s.deletedSavedTunes, id].slice(-500),
          })),

        applyAccountPull: ({ addTunes, removeTunes, settings }) => {
          set((s) => {
            const removing = new Set(removeTunes);
            const have = new Set(s.savedCharts.map((tune) => tune.id));
            // Another device wrote these: the rehydrate guard, applied here too.
            const arriving = checkSavedTunes(addTunes).kept.filter((tune) => !have.has(tune.id));
            const tuneBands = { ...s.tuneBands };
            const tuneFeels = { ...s.tuneFeels };
            const tuneArrangements = { ...s.tuneArrangements };
            const tunePlayback = { ...s.tunePlayback };
            const base = getStylePreset('swing')!.band;
            const fullBand = (raw: unknown): StyleBand | undefined =>
              raw && typeof raw === 'object' ? bandFrom(base, raw) : undefined;
            for (const [key, record] of Object.entries(settings)) {
              delete tuneBands[key];
              delete tuneFeels[key];
              delete tuneArrangements[key];
              delete tunePlayback[key];
              if (!record) continue;
              const band = fullBand(record.band);
              if (band) tuneBands[key] = band;
              if (record.feels && typeof record.feels === 'object') {
                const feels: Record<string, StyleBand> = {};
                for (const [label, raw] of Object.entries(record.feels)) {
                  const feel = fullBand(raw);
                  if (feel && label.trim()) feels[label] = feel;
                }
                if (Object.keys(feels).length) tuneFeels[key] = feels;
              }
              const arrangement = normalizeTuneArrangements({ [key]: record.arrangement })[key];
              if (arrangement) tuneArrangements[key] = arrangement;
              const playback = normalizeTunePlayback({ [key]: record.playback })[key];
              if (playback) tunePlayback[key] = playback;
            }
            return {
              savedCharts: [
                ...s.savedCharts.filter((tune) => !removing.has(tune.id ?? '')),
                ...arriving,
              ],
              tuneBands,
              tuneFeels,
              tuneArrangements,
              tunePlayback,
            };
          });
          // The tune on screen plays the way you left it on the other device.
          const key = get().currentTuneKey;
          if (key && key in settings) resolvePlayback(get, set);
        },

        setAccountSync: (accountSync) => set({ accountSync }),

        clearDeletedSavedTunes: (ids) =>
          set((s) => {
            const done = new Set(ids);
            const deletedSavedTunes = s.deletedSavedTunes.filter((id) => !done.has(id));
            return deletedSavedTunes.length === s.deletedSavedTunes.length
              ? {}
              : { deletedSavedTunes };
          }),

        setPlaylist: (source) => {
          let index: IRealIndex;
          try {
            index = parseTuneIndex(source);
          } catch (e) {
            return {
              ok: false as const,
              error: e instanceof Error ? e.message : 'Could not read that link.',
            };
          }
          if (index.songs.length === 0) {
            return {
              ok: false as const,
              error: isAbcScheme(index.scheme)
                ? 'No tunes found in that ABC file.'
                : 'No songs found in the iReal Pro link.',
            };
          }
          // Persist the raw source once (not on every edit). A huge playlist may
          // exceed quota — keep it in memory for this session and report that.
          let persisted = true;
          try {
            localStorage.setItem(PLAYLIST_KEY, source);
          } catch {
            persisted = false;
          }
          set({ playlist: index });
          return { ok: true as const, persisted };
        },

        rehydratePlaylist: () => {
          let source: string | null = null;
          try {
            source = localStorage.getItem(PLAYLIST_KEY);
          } catch {
            source = null;
          }
          if (!source) return;
          try {
            set({ playlist: parseTuneIndex(source) });
          } catch {
            try {
              localStorage.removeItem(PLAYLIST_KEY);
            } catch {
              /* ignore */
            }
          }
        },

        clearPlaylist: () => {
          try {
            localStorage.removeItem(PLAYLIST_KEY);
          } catch {
            /* ignore */
          }
          set({ playlist: null });
        },

        setTunesBrowseScope: (tunesBrowseScope) => set({ tunesBrowseScope }),
        setBandScope: (bandScope) => set({ bandScope }),
        setPlayCursor: (playCursor) => set({ playCursor }),
        setFocusMode: (focusMode) => set({ focusMode }),
        toggleFocusMode: () => set((s) => ({ focusMode: !s.focusMode })),

        createPlaylist: (name) => {
          const doc = createPlaylistDoc(name);
          set((s) => ({ userPlaylists: [...s.userPlaylists, doc] }));
          return doc.id;
        },

        renamePlaylist: (id, name) =>
          set((s) => editPlaylist(s, id, (p) => renamePlaylistDoc(p, name))),

        describePlaylist: (id, description) =>
          set((s) => editPlaylist(s, id, (p) => setPlaylistDescription(p, description))),

        deletePlaylist: (id) =>
          set((s) =>
            s.userPlaylists.some((p) => p.id === id)
              ? {
                  userPlaylists: s.userPlaylists.filter((p) => p.id !== id),
                  deletedPlaylists: recordTombstone(s.deletedPlaylists, id),
                  // Deleting a shared playlist someone else made takes it off
                  // this device; it stays for everyone else. Deleting one of
                  // yours deletes it from your account, so the sync sends that
                  // on (the device may be offline now).
                  ...(s.playlistLinks[id]
                    ? { playlistLinks: withoutKey(s.playlistLinks, id) }
                    : {}),
                  ...(s.playlistLinks[id]?.mine
                    ? {
                        playlistDeletes: [
                          ...s.playlistDeletes.filter(
                            (d) => d.shareId !== s.playlistLinks[id].shareId,
                          ),
                          {
                            shareId: s.playlistLinks[id].shareId,
                            deletedAt: new Date().toISOString(),
                          },
                        ],
                      }
                    : {}),
                  // Deleting the set you are in leaves it, as leaveSet does:
                  // the strip goes with the list, so an armed Auto left behind
                  // could not be seen or switched off, and would run the next
                  // set that gets opened.
                  ...(s.setCursor?.playlistId === id
                    ? { setCursor: null, autoAdvanceSet: false, setSkipNotice: null }
                    : {}),
                  ...(s.lastPlaylistAddedTo === id ? { lastPlaylistAddedTo: null } : {}),
                }
              : {},
          ),

        duplicatePlaylist: (id, name) => {
          const source = get().userPlaylists.find((p) => p.id === id);
          if (!source) return null;
          const copy = duplicatePlaylistDoc(source, name);
          // Beside the original, not at the end: a copy made to tweak one set
          // belongs next to the set it came from.
          set((s) => {
            const at = s.userPlaylists.findIndex((p) => p.id === id);
            const next = [...s.userPlaylists];
            next.splice(at + 1, 0, copy);
            return { userPlaylists: next };
          });
          return copy.id;
        },

        adoptSharedPlaylist: (doc, share) =>
          set((s) => {
            const at = s.userPlaylists.findIndex((p) => p.id === doc.id);
            const local = at >= 0 ? s.userPlaylists[at] : null;
            const current = s.playlistLinks[doc.id];
            // Already following this link: what is here stays, and the next
            // sync merges it with the server's copy.
            if (local && current?.shareId === share.shareId) {
              return {
                playlistLinks: {
                  ...s.playlistLinks,
                  [doc.id]: { ...current, access: share.access, mine: share.mine },
                },
              };
            }
            // Your own copy, changed here since the server's: it stays, and the
            // next sync sends it up (base = the server's copy, so it reads as
            // a change made here).
            const keepLocal =
              !!local && share.mine && Date.parse(local.updatedAt) > Date.parse(doc.updatedAt);
            const userPlaylists = [...s.userPlaylists];
            if (!keepLocal) {
              if (local)
                userPlaylists[at] = { ...local, ...playlistContent(doc), updatedAt: doc.updatedAt };
              else userPlaylists.push(doc);
            }
            return {
              userPlaylists,
              playlistLinks: {
                ...s.playlistLinks,
                [doc.id]: { ...share, base: playlistContent(doc) },
              },
              // Opening the link is an explicit re-adoption; a standing
              // tombstone would tell a future sync to delete it again.
              deletedPlaylists: s.deletedPlaylists.filter((t) => t.id !== doc.id),
            };
          }),

        claimPlaylistsFor: (userId) => {
          const s = get();
          if (s.playlistAccount === userId) return false;
          if (s.playlistAccount === null) {
            set({ playlistAccount: userId });
            return false;
          }
          set({
            playlistAccount: userId,
            userPlaylists: [],
            deletedPlaylists: [],
            playlistLinks: {},
            playlistDeletes: [],
            setCursor: null,
            lastPlaylistAddedTo: null,
            autoAdvanceSet: false,
            setSkipNotice: null,
            playlistCallSource: null,
          });
          return true;
        },

        forgetAccountPlaylist: (id) =>
          set((s) => {
            if (!s.userPlaylists.some((p) => p.id === id) && !s.playlistLinks[id]) return {};
            return {
              userPlaylists: s.userPlaylists.filter((p) => p.id !== id),
              playlistLinks: withoutKey(s.playlistLinks, id),
              ...(s.setCursor?.playlistId === id
                ? { setCursor: null, autoAdvanceSet: false, setSkipNotice: null }
                : {}),
            };
          }),

        clearPlaylistDeletes: (shareIds) =>
          set((s) => {
            const done = new Set(shareIds);
            const rest = s.playlistDeletes.filter((d) => !done.has(d.shareId));
            return rest.length === s.playlistDeletes.length ? {} : { playlistDeletes: rest };
          }),

        applySharedPlaylist: (id, patch) =>
          set((s) => {
            const out: Partial<AppState> = {};
            const at = s.userPlaylists.findIndex((p) => p.id === id);
            if (patch.content && at >= 0) {
              const list = s.userPlaylists[at];
              if (!sameContent(playlistContent(list), patch.content)) {
                const userPlaylists = [...s.userPlaylists];
                userPlaylists[at] = {
                  ...list,
                  name: patch.content.name,
                  entries: patch.content.entries,
                  updatedAt: new Date().toISOString(),
                };
                out.userPlaylists = userPlaylists;
              }
            }
            if (patch.link !== undefined) {
              out.playlistLinks = patch.link
                ? { ...s.playlistLinks, [id]: patch.link }
                : withoutKey(s.playlistLinks, id);
            }
            return out;
          }),

        recordRecentTune: (ref) =>
          set((state) => {
            const key = playlistRefKey(ref);
            const rest = state.recentTunes.filter((candidate) => playlistRefKey(candidate) !== key);
            // The load came first, so the identity is the opened tune's.
            const tuneKey = state.currentTuneKey ?? tuneIdentityOf(state.score, state.chart);
            return {
              recentTunes: [ref, ...rest].slice(0, RECENT_LIMIT),
              tuneHistory: recordOpen(state.tuneHistory, tuneKey, ref, Date.now()),
            };
          }),

        recordTunePlay: (key, tune, seconds) =>
          set((state) => {
            const tuneHistory = recordPlay(state.tuneHistory, key, tune, seconds, Date.now());
            return tuneHistory === state.tuneHistory ? {} : { tuneHistory };
          }),

        setTuneTaste: (tune, verdict) =>
          set((state) => {
            const now = Date.now();
            const tuneTaste = markTaste(state.tuneTaste, tune, verdict, now);
            if (tuneTaste === state.tuneTaste) return {};
            const tuneTasteRemoved = noteTasteRemoval(
              state.tuneTasteRemoved,
              tune,
              verdict ? null : now,
              now,
            );
            return { tuneTaste, tuneTasteRemoved };
          }),

        restoreTuneTaste: (tune, previous) =>
          set((state) => {
            const now = Date.now();
            return {
              tuneTaste: restoreTasteMark(state.tuneTaste, tune, previous, now),
              tuneTasteRemoved: noteTasteRemoval(
                state.tuneTasteRemoved,
                tune,
                previous ? null : now,
                now,
              ),
            };
          }),

        claimHistoryFor: (userId) => {
          const s = get();
          if (s.historyAccount === userId) return false;
          if (s.historyAccount === null) {
            set({ historyAccount: userId });
            return false;
          }
          set({
            historyAccount: userId,
            recentTunes: [],
            tuneHistory: {},
            otherHistory: {},
            tuneTaste: [],
            tuneTasteRemoved: [],
            tastePulls: s.tastePulls + 1,
          });
          return true;
        },

        applyPersonalPull: (pull) =>
          set((state) => ({
            ...pull,
            ...(pull.tuneTaste ? { tastePulls: state.tastePulls + 1 } : {}),
          })),

        addToPlaylist: (id, ref) => {
          const entryId = makeEntryId();
          let added = false;
          set((s) => {
            const patch = editPlaylist(s, id, (p) => addEntry(p, ref, { entryId }));
            added = 'userPlaylists' in patch;
            // The list you file into is the one you are most likely filing
            // into next: the Add to menu lists it first.
            return added ? { ...patch, lastPlaylistAddedTo: id } : patch;
          });
          return added ? entryId : null;
        },

        removeFromPlaylist: (id, entryId) =>
          set((s) => editPlaylist(s, id, (p) => removeEntry(p, entryId))),

        movePlaylistEntry: (id, entryId, toIndex) =>
          set((s) => editPlaylist(s, id, (p) => moveEntry(p, entryId, toIndex))),

        setPlaylistEntryNote: (id, entryId, note) =>
          set((s) => editPlaylist(s, id, (p) => setEntryNote(p, entryId, note))),

        replacePlaylistEntryRef: (id, entryId, ref) =>
          set((s) => editPlaylist(s, id, (p) => replaceEntryRef(p, entryId, ref))),

        enterSetAt: (playlistId, entryId) => set({ setCursor: { playlistId, entryId } }),
        openedFromPlaylist: (playlistId, entryId) => {
          const s = get();
          const tuneKey = s.currentTuneKey ?? tuneIdentityOf(s.score, s.chart);
          set({ playlistCallSource: { playlistId, entryId, tuneKey } });
          // The load resolved the tune's playback without the row; with a call
          // to apply, resolve again with it. The layers are idempotent, so the
          // second pass lands where one pass with the call would have.
          if (activePlaylistCall(get())) resolvePlayback(get, set);
        },
        savePlaylistCall: () => {
          const s = get();
          const source = s.playlistCallSource;
          if (!source || !playlistCallRow(s)) return false;
          if (!canEditPlaylist(s.playlistLinks, source.playlistId)) return false;
          const call = playlistCallOf(s);
          set((state) =>
            editPlaylist(state, source.playlistId, (doc) =>
              setEntryCall(doc, source.entryId, call),
            ),
          );
          return true;
        },
        clearPlaylistCall: () => {
          const source = get().playlistCallSource;
          if (!source) return;
          set((state) =>
            editPlaylist(state, source.playlistId, (doc) =>
              setEntryCall(doc, source.entryId, null),
            ),
          );
        },
        // The notice goes with the set: "Skipped Blue Bossa" left hanging over
        // a set you are no longer in explains nothing.
        leaveSet: () => set({ setCursor: null, autoAdvanceSet: false, setSkipNotice: null }),
        setAutoAdvanceSet: (autoAdvanceSet) => set({ autoAdvanceSet }),
        requestPlayback: () => set({ pendingPlay: true }),
        clearPendingPlay: () => set({ pendingPlay: false }),
        noteSetSkip: (message) => set({ setSkipNotice: message }),

        noteCurrentTuneRef: (currentTuneRef) => set({ currentTuneRef }),

        addCurrentTuneToPlaylist: (playlistId) => {
          const s = get();
          if (
            !s.userPlaylists.some((p) => p.id === playlistId) ||
            !canEditPlaylist(s.playlistLinks, playlistId)
          ) {
            return { ok: false, saved: false, title: s.score.title, entryId: null };
          }
          const entryId = makeEntryId();
          const existing = s.currentTuneRef;
          if (existing) {
            set((st) => ({
              ...editPlaylist(st, playlistId, (p) => addEntry(p, existing, { entryId })),
              lastPlaylistAddedTo: playlistId,
            }));
            return { ok: true, saved: false, title: s.score.title, entryId };
          }
          // Nothing identifiable is loaded — a blank chart, an ad-hoc import,
          // or a tune edited since it was opened. Snapshot it so the row
          // reopens the music actually on screen rather than whatever the old
          // reference used to point at.
          const title = s.score.title.trim() || 'Untitled Chart';
          const id = uid('chart');
          const snapshot: Score = { ...structuredClone(s.score), id, title };
          const ref: PlaylistTuneRef = { kind: 'saved', id, title };
          set((st) => ({
            savedCharts: [...st.savedCharts, snapshot],
            ...editPlaylist(st, playlistId, (p) => addEntry(p, ref, { entryId })),
            // The snapshot IS the loaded tune now, so adding it again adds a
            // reference rather than a second copy.
            currentTuneRef: ref,
            lastPlaylistAddedTo: playlistId,
          }));
          return { ok: true, saved: true, title, entryId };
        },
      };
    },
    {
      // The key stays 'vlc:v2' — renaming it would orphan user data; the
      // `version` field below is what actually versions the blob.
      name: 'vlc:v2',
      version: 14,
      migrate: migratePersisted,
      // Rebuild the chart projection at rehydrate: `chart` is not persisted
      // (score is the truth), and without this the store would boot with the
      // empty initial chart beside the user's real score.
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<AppState>) };
        merged.arrangeChordNameScale = clampScale(merged.arrangeChordNameScale);
        merged.arrangeFretNumberScale = clampScale(merged.arrangeFretNumberScale);
        merged.arrangeDiagramMarkers = normalizeDiagramMarkers(merged.arrangeDiagramMarkers);
        const savedAlternatives = persisted as {
          alternativeShapes?: unknown;
          preferredAlternativeIds?: unknown;
        } | null;
        const builtinIds = new Set(BUILTIN_ALTERNATIVE_SHAPES.map((shape) => shape.id));
        merged.alternativeShapes = normalizeAlternativeShapes(
          savedAlternatives?.alternativeShapes,
        ).filter((shape) => !builtinIds.has(shape.id));
        merged.preferredAlternativeIds = normalizePreferredAlternativeIds(
          savedAlternatives?.preferredAlternativeIds,
          allAlternativeShapes(merged.alternativeShapes),
        );
        // Same-version sessions may still contain the old on/off switch.
        merged.masterCompression = normalizeCompressionAmount(merged.masterCompression);
        merged.masterReverb =
          typeof merged.masterReverb === 'number' && Number.isFinite(merged.masterReverb)
            ? Math.max(0, Math.min(1, merged.masterReverb))
            : 0;
        const savedEffects = persisted as {
          masterReverbEnabled?: unknown;
          masterCompressionEnabled?: unknown;
        } | null;
        // Before bypass switches, a nonzero amount meant the effect was on.
        merged.masterReverbEnabled =
          typeof savedEffects?.masterReverbEnabled === 'boolean'
            ? savedEffects.masterReverbEnabled
            : merged.masterReverb > 0;
        merged.masterCompressionEnabled =
          typeof savedEffects?.masterCompressionEnabled === 'boolean'
            ? savedEffects.masterCompressionEnabled
            : merged.masterCompression > 0;
        // Older sessions have no sound preference; invalid values must not
        // leave playback pointing at an instrument this build cannot load.
        const savedSounds = persisted as {
          leadSound?: unknown;
          bassSound?: unknown;
          guitarSound?: unknown;
          drumTimeStretch?: unknown;
        } | null;
        // A missing or unknown lead falls back to the house band's piano.
        merged.leadSound =
          savedSounds?.leadSound === 'guitar' ||
          savedSounds?.leadSound === 'piano' ||
          savedSounds?.leadSound === 'trumpet' ||
          savedSounds?.leadSound === 'tenor-sax'
            ? savedSounds.leadSound
            : 'piano';
        // A missing or unknown bass falls back to the house band's upright.
        merged.bassSound =
          savedSounds?.bassSound === 'electric' || savedSounds?.bassSound === 'original'
            ? savedSounds.bassSound
            : 'upright';
        merged.guitarSound = savedSounds?.guitarSound === 'original' ? 'original' : 'jazz-guitar';
        merged.drumTimeStretch =
          typeof savedSounds?.drumTimeStretch === 'boolean' ? savedSounds.drumTimeStretch : true;
        const persistedPreferences = persisted as {
          defaultVoicingFamily?: unknown;
          /** Accepted only for sessions written by an interim v7 build. */
          voicingFamily?: unknown;
        } | null;
        const defaultVoicingFamily = isVoicingFamilySelection(
          persistedPreferences?.defaultVoicingFamily,
        )
          ? persistedPreferences.defaultVoicingFamily
          : isVoicingFamilySelection(persistedPreferences?.voicingFamily)
            ? persistedPreferences.voicingFamily
            : current.defaultVoicingFamily;
        merged.defaultVoicingFamily = availableVoicingFamily(defaultVoicingFamily);
        // The current arrangement starts from the remembered default (or the
        // loaded tune's own record, below), then a Workbench query/study may
        // apply its session-only override.
        merged.voicingFamily = merged.defaultVoicingFamily;
        merged.voiceLeadingEngine = normalizeVoiceLeadingEngineId(merged.voiceLeadingEngine);
        // A reading edition this build does not offer falls back to the C
        // book, never to a sheet in an unknown transposition.
        if (!isScoreEdition(merged.scoreEdition)) merged.scoreEdition = DEFAULT_SCORE_EDITION;
        // The reading surface and the map's detail are enums a stale blob can
        // carry junk for; either falls back to its default.
        if (!isViewSurface(merged.viewSurface)) merged.viewSurface = 'chart';
        if (!isTuneMapDetail(merged.tuneMapDetail))
          merged.tuneMapDetail = LEGACY_TUNE_MAP_DETAIL[String(merged.tuneMapDetail)] ?? 'shown';
        if (!isTuneMapLayout(merged.tuneMapLayout)) merged.tuneMapLayout = 'card';
        merged.barsPerLine = normalizeBarsPerLine(merged.barsPerLine);
        // The four workspace tabs became surface × lens. A blob persisted
        // before the split carries chartViewMode (+ arrangementLayout for
        // Arrange); map it once. ('both', removed earlier, lands on
        // chart+none via the default branch.)
        const legacy = persisted as {
          chartViewMode?: string;
          arrangementLayout?: string;
          viewSurface?: ViewSurface;
        } | null;
        if (legacy && !legacy.viewSurface && legacy.chartViewMode) {
          const mode = legacy.chartViewMode;
          const arrangeOnScore = mode === 'arrange' && legacy.arrangementLayout !== 'voicings';
          merged.viewSurface = mode === 'score' || arrangeOnScore ? 'score' : 'chart';
          merged.voicingLens =
            mode === 'guitar' ? 'comping' : mode === 'arrange' ? 'melody' : 'none';
        }
        // Saved tunes are untrusted on the way back in: a corrupt entry used
        // to reach the Tunes browser and the palette as a Score that isn't
        // one, crashing a list the user then can't get past. Drop the bad
        // ones, keep the rest, and say so.
        const savedCheck = checkSavedTunes(merged.savedCharts);
        merged.savedCharts = savedCheck.kept;
        // A persisted ending is untrusted data: every field falls back.
        merged.ending = normalizeEnding(merged.ending);
        merged.metronomeSettings = normalizeMetronome(merged.metronomeSettings);
        merged.practiceMetronome = normalizePracticeMetronome(merged.practiceMetronome);
        if (!isSwingFeel(merged.swingFeel)) merged.swingFeel = DEFAULT_SWING_FEEL;
        // The feel fields (placements, phrasing, accent, skips, the kit): a
        // blob from before them, or with junk in one, plays its band's own
        // kind of defaults, field by field.
        Object.assign(
          merged,
          withBandFeel({
            bassSwing: merged.bassSwing,
            ...normalizeBandFeel(merged as unknown as Record<string, unknown>),
          }),
        );
        if (!FEEL_TIMINGS.includes(merged.feelTiming)) merged.feelTiming = 'relaxed';
        merged.channelLevels = normalizeLevels(merged.channelLevels);
        merged.tuneArrangements = normalizeTuneArrangements(merged.tuneArrangements);
        // Per-tune playback records and the house cache are untrusted on the
        // way back in, like every persisted blob: field by field.
        merged.tunePlayback = normalizeTunePlayback(merged.tunePlayback);
        merged.tuneHistory = normalizeTuneHistory(merged.tuneHistory);
        merged.tuneTaste = normalizeTuneTaste(merged.tuneTaste);
        merged.tuneTasteRemoved = normalizeTasteRemovals(merged.tuneTasteRemoved);
        merged.otherHistory = normalizeTuneHistory(merged.otherHistory);
        merged.historyAccount =
          typeof merged.historyAccount === 'string' && merged.historyAccount
            ? merged.historyAccount
            : null;
        merged.houseDefaults = normalizeHouseDefaults(
          merged.houseDefaults,
          getStylePreset('swing')!.band,
        );
        merged.playbackDisplaced = normalizeCarry(merged.playbackDisplaced);
        merged.accountSync = normalizeAccountSync(merged.accountSync);
        merged.playlistLinks = normalizePlaylistLinks(merged.playlistLinks);
        merged.playlistDeletes = normalizePlaylistDeletes(merged.playlistDeletes);
        merged.playlistAccount =
          typeof merged.playlistAccount === 'string' && merged.playlistAccount
            ? merged.playlistAccount
            : null;
        merged.keyStamps = normalizeKeyStamps(merged.keyStamps);
        merged.deletedSavedTunes = Array.isArray(merged.deletedSavedTunes)
          ? (merged.deletedSavedTunes as unknown[]).filter(
              (id): id is string => typeof id === 'string',
            )
          : [];
        {
          const written = merged.writtenPlayback as unknown;
          const side = normalizeTuneSide(written);
          merged.writtenPlayback =
            written && typeof written === 'object' && side.tempo !== undefined
              ? {
                  tempo: side.tempo,
                  key: side.key ?? null,
                  repeats: side.repeats ?? 1,
                }
              : null;
        }
        merged.appliedHouseRevision =
          typeof merged.appliedHouseRevision === 'number' &&
          Number.isInteger(merged.appliedHouseRevision)
            ? merged.appliedHouseRevision
            : null;
        merged.playlistCallSource = normalizeCallSource(merged.playlistCallSource);
        // The loaded tune comes back voiced by its own record, as resolveBand
        // voices it on a load. A reload does not load the tune again, and the
        // family is not persisted on its own: left at the default, the NEXT
        // voicing change would write the default over the tune's record.
        const rememberedVoicing = merged.currentTuneKey
          ? merged.tuneArrangements[merged.currentTuneKey]?.voicing
          : undefined;
        if (rememberedVoicing) {
          Object.assign(merged, rememberedVoicing);
          merged.voicingFamily = availableVoicingFamily(rememberedVoicing.voicingFamily);
        }
        // A cursor on a playlist that no longer exists is no set at all, so
        // leave it here with the Auto it armed, as deletePlaylist now does. A
        // blob saved before that would otherwise run the next set opened.
        const cursorList = merged.setCursor?.playlistId;
        if (
          cursorList !== undefined &&
          !(
            Array.isArray(merged.userPlaylists) &&
            merged.userPlaylists.some((p) => p?.id === cursorList)
          )
        ) {
          merged.setCursor = null;
          merged.autoAdvanceSet = false;
        }
        // The playlist last filed into has to be one that is still here.
        if (
          merged.lastPlaylistAddedTo !== null &&
          (typeof merged.lastPlaylistAddedTo !== 'string' ||
            !(
              Array.isArray(merged.userPlaylists) &&
              merged.userPlaylists.some((p) => p?.id === merged.lastPlaylistAddedTo)
            ))
        ) {
          merged.lastPlaylistAddedTo = null;
        }
        if (savedCheck.dropped.length) {
          console.warn(
            `Dropped ${savedCheck.dropped.length} unreadable saved tune(s):\n` +
              savedCheck.dropped.join('\n'),
          );
        }
        try {
          // An audition is never something you come back to: it was applied so
          // it could be heard, and a "Preview" layer nobody kept turning up
          // after a reload is a mystery, not a feature.
          merged.score = withTrainerRepeatFloor(
            normalizeScore(reharm.dropReharmPreview(merged.score)),
            merged.trainer,
          );
          merged.chart = scoreToChart(merged.score);
        } catch (error) {
          // A persisted score today's code cannot read must NOT be silently
          // replaced by emptiness: the session's very next persist write
          // would overwrite the user's tune with a blank chart, in every tab
          // (the "clicked the logo and home is empty" report). Stash the
          // unreadable score where a later fix — or the user, via devtools —
          // can recover it, say so loudly, and only then start fresh.
          try {
            window.localStorage.setItem('vlc:score-recovery', JSON.stringify(merged.score));
          } catch {
            // Quota or no DOM: the console line below is still the trail.
          }
          console.error(
            'Persisted score failed to rehydrate — stashed at localStorage["vlc:score-recovery"].',
            error,
          );
          merged.score = withTrainerRepeatFloor(INITIAL_TRUTH.score, merged.trainer);
          merged.chart = scoreToChart(merged.score);
        }
        return merged;
      },
      // Derived from the ONE registry in ./persistence.ts — never hand-listed,
      // because a forgotten entry loses user data silently.
      partialize: persistedSlice,
    },
  ),
);

// Cover the api-level write path too: tests (and devtools) write via
// useStore.setState, which bypasses the creator's `set`.
useStore.setState = liftingWrite(useStore.setState, useStore.getState);

export { createEmptySong, createEmptyChart, chartToSequence };
